import React, { useEffect, useRef, useState, useCallback } from "react";
import ProspectusA5 from "@/components/superadmin/ProspectusA5";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, API, getToken, setToken, setB2CToken } from "@/lib/api";
import { writeImpersonation } from "@/components/ImpersonationBanner";
import MatchSettingsPanel from "@/components/superadmin/MatchSettingsPanel";
import HomeSettingsPanel from "@/components/superadmin/HomeSettingsPanel";
import EmailPreviewPanel from "@/components/superadmin/EmailPreviewPanel";
import ConsentExportPanel from "@/components/superadmin/ConsentExportPanel";
import GlobalStatsPanel from "@/components/superadmin/GlobalStatsPanel";
import CancelReasonsPanel from "@/components/superadmin/CancelReasonsPanel";
import FreeUnlocksPanel from "@/components/superadmin/FreeUnlocksPanel";
import DisputesPanel from "@/components/superadmin/DisputesPanel";
import CheckoutAbandonsPanel from "@/components/superadmin/CheckoutAbandonsPanel";
import RevenueBreakdown from "@/components/superadmin/RevenueBreakdown";
import RevenueMonthlyChart from "@/components/superadmin/RevenueMonthlyChart";
import SendcloudReconciliation from "@/components/superadmin/SendcloudReconciliation";
import ServerHealthCard from "@/components/superadmin/ServerHealthCard";
import { BarChart, Bar, LineChart, Line, CartesianGrid, ReferenceLine, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { readPreviewRegion, writePreviewRegion } from "@/components/PreviewRegionBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { countryFlag, COUNTRIES } from "@/lib/countries";
import { PARTNER_CATEGORY_KEYS, PARTNER_CATEGORY_LABELS_FR } from "@/lib/partnerCategories";
import {
  Building2, Euro, Package, Users, UserPlus, Pencil, Trash2, KeyRound,
  Power, Search, Download, EyeOff, Eye, RotateCcw, Megaphone, AlertTriangle, Settings,
  Image as ImageIcon, Flag, X, MapPin, Send, TrendingUp, TrendingDown, Target, BellRing, Check, Cookie, UserCog, FileText, RefreshCw, Mail, WifiOff, ShieldCheck, History,
  Hotel, UtensilsCrossed, Trees, Landmark, Building, Ban, Trophy, CalendarClock, ChevronRight, Archive, Lock, Unlock, Sparkles,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CalendarDays, Truck } from "lucide-react";
import { FindoSpinner, PageLoader } from "@/components/FindoSpinner";

const PARTNER_TIERS = ["free", "small", "medium", "large"];

const CATEGORY_ICONS = {
  hotel: Hotel, restaurant: UtensilsCrossed, park: Trees,
  city_hall: Landmark, prefecture: Building, other: Building2, uncategorized: Building2,
};
function CategoryIcon({ category, size = 15, className = "" }) {
  const Ic = CATEGORY_ICONS[category] || Building2;
  return <Ic size={size} className={className} />;
}

export default function Superadmin() {
  const [overview, setOverview] = useState(null);
  const [previewRegion, setPreviewRegionState] = useState(readPreviewRegion() || "");
  const [partners, setPartners] = useState([]);
  const [b2cUsers, setB2cUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [ads, setAds] = useState([]);
  const [adCounts, setAdCounts] = useState({});
  const [emailQuota, setEmailQuota] = useState(null);
  const [auditRefresh, setAuditRefresh] = useState(0);
  const [denied, setDenied] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryDismissed, setSummaryDismissed] = useState(false);
  const [wsOnline, setWsOnline] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const perms = new Set(ctx?.perms || []);
  const can = (p) => !ctx || perms.has(p);  // permissive until context loads
  const TAB_PERM = {
    partners: "users.view", directory: "users.view", b2c: "users.view", ads: "moderation",
    reports: "moderation",
    billing: "finance", terminations: "finance", messages: "users.view",
    establishments: "moderation", traffic: "admins.manage",
    alerts: "finance", payments: "finance", journal: "admins.manage", rgpd: "admins.manage",
    cookies: "admins.manage", devis: "finance", "signed-quotes": "finance", invoices: "finance",
    apercus: "finance",
    impersonation: "admins.manage", admins: "admins.manage", retention: "admins.manage",
    errors: "admins.manage", branding: "admins.manage", prospectus: "admins.manage",
    matching: "admins.manage",
    home: "admins.manage",
    "email-preview": "overview",
    "gdpr": "overview",
    "stats": "overview",
    "cancels": "overview",
    "free-unlocks": "overview",
    "security": "admins.manage",
    "disputes": "admins.manage",
  };
  const TAB_KEYS = Object.keys(TAB_PERM);
  const visibleTabs = TAB_KEYS.filter((k) => can(TAB_PERM[k]) && (k !== "disputes" || ctx?.is_main));
  const firstTab = visibleTabs[0] || "partners";
  const requested = TAB_KEYS.includes(searchParams.get("tab")) ? searchParams.get("tab") : firstTab;
  const activeTab = visibleTabs.includes(requested) ? requested : firstTab;
  const onTabChange = (v) => setSearchParams(v === "partners" ? {} : { tab: v });
  const onNotifNavigate = (n) => {
    const m = n.meta || {};
    if (n.type === "invoice") setSearchParams({ tab: "invoices", ...(m.invoice_number ? { focus: String(m.invoice_number) } : {}) });
    else if (n.type === "report") setSearchParams({ tab: "ads", ...(m.ad_id ? { focus: String(m.ad_id) } : {}) });
    else if (n.type === "devis") setSearchParams({ tab: "devis", ...(m.quote_id ? { focus: String(m.quote_id) } : {}) });
    else if (["stripe_webhook_silent", "stripe_reconciled"].includes(n.type)) setSearchParams({ tab: "payments" });
    else if (n.type === "sendcloud_mismatch") document.getElementById("sendcloud-reconciliation")?.scrollIntoView({ behavior: "smooth", block: "start" });
    else if (n.type === "server_memory") document.getElementById("server-health-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const reloadPartners = () =>
    api("/admin/partners?limit=200").then((r) => setPartners(r.partners || [])).catch(() => {});
  const reloadB2c = () =>
    api("/admin/b2c-users?limit=200").then((r) => setB2cUsers(r.users || [])).catch(() => {});
  const reloadAds = () =>
    api("/admin/public-ads").then((r) => { setAds(r.items || []); setAdCounts(r.counts_by_status || {}); }).catch(() => {});
  const bumpAudit = () => setAuditRefresh((n) => n + 1);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadTop = () => {
    api("/admin/console-context").then(setCtx).catch(() => {});
    api("/admin/my-summary").then(setSummary).catch(() => {});
    api("/admin/overview")
      .then(setOverview)
      .catch((e) => { if (/403|superadmin|Réservé|refusé/i.test(e.message)) setDenied(true); });
    reloadPartners();
    reloadB2c();
    reloadAds();
    api("/admin/email-quota").then(setEmailQuota).catch(() => {});
    api("/admin/payments?limit=50").then((r) => setPayments(r.payments || [])).catch(() => {});
  };

  const doRefresh = () => {
    loadTop();
    bumpAudit();
    setRefreshKey((k) => k + 1);
    toast.success("Données actualisées");
  };

  useEffect(() => {
    loadTop();
    // eslint-disable-next-line
  }, []);

  // Real-time counter refresh, pushed over the notification WebSocket ('kpi'
  // events) — no client-side polling. NotificationCenter falls back to polling
  // only while the socket is disconnected.
  const refreshCounters = () => {
    api("/admin/overview").then(setOverview).catch(() => {});
    reloadB2c();
    api("/admin/email-quota").then(setEmailQuota).catch(() => {});
  };

  if (denied) {
    return (
      <div className="p-10 max-w-3xl mx-auto">
        <PageHeader title="Superadmin" />
        <p className="text-slate-500" data-testid="superadmin-denied">Accès réservé au superadministrateur.</p>
      </div>
    );
  }

  const kpis = overview?.counts || overview || {};

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="Superadmin" subtitle="Vue globale de la plateforme findloop." testid="superadmin-header" />
        <div className="flex items-center gap-2 flex-shrink-0 mt-1">
          {!wsOnline && (
            <Badge className="bg-amber-100 text-amber-700 rounded-full font-semibold flex items-center gap-1.5 h-8 px-3" data-testid="ws-offline-badge">
              <WifiOff size={13} /> Hors ligne · reconnexion…
            </Badge>
          )}
          <NotificationCenter onNavigate={onNotifNavigate} onKpi={refreshCounters} onStatus={setWsOnline} />
          <div className="hidden sm:flex items-center gap-1.5 rounded-full border border-findo-indigo/30 bg-findo-indigo/5 pl-2.5 pr-1 h-9"
            title="Prévisualiser les pages publiques comme si l'utilisateur venait de cette région (n'affecte que votre navigateur)"
            data-testid="preview-region-selector">
            <Eye size={14} className="text-findo-indigo flex-shrink-0" />
            <span className="text-xs font-semibold text-findo-indigo">Prévisualiser</span>
            <select
              value={previewRegion}
              onChange={(e) => { const v = e.target.value; setPreviewRegionState(v); writePreviewRegion(v || null); }}
              className="h-7 rounded-full bg-white border border-findo-indigo/20 text-xs font-semibold text-slate-700 px-2 focus:outline-none focus:ring-2 focus:ring-findo-indigo/20"
              data-testid="preview-region-select">
              <option value="">Désactivé</option>
              <option value="europe">Europe</option>
              <option value="afrique">Afrique</option>
            </select>
          </div>
          <Button variant="outline" size="sm" onClick={doRefresh} className="rounded-full font-semibold" data-testid="superadmin-refresh-btn">
            <RefreshCw size={15} className="mr-1.5" /> Rafraîchir
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Kpi Icon={Building2} label="Partenaires" value={kpis.partners_total ?? partners.length} tone="indigo" testid="kpi-partners" />
        <Kpi Icon={Users} label="Utilisateurs B2C" value={b2cUsers.length} tone="teal" testid="kpi-b2c" />
        <Kpi Icon={Package} label="Objets" value={kpis.items_total ?? "—"} tone="amber" testid="kpi-items" />
        <Kpi Icon={Euro} label="Revenus (€)" value={fmtEur(kpis.revenue_total_eur)} tone="rose" testid="kpi-revenue-total" />
        <Kpi Icon={CalendarDays} label="Ce mois-ci (€)" value={fmtEur(kpis.revenue_month_eur)} tone="rose" testid="kpi-revenue-month"
          sub={<MonthTrend pct={kpis.revenue_month_trend_pct} prev={kpis.revenue_prev_month_eur} />} />
      </div>
      {can("admins.manage") && <ServerHealthCard key={`sh-${refreshKey}`} />}
      <RevenueBreakdown bySource={kpis.revenue_by_source} total={kpis.revenue_total_eur} month={kpis.revenue_month_eur} sendcloud={kpis.shipping_sendcloud} />
      {can("finance") && <SendcloudReconciliation key={`sc-${refreshKey}`} />}
      {can("finance") && <RevenueMonthlyChart />}

      <ExpiringContractsBanner canView={can("finance")} onGo={() => onTabChange("signed-quotes")} />

      <EmailQuotaCard quota={emailQuota} onSaved={(q) => setEmailQuota(q)} />

      {summary && !summaryDismissed && summary.items?.some((i) => i.count > 0) && (
        <div className="mb-6 rounded-2xl border border-findo-indigo/20 bg-findo-indigo/5 p-4" data-testid="login-summary">
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="font-display font-semibold text-findo-navy flex items-center gap-2">
              <BellRing size={17} className="text-findo-indigo" /> Ce qui vous attend
            </p>
            <button onClick={() => setSummaryDismissed(true)} className="text-slate-400 hover:text-slate-600" data-testid="login-summary-dismiss"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {summary.items.filter((i) => i.count > 0).map((i) => (
              <button key={i.key} onClick={() => onTabChange(i.tab)} data-testid={`login-summary-${i.key}`}
                className="text-left bg-white rounded-xl border border-border p-3 hover:border-findo-indigo hover:shadow-sm transition-all">
                <p className="text-2xl font-display font-bold text-findo-indigo">{i.count}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-tight">{i.label}</p>
              </button>
            ))}
          </div>
        </div>
      )}


      <Tabs key={refreshKey} value={activeTab} onValueChange={onTabChange}>
        <TabsList className="rounded-xl flex flex-wrap h-auto justify-start gap-1 p-1.5" data-testid="superadmin-tabs-list">
          {can("users.view") && <TabsTrigger value="partners" data-testid="tab-partners">Partenaires B2B</TabsTrigger>}
          {can("users.view") && <TabsTrigger value="directory" data-testid="tab-directory">Annuaire B2B</TabsTrigger>}
          {can("users.view") && <TabsTrigger value="b2c" data-testid="tab-b2c">Utilisateurs B2C</TabsTrigger>}
          {can("moderation") && <TabsTrigger value="ads" data-testid="tab-ads">Annonces</TabsTrigger>}
          {can("moderation") && <TabsTrigger value="reports" data-testid="tab-reports">Signalements</TabsTrigger>}
          {can("moderation") && <TabsTrigger value="establishments" data-testid="tab-establishments">Établissements</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="retention" data-testid="tab-retention">Conservation</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="traffic" data-testid="tab-traffic">Trafic</TabsTrigger>}
          {can("finance") && <TabsTrigger value="alerts" data-testid="tab-alerts">Alertes</TabsTrigger>}
          {can("finance") && <TabsTrigger value="payments" data-testid="tab-payments">Paiements</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="journal" data-testid="tab-journal">Journal</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="errors" data-testid="tab-errors">Erreurs</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="rgpd" data-testid="tab-rgpd">RGPD</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="cookies" data-testid="tab-cookies">Cookies</TabsTrigger>}
          {can("finance") && <TabsTrigger value="devis" data-testid="tab-devis">Devis</TabsTrigger>}
          {can("finance") && <TabsTrigger value="signed-quotes" data-testid="tab-signed-quotes">Devis signés</TabsTrigger>}
          {can("finance") && <TabsTrigger value="invoices" data-testid="tab-invoices">Factures devis</TabsTrigger>}
          {can("finance") && <TabsTrigger value="apercus" data-testid="tab-apercus">Aperçus</TabsTrigger>}
          {can("finance") && <TabsTrigger value="billing" data-testid="tab-billing">Facturation</TabsTrigger>}
          {can("finance") && <TabsTrigger value="terminations" data-testid="tab-terminations">Résiliations</TabsTrigger>}
          {can("users.view") && <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="impersonation" data-testid="tab-impersonation">Impersonation</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="admins" data-testid="tab-admins">Admins</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="branding" data-testid="tab-branding">Marque</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="prospectus" data-testid="tab-prospectus">Prospectus A5</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="matching" data-testid="tab-matching">IA</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="home" data-testid="tab-home">Accueil</TabsTrigger>}
          {can("overview") && <TabsTrigger value="email-preview" data-testid="tab-email-preview">Aperçu e-mails</TabsTrigger>}
          {can("overview") && <TabsTrigger value="gdpr" data-testid="tab-gdpr">RGPD</TabsTrigger>}
          {can("overview") && <TabsTrigger value="stats" data-testid="tab-stats">Statistiques</TabsTrigger>}
          {can("overview") && <TabsTrigger value="cancels" data-testid="tab-cancels">Annulations</TabsTrigger>}
          {can("overview") && <TabsTrigger value="free-unlocks" data-testid="tab-free-unlocks">Offre Afrique</TabsTrigger>}
          {can("admins.manage") && <TabsTrigger value="security" data-testid="tab-security">Sécurité</TabsTrigger>}
          {ctx?.is_main && <TabsTrigger value="disputes" data-testid="tab-disputes">Litiges</TabsTrigger>}
        </TabsList>

        <TabsContent value="partners" className="mt-5">
          <PartnersPanel partners={partners} reload={() => { reloadPartners(); bumpAudit(); }} />
        </TabsContent>

        <TabsContent value="directory" className="mt-5">
          <PartnerDirectoryPanel />
        </TabsContent>

        <TabsContent value="billing" className="mt-5">
          <CheckoutAbandonsPanel />
          <BillingPanel />
        </TabsContent>

        <TabsContent value="terminations" className="mt-5">
          <TerminationsPanel />
        </TabsContent>

        <TabsContent value="prospectus" className="mt-5">
          <ProspectusA5 />
        </TabsContent>

        <TabsContent value="branding" className="mt-5">
          <BrandingPanel />
        </TabsContent>

        <TabsContent value="matching" className="mt-5">
          <MatchSettingsPanel />
        </TabsContent>

        <TabsContent value="home" className="mt-5">
          <HomeSettingsPanel />
        </TabsContent>

        <TabsContent value="email-preview" className="mt-5">
          <EmailPreviewPanel />
        </TabsContent>

        <TabsContent value="gdpr" className="mt-5">
          <ConsentExportPanel />
        </TabsContent>

        <TabsContent value="stats" className="mt-5">
          <GlobalStatsPanel />
        </TabsContent>

        <TabsContent value="apercus" className="mt-5">
          <BillingPreviewLogsPanel />
        </TabsContent>

        <TabsContent value="messages" className="mt-5">
          <ContactMessagesPanel />
        </TabsContent>

        <TabsContent value="b2c" className="mt-5">
          <B2CPanel users={b2cUsers} reload={() => { reloadB2c(); bumpAudit(); }} />
        </TabsContent>

        <TabsContent value="ads" className="mt-5">
          <AdsPanel ads={ads} counts={adCounts} reload={() => { reloadAds(); bumpAudit(); }} />
        </TabsContent>

        <TabsContent value="reports" className="mt-5">
          <ReportsPanel />
        </TabsContent>

        <TabsContent value="establishments" className="mt-5">
          <EstablishmentsAdminPanel />
        </TabsContent>

        <TabsContent value="retention" className="mt-5">
          <LegalRetentionPanel />
          <DispositionJournalPanel />
        </TabsContent>

        <TabsContent value="traffic" className="mt-5">
          <CityAnalyticsPanel />
        </TabsContent>

        <TabsContent value="alerts" className="mt-5">
          <AlertsPanel />
        </TabsContent>

        <TabsContent value="payments" className="mt-5">
          <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden">
            {payments.map((p, i) => (
              <div key={i} className="flex items-center gap-4 p-4 text-sm">
                <Badge className="bg-findo-indigo/10 text-findo-indigo capitalize">{p.kind}</Badge>
                <span className="flex-1 text-slate-600 truncate">{p.declaration_id || p.partner_id || "—"}</span>
                <span className="font-bold text-slate-900">{p.amount_eur ?? p.amount ?? 0}€</span>
                <Badge className="bg-teal-100 text-teal-700">{p.status}</Badge>
              </div>
            ))}
            {payments.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">Aucun paiement.</p>}
          </div>
        </TabsContent>

        <TabsContent value="journal" className="mt-5">
          <AuditPanel refreshKey={auditRefresh} />
        </TabsContent>

        <TabsContent value="errors" className="mt-5">
          <ErrorLogsPanel />
        </TabsContent>

        <TabsContent value="rgpd" className="mt-5">
          <GdprPanel />
        </TabsContent>

        <TabsContent value="cookies" className="mt-5">
          <CookieConsentPanel />
        </TabsContent>

        <TabsContent value="devis" className="mt-5">
          <QuotesPanel />
        </TabsContent>

        <TabsContent value="signed-quotes" className="mt-5">
          <SignedQuotesPanel />
        </TabsContent>

        <TabsContent value="invoices" className="mt-5">
          <InvoicesPanel />
        </TabsContent>

        <TabsContent value="impersonation" className="mt-5">
          <ImpersonationLogsPanel />
        </TabsContent>

        <TabsContent value="admins" className="mt-5">
          <AdminsPanel ctx={ctx} />
        </TabsContent>

        <TabsContent value="security" className="mt-5">
          <SecurityLocksPanel />
        </TabsContent>
        <TabsContent value="cancels" className="mt-5">
          <CancelReasonsPanel />
        </TabsContent>
        <TabsContent value="free-unlocks" className="mt-5">
          <FreeUnlocksPanel />
        </TabsContent>
        <TabsContent value="disputes" className="mt-5">
          <DisputesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ─── Partners (B2B) ─────────────────────────────────────────────── */

function PartnersPanel({ partners, reload }) {
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [impTarget, setImpTarget] = useState(null);
  const [estTarget, setEstTarget] = useState(null);

  const filtered = partners.filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return `${p.org_name} ${p.email}`.toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un partenaire…" className="pl-9 h-10 rounded-full" data-testid="partner-search" />
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="partner-add-btn">
          <UserPlus size={16} className="mr-1.5" /> Ajouter un partenaire
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden" data-testid="partners-list">
        {filtered.map((p) => {
          const active = p.subscription_status !== "suspended";
          return (
            <div key={p.id} className="flex items-center gap-3 p-4" data-testid="partner-row">
              <span className="text-xl">{countryFlag(p.country)}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{p.org_name}</p>
                <p className="text-xs text-slate-400 truncate">{p.email}</p>
                {p.cgu_accepted_at && (
                  <p className="text-[11px] text-teal-600 truncate" data-testid="partner-cgu-accepted" title="Date et heure d'acceptation des CGU">
                    CGU acceptées le {new Date(p.cgu_accepted_at).toLocaleString("fr-FR")}
                  </p>
                )}
              </div>
              <Badge className="bg-slate-100 text-slate-600 capitalize hidden sm:inline-flex">{p.plan_tier || p.tier || "free"}</Badge>
              <Badge className={active ? "bg-teal-100 text-teal-700" : "bg-red-100 text-red-700"}>
                {active ? "Actif" : "Suspendu"}
              </Badge>
              <button type="button" onClick={() => setEstTarget(p)} title="Établissements & facturation"
                className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-findo-indigo transition-colors" data-testid="partner-establishments-btn">
                <Building size={16} />
              </button>
              <RowActions
                active={active}
                onToggle={() => toggle(p, active, reload)}
                onEdit={() => { setEditing(p); setFormOpen(true); }}
                onReset={() => resetPw(`/admin/partners/${p.id}/reset-password`, reload)}
                onDelete={() => del(`/admin/partners/${p.id}`, `le partenaire ${p.org_name}`, reload)}
                onImpersonate={() => setImpTarget({ type: "partner", id: p.id, name: p.org_name })}
                onReactivate={p.contract_terminated ? (async () => {
                  try { const r = await api(`/admin/partners/${p.id}/reactivate`, { method: "POST" }); toast.success(`Partenaire réactivé (${r.invoices_settled} facture(s) régularisée(s))`); reload(); }
                  catch (e) { toast.error(e.message); }
                }) : undefined}
                testidPrefix="partner"
              />
            </div>
          );
        })}
        {filtered.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">Aucun partenaire.</p>}
      </div>

      <PartnerFormDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing} reload={reload} />
      <AdminEstablishmentDialog partner={estTarget} onClose={() => setEstTarget(null)} reload={reload} />
      <ImpersonateDialog target={impTarget} onClose={() => setImpTarget(null)} />
    </div>
  );

  async function toggle(p, active, reload) {
    // `active` is the CURRENT state; suspending when currently active.
    let reason = "";
    if (active) {
      const r = window.prompt("Motif de la suspension (envoyé au partenaire par e-mail, facultatif) :", "");
      if (r === null) return;
      reason = r;
    }
    try {
      const qs = new URLSearchParams({ active: String(!active) });
      if (active && reason.trim()) qs.set("reason", reason.trim());
      await api(`/admin/partners/${p.id}/toggle?${qs.toString()}`, { method: "PATCH" });
      toast.success(!active ? "Partenaire activé" : "Partenaire suspendu (e-mail envoyé)");
      reload();
    } catch (e) { toast.error(e.message); }
  }
}

function AdminEstablishmentDialog({ partner, onClose, reload }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!partner;
  useEffect(() => {
    if (!partner) { setData(null); setName(""); setCity(""); return; }
    api(`/admin/partners/${partner.id}/establishments`).then(setData).catch(() => setData(null));
  }, [partner]);
  const unit = data?.extra_unit_price_eur || 0;
  const projected = data ? Math.round((data.monthly_total_eur + (data.can_add ? unit : 0)) * 100) / 100 : 0;
  async function add() {
    if (name.trim().length < 2) { toast.error("Nom d'établissement requis"); return; }
    setBusy(true);
    try {
      const r = await api(`/admin/partners/${partner.id}/establishments`, { method: "POST", body: { name: name.trim(), city: city.trim() || undefined } });
      setData(r); setName(""); setCity("");
      toast.success(`Établissement ajouté · nouveau total ${r.monthly_total_eur} €/mois`);
      reload && reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function downloadPdf() {
    try {
      const res = await fetch(`${API}/admin/partners/${partner.id}/establishments/preview.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "apercu-facturation.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Aperçu PDF indisponible"); }
  }
  async function sendPreviewEmail() {
    const note = window.prompt("Note personnelle à ajouter dans l'e-mail (optionnel) :", "") ?? "";
    try {
      const r = await api("/admin/establishments/send-preview", { method: "POST", body: { partner_id: partner.id, note: note.trim() || undefined } });
      toast.success(`Aperçu envoyé à ${r.email} (${r.draft})`);
    } catch (e) { toast.error(e.message || "Envoi impossible"); }
  }
  async function generateInvoice(force = false) {
    const msg = force
      ? "Régénérer (recalculer) la facture du mois en cours ? La facture non payée existante sera remplacée par le montant actuel."
      : "Convertir cet aperçu en facture mensuelle officielle du partenaire ?";
    if (!window.confirm(msg)) return;
    try {
      const url = `/admin/partners/${partner.id}/monthly-invoices/generate${force ? "?force=true" : ""}`;
      const r = await api(url, { method: "POST" });
      const verb = r.regenerated ? "régénérée" : "créée";
      toast.success(`Facture ${r.invoice.number} ${verb} · ${r.invoice.total_ttc} € TTC (${r.invoice.status})`, { duration: 8000 });
    } catch (e) { toast.error(e.message || "Génération impossible"); }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-establishment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Établissements — {partner?.org_name}</DialogTitle>
          <DialogDescription>Établissement principal au tarif de base, chaque établissement supplémentaire -20%. Le tout sur une facture mensuelle unique consolidée.</DialogDescription>
        </DialogHeader>
        {!data ? <PageLoader label="Chargement…" className="py-8" size={32} /> : (
          <div className="space-y-3">
            <div className="rounded-xl border border-border p-3 text-sm space-y-1.5" data-testid="establishment-recap">
              <div className="flex justify-between"><span className="text-slate-500">Formule</span><b className="capitalize">{data.tier}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">Établissements actuels</span><b>{data.count}</b></div>
            </div>

            {/* Détail ligne par ligne — facture consolidée */}
            <div className="rounded-xl border border-border overflow-hidden" data-testid="establishment-lines">
              <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 border-b border-border">Détail de la facture mensuelle consolidée</div>
              <ul className="divide-y divide-border">
                {(data.establishments || []).map((row) => (
                  <li key={row.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`establishment-line-${row.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-findo-navy truncate">{row.name}{row.is_primary ? " · principal" : ""}</p>
                      <p className="text-xs text-slate-400">{row.label}</p>
                    </div>
                    <b className="text-slate-700 whitespace-nowrap pl-3">
                      {row.on_quote ? "sur devis" : `${row.monthly_eur} €`}
                    </b>
                  </li>
                ))}
              </ul>
            </div>

            {/* Récapitulatif TTC */}
            <div className="rounded-xl border border-border p-3 text-sm space-y-1.5" data-testid="establishment-totals">
              <div className="flex justify-between"><span className="text-slate-500">Sous-total HT</span><b data-testid="establishment-total-ht">{data.monthly_total_eur} €</b></div>
              <div className="flex justify-between"><span className="text-slate-500">TVA ({data.vat_pct}%)</span><b data-testid="establishment-total-tva">{data.vat_eur} €</b></div>
              <div className="flex justify-between text-findo-navy border-t border-border pt-1.5"><span className="font-semibold">Total TTC / mois</span><b data-testid="establishment-total-ttc">{data.monthly_total_ttc_eur} €</b></div>
              {data.vat_mention ? <p className="text-[11px] text-slate-400 pt-1">{data.vat_mention}</p> : null}
              <button type="button" onClick={() => generateInvoice(true)} className="text-[11px] font-semibold text-findo-indigo inline-flex items-center gap-1 hover:underline pt-1" data-testid="establishment-regenerate-invoice"><RefreshCw size={11} /> Régénérer la facture du mois (recalcul)</button>
            </div>

            {data.can_add && (
              <div className="rounded-xl bg-findo-indigo/5 border border-findo-indigo/20 p-3 text-sm space-y-1.5" data-testid="establishment-add-preview">
                <div className="flex justify-between"><span className="text-slate-500">Prix du prochain établissement (-20%)</span><b>+{unit} €</b></div>
                <div className="flex justify-between text-findo-navy"><span className="font-semibold">Nouveau total HT après ajout</span><b data-testid="establishment-projected-total">{projected} €/mois</b></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du nouvel établissement" className="rounded-xl h-10" data-testid="establishment-name" />
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ville (optionnel)" className="rounded-xl h-10" data-testid="establishment-city" />
            </div>
            {!data.can_add && <p className="text-xs text-findo-red" data-testid="establishment-limit">Limite d'établissements atteinte pour cette formule.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="establishment-close">Fermer</Button>
          <Button variant="outline" onClick={downloadPdf} disabled={!data} className="rounded-full font-semibold gap-1.5" data-testid="establishment-pdf"><Download size={14} /> Aperçu PDF</Button>
          <Button variant="outline" onClick={sendPreviewEmail} disabled={!data} className="rounded-full font-semibold gap-1.5" data-testid="establishment-email"><Mail size={14} /> Envoyer</Button>
          <Button variant="outline" onClick={() => generateInvoice(false)} disabled={!data} className="rounded-full font-semibold gap-1.5 text-findo-indigo border-findo-indigo/40" data-testid="establishment-generate-invoice"><FileText size={14} /> Convertir en facture</Button>
          <Button onClick={add} disabled={busy || !data || !data.can_add} className="rounded-full bg-findo-navy font-semibold" data-testid="establishment-confirm">
            {busy ? <FindoSpinner size={16} /> : "Confirmer l'ajout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartnerFormDialog({ open, onClose, editing, reload }) {
  const isEdit = !!editing;
  const empty = { email: "", org_name: "", country: "FR", plan_tier: "free", category: "", password: "",
    kbis: "", vat_number: "", phone: "", address_street: "", address_city: "", address_postal: "", address_country: "", establishments: [] };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const frRequired = form.country === "FR";

  // Live consolidated billing recap (HT/TVA/TTC + per-line) while provisioning.
  useEffect(() => {
    if (isEdit || !open) { setPreview(null); return; }
    const est = (form.establishments || []).map((n) => (n || "").trim()).filter(Boolean).map((n) => ({ name: n }));
    const h = setTimeout(() => {
      api("/admin/establishments/price-preview", { method: "POST", body: { plan_tier: form.plan_tier, country: form.country, establishments: est } })
        .then(setPreview).catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(h);
  }, [isEdit, open, form.plan_tier, form.country, JSON.stringify(form.establishments)]);

  async function downloadPreviewPdf() {
    try {
      const est = (form.establishments || []).map((n) => (n || "").trim()).filter(Boolean).map((n) => ({ name: n }));
      const res = await fetch(`${API}/admin/establishments/billing-preview.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ org_name: form.org_name, plan_tier: form.plan_tier, country: form.country, establishments: est }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "apercu-facturation.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Aperçu PDF indisponible"); }
  }

  async function sendPreviewEmail() {
    const to = (form.email || "").trim();
    if (!to || !to.includes("@")) { toast.error("Renseignez l'e-mail du partenaire d'abord."); return; }
    const note = window.prompt("Note personnelle à ajouter dans l'e-mail (optionnel) :", "") ?? "";
    try {
      const est = (form.establishments || []).map((n) => (n || "").trim()).filter(Boolean).map((n) => ({ name: n }));
      const r = await api("/admin/establishments/send-preview", { method: "POST", body: { email: to, org_name: form.org_name, plan_tier: form.plan_tier, country: form.country, establishments: est, note: note.trim() || undefined } });
      toast.success(`Aperçu envoyé à ${r.email} (${r.draft})`);
    } catch (e) { toast.error(e.message || "Envoi impossible"); }
  }

  useEffect(() => {
    if (editing) setForm({ ...empty, email: editing.email, org_name: editing.org_name || "", country: editing.country || "FR", plan_tier: editing.plan_tier || editing.tier || "free", category: editing.category || "",
      kbis: editing.kbis || "", vat_number: editing.vat_number || "", phone: editing.phone || "",
      address_street: editing.address?.street || "", address_city: editing.address?.city || "", address_postal: editing.address?.postal_code || "", address_country: editing.address?.country || "" });
    else setForm(empty);
  }, [editing, open]);

  async function submit(e) {
    e.preventDefault();
    if (frRequired && (!form.kbis.trim() || !form.vat_number.trim())) {
      toast.error("Le numéro Kbis et le numéro de TVA sont obligatoires pour la France."); return;
    }
    setBusy(true);
    const legal = {
      kbis: form.kbis.trim() || undefined, vat_number: form.vat_number.trim() || undefined,
      phone: form.phone.trim() || undefined,
      address: { street: form.address_street.trim() || undefined, city: form.address_city.trim() || undefined,
        postal_code: form.address_postal.trim() || undefined, country: form.address_country.trim() || undefined },
    };
    try {
      if (isEdit) {
        await api(`/admin/partners/${editing.id}`, { method: "PATCH", body: { org_name: form.org_name, country: form.country, plan_tier: form.plan_tier, category: form.category || undefined, ...legal } });
        toast.success("Partenaire modifié");
      } else {
        const estList = (form.establishments || []).map((n) => n.trim()).filter(Boolean).map((n) => ({ name: n }));
        const r = await api("/admin/partners", { method: "POST", body: { email: form.email.trim().toLowerCase(), org_name: form.org_name, country: form.country, plan_tier: form.plan_tier, category: form.category || undefined, password: form.password || undefined, establishments: estList.length ? estList : undefined, ...legal } });
        const totalNote = r.billing_breakdown ? ` · total ${r.billing_breakdown.monthly_total_eur} €/mois (${r.billing_breakdown.count} établissement·s)` : "";
        toast.success(`Partenaire créé${r.initial_password ? ` · mot de passe : ${r.initial_password}` : ""}${totalNote}`, { duration: 10000 });
      }
      onClose();
      reload();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="partner-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Modifier le partenaire" : "Ajouter un partenaire"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="findo-numbered space-y-5">
          <div><Label>Email</Label><Input type="email" required disabled={isEdit} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-email" /></div>
          <div><Label>Nom de l'établissement</Label><Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-org" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Pays</Label>
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm" data-testid="partner-form-country">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{`${c.flag} ${c.name}`}</option>)}
              </select>
            </div>
            <div><Label>Plan</Label>
              <select value={form.plan_tier} onChange={(e) => setForm({ ...form, plan_tier: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm capitalize" data-testid="partner-form-tier">
                {PARTNER_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Catégorie</Label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm" data-testid="partner-form-category">
              <option value="">Non catégorisé</option>
              {PARTNER_CATEGORY_KEYS.map((k) => <option key={k} value={k}>{PARTNER_CATEGORY_LABELS_FR[k]}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Numéro Kbis{frRequired ? " *" : ""}</Label><Input value={form.kbis} onChange={(e) => setForm({ ...form, kbis: e.target.value })} required={frRequired} placeholder="ex. 552 032 534 R.C.S." className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-kbis" /></div>
            <div><Label>Numéro de TVA{frRequired ? " *" : ""}</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} required={frRequired} placeholder="ex. FR12345678901" className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-vat" /></div>
          </div>
          <div><Label>Téléphone (optionnel)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+33 1 23 45 67 89" className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-phone" /></div>
          <div><Label>Adresse — Rue</Label><Input value={form.address_street} onChange={(e) => setForm({ ...form, address_street: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-street" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Ville</Label><Input value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-city" /></div>
            <div><Label>Code postal</Label><Input value={form.address_postal} onChange={(e) => setForm({ ...form, address_postal: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-postal" /></div>
            <div><Label>Pays (adresse)</Label><Input value={form.address_country} onChange={(e) => setForm({ ...form, address_country: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-addr-country" /></div>
          </div>
          {!isEdit && (
            <div><Label>Mot de passe (optionnel — généré si vide)</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="partner-form-password" /></div>
          )}
          {!isEdit && (
            <div data-testid="partner-form-establishments">
              <Label>Établissements supplémentaires (optionnel)</Label>
              <p className="text-xs text-slate-400 mb-1.5">Le principal est au tarif de base ; chaque établissement supplémentaire bénéficie de -20% (facture mensuelle unique consolidée).</p>
              {(form.establishments || []).map((val, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={val} onChange={(e) => { const arr = [...form.establishments]; arr[i] = e.target.value; setForm({ ...form, establishments: arr }); }} placeholder={`Établissement supplémentaire ${i + 1}`} className="h-10 rounded-xl" data-testid={`partner-form-est-${i}`} />
                  <Button type="button" variant="ghost" onClick={() => setForm({ ...form, establishments: form.establishments.filter((_, j) => j !== i) })} className="rounded-xl text-findo-danger" data-testid={`partner-form-est-remove-${i}`}>✕</Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, establishments: [...(form.establishments || []), ""] })} className="rounded-full" data-testid="partner-form-est-add">+ Ajouter un établissement</Button>
            </div>
          )}
          {!isEdit && preview && (
            <div className="rounded-xl border border-findo-indigo/25 bg-findo-indigo/5 p-3 text-sm space-y-2" data-testid="partner-form-billing-recap">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold uppercase tracking-wide text-findo-indigo">Récapitulatif de facturation mensuelle</p>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={downloadPreviewPdf} className="text-xs font-semibold text-findo-navy inline-flex items-center gap-1 hover:underline" data-testid="partner-form-billing-pdf"><Download size={13} /> Aperçu PDF</button>
                  <button type="button" onClick={sendPreviewEmail} className="text-xs font-semibold text-findo-indigo inline-flex items-center gap-1 hover:underline" data-testid="partner-form-billing-email"><Mail size={13} /> Envoyer au partenaire</button>
                </div>
              </div>
              <div className="rounded-lg bg-white border border-border overflow-hidden">
                <ul className="divide-y divide-border" data-testid="partner-form-billing-lines">
                  {(preview.establishments || []).map((row, i) => (
                    <li key={i} className="flex items-center justify-between px-3 py-1.5" data-testid={`partner-form-billing-line-${i}`}>
                      <div className="min-w-0"><span className="font-medium text-findo-navy truncate">{row.name}{row.is_primary ? " · principal" : ""}</span><span className="block text-xs text-slate-400">{row.label}</span></div>
                      <b className="text-slate-700 whitespace-nowrap pl-3">{row.on_quote ? "sur devis" : `${row.monthly_eur} €`}</b>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Sous-total HT</span><b data-testid="partner-form-total-ht">{preview.monthly_total_eur} €</b></div>
                <div className="flex justify-between"><span className="text-slate-500">TVA ({preview.vat_pct}%)</span><b data-testid="partner-form-total-tva">{preview.vat_eur} €</b></div>
                <div className="flex justify-between text-findo-navy border-t border-findo-indigo/20 pt-1"><span className="font-semibold">Total TTC / mois</span><b data-testid="partner-form-total-ttc">{preview.monthly_total_ttc_eur} €</b></div>
              </div>
              {preview.vat_mention ? <p className="text-[11px] text-slate-400">{preview.vat_mention}</p> : null}
              {preview.exceeds_max && <p className="text-[11px] text-findo-danger font-semibold" data-testid="partner-form-billing-warning">La formule « {preview.tier} » est limitée à {preview.max_establishments} établissement(s) — retirez des sites ou changez de plan.</p>}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="w-full rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="partner-form-submit">{busy ? <FindoSpinner size={16} /> : isEdit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BillingPreviewLogsPanel() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const setSearchParams = useSearchParams()[1];
  const load = () => api("/admin/billing-preview-logs").then(setData).catch(() => setData({ logs: [], total: 0 }));
  useEffect(() => { load(); api("/admin/apercu-settings").then(setSettings).catch(() => {}); }, []);
  const fmt = (d) => d ? new Date(d).toLocaleString("fr-FR") : "—";

  async function markAccepted(id) {
    try {
      const r = await api(`/admin/billing-preview-logs/${id}`, { method: "PATCH", body: { status: "accepted" } });
      toast.success(r.linked_quote ? "Accepté et rattaché au devis signé du partenaire" : "Aperçu marqué comme accepté");
      load();
    } catch (e) { toast.error(e.message || "Échec"); }
  }
  async function saveDelay(days) {
    try {
      await api("/admin/apercu-settings", { method: "PUT", body: { reminder_days: Number(days) } });
      setSettings((s) => ({ ...s, reminder_days: Number(days) }));
      toast.success(`Relance automatique fixée à ${days} jours`);
    } catch (e) { toast.error(e.message || "Échec"); }
  }
  async function exportCsv() {
    try {
      const res = await fetch(`${API}/admin/billing-preview-logs/export.csv`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "findloop-apercus-facturation.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export CSV indisponible"); }
  }
  function openLinkedQuote(needle) {
    setSearchParams({ tab: "signed-quotes", focus: needle || "" });
  }

  const FILTERS = [["all", "Tous"], ["sent", "Envoyés"], ["accepted", "Acceptés"], ["reminded", "Relancés"]];
  const logs = (data?.logs || []).filter((l) => {
    if (statusFilter === "sent" && l.status !== "sent") return false;
    if (statusFilter === "accepted" && l.status !== "accepted") return false;
    if (statusFilter === "reminded" && !l.reminder_sent) return false;
    const q = query.trim().toLowerCase();
    if (q && !(`${l.org_name || ""} ${l.email || ""} ${l.draft || ""}`.toLowerCase().includes(q))) return false;
    return true;
  });

  return (
    <div data-testid="apercus-panel">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-semibold text-findo-navy">Journal des aperçus de facturation</h3>
          <p className="text-sm text-slate-500">Aperçus consolidés envoyés aux partenaires (date · partenaire · brouillon · total · statut).</p>
        </div>
        <div className="flex items-center gap-2">
          {settings && (
            <label className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="apercus-delay-wrap">
              Relance auto après
              <select value={settings.reminder_days} onChange={(e) => saveDelay(e.target.value)} className="h-9 rounded-full border border-border px-3 text-sm font-semibold text-findo-navy" data-testid="apercus-delay-select">
                {(settings.allowed_days || [3, 7, 14]).map((d) => <option key={d} value={d}>{d} jours</option>)}
              </select>
            </label>
          )}
          <Button variant="outline" onClick={exportCsv} disabled={!data || data.logs.length === 0} className="rounded-full font-semibold gap-1.5 shrink-0" data-testid="apercus-export-csv"><Download size={14} /> Export CSV</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="apercus-filters">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setStatusFilter(k)} data-testid={`apercus-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${statusFilter === k ? "bg-white text-findo-navy shadow-sm" : "text-slate-500 hover:text-findo-navy"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un partenaire…" className="h-9 rounded-full border border-border pl-9 pr-4 text-sm w-64" data-testid="apercus-search" />
        </div>
      </div>

      {!data ? <PageLoader label="Chargement…" className="py-8" size={32} /> : logs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-10 text-center text-slate-400" data-testid="apercus-empty">{(data.logs || []).length === 0 ? "Aucun aperçu envoyé pour le moment." : "Aucun aperçu ne correspond au filtre."}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="apercus-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Date d'envoi</th>
                <th className="text-left px-4 py-3">Partenaire</th>
                <th className="text-left px-4 py-3">Brouillon</th>
                <th className="text-left px-4 py-3">Destinataire</th>
                <th className="text-right px-4 py-3">Total TTC</th>
                <th className="text-left px-4 py-3">Relancé le</th>
                <th className="text-center px-4 py-3">Statut</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-border" data-testid="apercus-row">
                  <td className="px-4 py-3 text-slate-600">{fmt(l.sent_at)}</td>
                  <td className="px-4 py-3 font-semibold text-findo-navy">
                    {l.org_name || "—"}
                    {l.has_note ? <span className="ml-1.5 text-xs text-findo-indigo font-normal">· note</span> : ""}
                    {l.quote_id ? <button onClick={() => openLinkedQuote(l.email)} className="ml-1.5 text-xs text-emerald-600 font-semibold hover:underline inline-flex items-center gap-0.5" data-testid={`apercus-open-quote-${l.id}`}>· lié au devis <ChevronRight size={11} /></button> : ""}
                    {l.reminder_sent ? <span className="ml-1.5 text-xs text-amber-600 font-normal">· relancé</span> : ""}
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-600">{l.draft}</td>
                  <td className="px-4 py-3 text-slate-500">{l.email}</td>
                  <td className="px-4 py-3 text-right font-semibold">{l.total_ttc_eur != null ? `${l.total_ttc_eur} €` : "—"}</td>
                  <td className="px-4 py-3 text-slate-500" data-testid="apercus-reminded-at">{l.reminder_at ? fmt(l.reminder_at) : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-center">
                    {l.status === "accepted"
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1" data-testid="apercus-status-accepted"><Check size={12} /> Accepté</span>
                      : <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-500 text-xs font-semibold px-2.5 py-1" data-testid="apercus-status-sent">Envoyé</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {l.status !== "accepted" && (
                      <button onClick={() => markAccepted(l.id)} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid={`apercus-accept-${l.id}`}>Marquer accepté</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Facturation (consolidated invoices) ─────────────────────────── */
const BILLING_TYPES = [
  { key: "all", label: "Tous" },
  { key: "b2b_fr", label: "B2B France (Factur-X)" },
  { key: "b2b_intl", label: "International" },
  { key: "b2c", label: "B2C (particuliers)" },
];
const BILLING_TYPE_BADGE = {
  b2b_fr: "bg-findo-indigo/10 text-findo-indigo",
  b2b_intl: "bg-amber-100 text-amber-700",
  b2c: "bg-teal-100 text-teal-700",
};
const BILLING_TYPE_LABEL = { b2b_fr: "B2B France", b2b_intl: "International", b2c: "B2C" };

const BROCHURE_LANGS = [["fr", "Français"], ["en", "English"], ["es", "Español"], ["de", "Deutsch"], ["it", "Italiano"], ["pt", "Português"]];

function BrochureCard() {
  const [lang, setLang] = useState("fr");
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/brochure.pdf?lang=${lang}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `findloop-prospectus-b2b-${lang}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Prospectus téléchargé");
    } catch { toast.error("Génération du prospectus impossible"); } finally { setBusy(false); }
  }
  return (
    <div className="mb-8 rounded-2xl border border-border bg-white p-5 flex items-center gap-5 flex-wrap card-shadow" data-testid="brochure-card">
      <div className="h-14 w-14 rounded-2xl bg-findo-navy text-white grid place-items-center shrink-0"><FileText size={26} /></div>
      <div className="flex-1 min-w-[220px]">
        <h2 className="font-display font-bold text-slate-900 text-lg">Prospectus B2B (PDF)</h2>
        <p className="text-sm text-slate-500">Demi-A4 (148 × 210 mm), 2 pages, fond perdu 3 mm + traits de coupe : arguments clés, fonctionnement, secteurs, contact et QR code.</p>
      </div>
      <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-10 rounded-xl border border-border px-3 text-sm font-semibold text-slate-700 bg-white" data-testid="brochure-lang-select">
        {BROCHURE_LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
      </select>
      <Button onClick={download} disabled={busy} className="rounded-xl font-semibold" data-testid="brochure-download-btn">
        <Download size={15} className="mr-1.5" /> {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Génération…</span> : "Télécharger le prospectus"}
      </Button>
    </div>
  );
}

async function downloadAdminPdf(path, filename) {
  try {
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error();
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success("PDF téléchargé");
  } catch { toast.error("Génération du PDF impossible"); }
}

function PrintDocRow({ title, desc, children }) {
  return (
    <div className="flex items-center gap-4 flex-wrap py-3 border-t border-border first:border-t-0 first:pt-0">
      <div className="flex-1 min-w-[220px]">
        <p className="font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      {children}
    </div>
  );
}

function PrintDocsCard() {
  const [lang, setLang] = useState("fr");
  const [busy, setBusy] = useState("");
  const run = async (key, path, name) => { setBusy(key); await downloadAdminPdf(path, name); setBusy(""); };
  const btn = (key, label, path, name) => (
    <Button onClick={() => run(key, path, name)} disabled={!!busy} variant="outline" className="rounded-xl font-semibold" data-testid={`print-${key}-btn`}>
      <Download size={15} className="mr-1.5" /> {busy === key ? "Génération…" : label}
    </Button>
  );
  return (
    <div className="mb-8 rounded-2xl border border-border bg-white p-5 card-shadow" data-testid="print-docs-card">
      <h2 className="font-display font-bold text-slate-900 text-lg mb-3">Documents d'impression</h2>
      <PrintDocRow title="Carte de visite findloop" desc="85 × 55 mm, recto-verso, fond perdu 3 mm + traits de coupe, vectoriel.">
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-10 rounded-xl border border-border px-3 text-sm font-semibold text-slate-700 bg-white" data-testid="print-findo-card-lang">
          {BROCHURE_LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
        {btn("findo-card", "Télécharger la carte de visite (PDF)", `/admin/business-card.pdf?lang=${lang}`, `findloop-carte-de-visite-${lang}.pdf`)}
      </PrintDocRow>
      <PrintDocRow title="Prospectus Marcel Nett.Services" desc="Demi-A4 (148 × 210 mm), une page, fond perdu 3 mm + traits de coupe, QR code.">
        {btn("mn-flyer", "Télécharger le prospectus Marcel Nett (PDF)", "/admin/marcelnett/flyer.pdf", "marcel-nett-services-prospectus.pdf")}
      </PrintDocRow>
      <PrintDocRow title="Carte de visite Marcel Nett.Services" desc="85 × 55 mm, recto-verso, fond perdu 3 mm + traits de coupe.">
        {btn("mn-card", "Télécharger la carte de visite Marcel Nett (PDF)", "/admin/marcelnett/business-card.pdf", "marcel-nett-services-carte-de-visite.pdf")}
      </PrintDocRow>
    </div>
  );
}

function BrandingPanel() {
  const [assets, setAssets] = useState(null);
  const [langs, setLangs] = useState(["fr"]);
  const [lang, setLang] = useState("fr");
  const [variant, setVariant] = useState("dark");
  const [scene, setScene] = useState("plain");
  useEffect(() => {
    api("/branding/assets", { auth: false })
      .then((r) => { setAssets(r.assets); setLangs(r.languages || ["fr"]); })
      .catch(() => setAssets([]));
  }, []);
  const kit = (assets || []).find((a) => a.slug === "findloop-brand-kit");
  const items = (assets || []).filter((a) => a.slug !== "findloop-brand-kit");
  const fileUrl = (a) => {
    const p = [];
    if (a.tagline) p.push(`lang=${lang}`);
    if (a.variant) p.push(`variant=${variant}`);
    if (a.scene) p.push(`scene=${scene}`);
    return `${API}/branding/file/${a.slug}${p.length ? "?" + p.join("&") : ""}`;
  };

  return (
    <div data-testid="branding-panel">
      <BrochureCard />
      <PrintDocsCard />
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-slate-900 text-lg">Logos & marque</h2>
          <p className="text-sm text-slate-500">Kit de logos findloop prêts pour les réseaux sociaux. Cliquez pour télécharger.</p>
        </div>
        {kit && (
          <a href={`${API}/branding/file/${kit.slug}?lang=${lang}&variant=${variant}&scene=${scene}`} download data-testid={`branding-download-${kit.slug}`}>
            <Button className="rounded-xl font-semibold">
              <Download size={15} className="mr-1.5" /> Tout télécharger (ZIP)
            </Button>
          </a>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-400 mr-1">Slogan</span>
        {langs.map((c) => (
          <button key={c} onClick={() => setLang(c)} data-testid={`branding-lang-${c}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${lang === c ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border"}`}>
            {c.toUpperCase()}
          </button>
        ))}
        <span className="mx-2 h-4 w-px bg-border" />
        <span className="text-xs font-bold uppercase tracking-wide text-slate-400 mr-1">Fond</span>
        <button onClick={() => setVariant("dark")} data-testid="branding-variant-dark"
          className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${variant === "dark" ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border"}`}>Sombre</button>
        <button onClick={() => setVariant("light")} data-testid="branding-variant-light"
          className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${variant === "light" ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border"}`}>Clair</button>
        <span className="mx-2 h-4 w-px bg-border" />
        <span className="text-xs font-bold uppercase tracking-wide text-slate-400 mr-1">Scène</span>
        <button onClick={() => setScene("plain")} data-testid="branding-scene-plain"
          className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${scene === "plain" ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border"}`}>Uni</button>
        <button onClick={() => setScene("map")} data-testid="branding-scene-map"
          className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${scene === "map" ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border"}`}>Carte</button>
      </div>
      {assets === null ? (
        <PageLoader label="Chargement…" className="py-8" size={32} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="branding-grid">
          {items.map((a) => (
            <div key={a.slug} className="bg-white rounded-2xl border border-border p-4 card-shadow flex flex-col" data-testid={`branding-card-${a.slug}`}>
              <div className={`rounded-xl border border-border grid place-items-center overflow-hidden mb-3 ${a.slug.includes("transparent") ? "bg-[repeating-conic-gradient(#eef2f7_0%_25%,#fff_0%_50%)] bg-[length:20px_20px]" : "bg-slate-50"}`} style={{ aspectRatio: `${a.width} / ${a.height}`, minHeight: 96 }}>
                <img key={`${a.slug}-${lang}-${variant}-${scene}`} src={fileUrl(a)} alt={a.label} className="max-w-full max-h-44 object-contain" />
              </div>
              <p className="font-semibold text-slate-800 text-sm">{a.label}</p>
              <p className="text-xs text-slate-400 mb-3 uppercase">{a.width} × {a.height} px · {a.ext}</p>
              <a href={fileUrl(a)} download className="mt-auto" data-testid={`branding-download-${a.slug}`}>
                <Button size="sm" className="w-full rounded-xl font-semibold">
                  <Download size={14} className="mr-1.5" /> Télécharger
                </Button>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SecurityLocksPanel() {
  const [data, setData] = useState(null);
  const load = () => api("/admin/security/locks")
    .then((r) => setData({ accounts: r.accounts || [], ips: r.ips || [] }))
    .catch(() => setData({ accounts: [], ips: [] }));
  useEffect(() => { load(); }, []);
  const fmt = (s) => { try { return new Date(s).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };
  const minsLeft = (s) => { try { return Math.max(0, Math.ceil((new Date(s) - new Date()) / 60000)); } catch { return "—"; } };

  async function unlock(id) {
    try {
      await api("/admin/security/locks/unlock", { method: "POST", body: { id } });
      toast.success("Verrou levé");
      load();
    } catch { toast.error("Impossible de lever ce verrou"); }
  }

  const total = data ? data.accounts.length + data.ips.length : 0;

  return (
    <div className="bg-white rounded-2xl border border-border p-5 card-shadow" data-testid="security-locks-panel">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-red/10 text-findo-red"><Lock size={18} /></span>
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">Verrous de sécurité actifs</h3>
            <p className="text-xs text-slate-500">Comptes et adresses IP temporairement bloqués par la protection anti-force-brute (5 échecs/compte, 20 échecs/IP).</p>
          </div>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="rounded-full" data-testid="security-refresh">
          <RefreshCw size={15} className="mr-1.5" /> Actualiser
        </Button>
      </div>

      {data === null ? (
        <PageLoader label="Chargement…" className="mt-4" size={32} />
      ) : total === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center py-10 text-center" data-testid="security-empty">
          <span className="h-12 w-12 grid place-items-center rounded-2xl bg-teal-100 text-teal-600 mb-3"><ShieldCheck size={24} /></span>
          <p className="text-sm font-semibold text-slate-700">Aucun verrou actif</p>
          <p className="text-xs text-slate-400 mt-1">Aucune tentative de connexion suspecte en cours.</p>
        </div>
      ) : (
        <div className="mt-5 space-y-6">
          <LockGroup title="Comptes verrouillés" icon={<KeyRound size={15} />} rows={data.accounts}
            empty="Aucun compte verrouillé." fmt={fmt} minsLeft={minsLeft} unlock={unlock} kind="account" />
          <LockGroup title="Adresses IP verrouillées" icon={<Ban size={15} />} rows={data.ips}
            empty="Aucune IP verrouillée." fmt={fmt} minsLeft={minsLeft} unlock={unlock} kind="ip" />
        </div>
      )}
    </div>
  );
}

function LockGroup({ title, icon, rows, empty, fmt, minsLeft, unlock, kind }) {
  return (
    <div data-testid={`security-group-${kind}`}>
      <div className="flex items-center gap-2 mb-2 text-slate-700">
        <span className="text-findo-indigo">{icon}</span>
        <h4 className="font-display font-bold text-sm">{title}</h4>
        <Badge className="bg-slate-100 text-slate-600">{rows.length}</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`security-table-${kind}`}>
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-border">
                <th className="py-2 pr-4 font-semibold">{kind === "ip" ? "Adresse IP" : "Compte"}</th>
                {kind === "account" && <th className="py-2 pr-4 font-semibold">Type</th>}
                <th className="py-2 pr-4 font-semibold text-center">Échecs</th>
                <th className="py-2 pr-4 font-semibold">Déverrouillage</th>
                <th className="py-2 pr-4 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60" data-testid={`security-row-${r.id}`}>
                  <td className="py-2.5 pr-4 font-medium text-slate-900 break-all">{r.identifier}</td>
                  {kind === "account" && (
                    <td className="py-2.5 pr-4">
                      <Badge className="bg-findo-indigo/10 text-findo-indigo">{r.scope === "b2c" ? "Client B2C" : "Partenaire B2B"}</Badge>
                    </td>
                  )}
                  <td className="py-2.5 pr-4 text-center font-bold text-findo-danger">{r.fails}</td>
                  <td className="py-2.5 pr-4 text-slate-600">
                    {fmt(r.locked_until)} <span className="text-xs text-slate-400">(dans {minsLeft(r.locked_until)} min)</span>
                  </td>
                  <td className="py-2.5 pr-4 text-right">
                    <Button onClick={() => unlock(r.id)} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`security-unlock-${r.id}`}>
                      <Unlock size={13} /> Déverrouiller
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TerminationsPanel() {
  const [rows, setRows] = useState(null);
  const load = () => api("/admin/terminations").then((r) => setRows(r.terminations || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const fmtDate = (s) => { try { return new Date(s).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };
  async function download(kind) {
    try {
      const res = await fetch(`${API}/admin/terminations/export.${kind}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `findloop-resiliations.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(`Export ${kind.toUpperCase()} indisponible`); }
  }

  return (
    <div className="bg-white rounded-2xl border border-border p-5 card-shadow" data-testid="terminations-panel">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-danger/10 text-findo-danger"><Trash2 size={18} /></span>
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">Journal des résiliations</h3>
            <p className="text-xs text-slate-500">Résiliations automatiques pour non-paiement (J+90) — trace RGPD.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => download("csv")} disabled={!rows || rows.length === 0} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="terminations-export-csv"><Download size={14} /> CSV</Button>
          <Button onClick={() => download("pdf")} disabled={!rows || rows.length === 0} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="terminations-export-pdf"><Download size={14} /> PDF</Button>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full" data-testid="terminations-refresh">
            <RefreshCw size={15} className="mr-1.5" /> Actualiser
          </Button>
        </div>
      </div>

      {rows === null ? (
        <PageLoader label="Chargement…" className="mt-4" size={32} />
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 mt-4" data-testid="terminations-empty">Aucune résiliation pour non-paiement à ce jour.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="terminations-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-border">
                <th className="py-2 pr-4 font-semibold">Partenaire</th>
                <th className="py-2 pr-4 font-semibold">Date</th>
                <th className="py-2 pr-4 font-semibold">Facture</th>
                <th className="py-2 pr-4 font-semibold text-right">Montant</th>
                <th className="py-2 pr-4 font-semibold text-right">Objets purgés</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60" data-testid={`termination-row-${r.id}`}>
                  <td className="py-2.5 pr-4">
                    <div className="font-semibold text-slate-800">{r.org_name || "—"}</div>
                    <div className="text-xs text-slate-400">{r.subject_email}</div>
                  </td>
                  <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">{fmtDate(r.at)}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{r.invoice_number || "—"}</td>
                  <td className="py-2.5 pr-4 text-right font-semibold text-slate-800 whitespace-nowrap">{Number(r.amount_ttc || 0).toFixed(2)} {r.currency || "EUR"}</td>
                  <td className="py-2.5 pr-4 text-right text-findo-red font-semibold">{r.items_purged ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BillingDemoCard() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [simulate, setSimulate] = useState(true);
  const [withBoosts, setWithBoosts] = useState(false);

  async function run() {
    setRunning(true);
    setReport(null);
    try {
      const r = await api(`/admin/billing/demo-cycle?simulate=${simulate}&with_boosts=${withBoosts}`, { method: "POST" });
      setReport(r);
      toast.success("Cycle de facturation déroulé");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  }

  async function cleanup() {
    try {
      await api("/admin/billing/demo-cycle", { method: "DELETE" });
      setReport(null);
      toast.success("Partenaire de démo supprimé");
    } catch (e) { toast.error(e.message); }
  }

  const stageOk = (s) =>
    s.invoice_status === "paid" || s.charge_ok || s.subscription_status === "active";
  const stageBad = (s) =>
    s.subscription_status === "suspended" || s.invoice_status === "failed" || s.charge_ok === false;

  return (
    <div className="bg-white rounded-2xl border border-border p-5 card-shadow mb-4" data-testid="billing-demo-card">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo"><Euro size={18} /></span>
        <div>
          <h3 className="font-display font-bold text-lg text-slate-900">Test du cycle de facturation</h3>
          <p className="text-xs text-slate-500">Déroule sur un partenaire de démo : facture → prélèvement off-session → relances J+3/J+7/J+15 → suspension J+45 → régularisation.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="billing-demo-simulate">
          <input type="checkbox" checked={simulate} onChange={(e) => setSimulate(e.target.checked)} className="rounded" />
          Mode simulé (sans appel Stripe/e-mail réel)
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="billing-demo-boosts">
          <input type="checkbox" checked={withBoosts} onChange={(e) => setWithBoosts(e.target.checked)} className="rounded" />
          Injecter un boost de démo
        </label>
        <Button onClick={run} disabled={running} className="rounded-full font-semibold" data-testid="billing-demo-run">
          <RefreshCw size={16} className={`mr-1.5 ${running ? "animate-spin" : ""}`} />
          {running ? "En cours…" : "Lancer un test de facturation"}
        </Button>
        {report && (
          <Button onClick={cleanup} variant="outline" className="rounded-full" data-testid="billing-demo-cleanup">
            <Trash2 size={15} className="mr-1.5" /> Nettoyer
          </Button>
        )}
      </div>

      {report && (
        <div className="mt-4" data-testid="billing-demo-report">
          <p className="text-xs text-slate-500 mb-2">Partenaire de démo : <b>{report.partner_email}</b> · Facture <b>{report.invoice_number}</b> · Mode : <b>{report.simulate ? "simulé" : "réel"}</b></p>
          <ol className="space-y-2">
            {report.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl border border-border bg-slate-50 p-3" data-testid={`billing-demo-step-${i}`}>
                <span className={`mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full text-white text-[11px] font-bold ${stageBad(s) && !stageOk(s) ? "bg-findo-red" : stageOk(s) ? "bg-emerald-500" : "bg-amber-500"}`}>
                  {s.step}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 text-sm">{s.label}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                    {s.invoice_number && <span>Facture : {s.invoice_number}</span>}
                    {s.amount_ttc != null && <span>Montant : {s.amount_ttc} € TTC</span>}
                    {s.boost_included === true && <span className="text-findo-indigo">Boost inclus ({s.boost_lines})</span>}
                    {s.reason && <span>Motif : {s.reason}</span>}
                    {"charge_ok" in s && <span>Prélèvement : {s.charge_ok ? "réussi" : "échec"}</span>}
                    {s.invoice_status && <span>Facture : <b className={s.invoice_status === "paid" ? "text-emerald-600" : s.invoice_status === "failed" ? "text-findo-danger" : "text-amber-600"}>{s.invoice_status}</b></span>}
                    {s.dunning_stage != null && <span>Palier relance : {s.dunning_stage}</span>}
                    {"terminated" in s && <span>Résilié : <b className={s.terminated ? "text-findo-red" : "text-emerald-600"}>{s.terminated ? "oui" : "non"}</b></span>}
                    {s.items_purged === true && <span className="text-findo-red">Objets purgés (RGPD)</span>}
                    {s.subscription_status && <span>Abonnement : <b className={s.subscription_status === "suspended" ? "text-findo-red" : "text-emerald-600"}>{s.subscription_status}</b></span>}
                    {s.next_retry_at && <span>Prochaine tentative : {String(s.next_retry_at).slice(0, 10)}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {report.note && <p className="text-xs text-slate-400 mt-3 italic">{report.note}</p>}
        </div>
      )}
    </div>
  );
}

function BillingPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [year, setYear] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (type !== "all") p.set("client_type", type);
      if (status !== "all") p.set("status", status);
      if (year !== "all") p.set("year", year);
      if (q.trim()) p.set("q", q.trim());
      setData(await api(`/admin/billing?${p.toString()}`));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [type, status, year, q]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  async function download(row) {
    try {
      const res = await fetch(`${API}/admin/billing/download?source=${row.source}&ref=${encodeURIComponent(row.ref)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${row.number || "facture"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible"); }
  }

  const fmt = (n, cur) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${cur || "EUR"}`;

  const [mrr, setMrr] = useState(null);
  useEffect(() => { api("/admin/billing/mrr").then(setMrr).catch(() => {}); }, []);

  async function remind(u) {
    try {
      const r = await api("/admin/monthly-invoices/remind", { method: "POST", body: { email: u.client_email } });
      toast.success(`Relance envoyée (${r.invoice_number || ""})`);
    } catch (e) { toast.error(e.message); }
  }

  async function remindAll() {
    try {
      const r = await api("/admin/monthly-invoices/remind-all", { method: "POST" });
      toast.success(`${r.clients_reminded} client(s) relancé(s)`);
    } catch (e) { toast.error(e.message); }
  }

  const TIER_FR = { small: "Starter", medium: "Business", large: "Grands comptes" };

  return (
    <div data-testid="billing-panel">
      <BillingDemoCard />
      {mrr && (
        <div className="mb-4" data-testid="billing-mrr">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="bg-findo-navy text-white rounded-2xl p-4">
              <p className="text-xs opacity-80">Revenu mensuel récurrent (MRR)</p>
              <p className="font-display font-bold text-2xl">{fmt(mrr.total_mrr_eur, "EUR")}
                {mrr.mrr_delta_eur != null && mrr.mrr_delta_eur !== 0 && (
                  <span className={`ml-2 text-xs font-semibold ${mrr.mrr_delta_eur < 0 ? "text-findo-red" : "text-emerald-300"}`} data-testid="mrr-delta">
                    {mrr.mrr_delta_eur < 0 ? "▼" : "▲"} {fmt(Math.abs(mrr.mrr_delta_eur), "EUR")}
                  </span>
                )}
              </p>
              <p className="text-xs opacity-70">ARR {fmt(mrr.arr_eur, "EUR")} · {mrr.total_active_partners} abonné(s)</p>
            </div>
            {mrr.per_tier.map((t) => (
              <div key={t.tier} className="bg-white rounded-2xl border border-border p-4 card-shadow" data-testid={`mrr-tier-${t.tier}`}>
                <p className="text-xs text-slate-400">{TIER_FR[t.tier] || t.tier}</p>
                <p className="font-display font-bold text-slate-900 text-lg">{fmt(t.mrr_eur, "EUR")}</p>
                <p className="text-xs text-slate-400">{t.active_partners} abonné(s) · impayés <span className={t.unpaid_rate_pct > 0 ? "text-findo-red font-semibold" : ""}>{t.unpaid_rate_pct}%</span></p>
              </div>
            ))}
          </div>
          {mrr.alerts?.length > 0 && (
            <div className="space-y-1 mb-1" data-testid="mrr-alerts">
              {mrr.alerts.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-findo-red/5 border border-findo-red/20 rounded-xl px-3 py-2" data-testid={`mrr-alert-${i}`}>
                  <AlertTriangle size={13} className="text-findo-danger shrink-0" />
                  <span className="font-semibold text-findo-red">{a.title} :</span>
                  <span className="text-slate-600 truncate">{a.body}</span>
                  <span className="text-slate-400 ml-auto whitespace-nowrap">{a.date}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° facture, client, email…" className="h-10 rounded-full pl-9" data-testid="billing-search" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="billing-type-filter">
          {BILLING_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="billing-status-filter">
          <option value="all">Tous statuts</option>
          <option value="paid">Payée</option>
          <option value="issued">Émise</option>
          <option value="unpaid">À payer</option>
          <option value="pending">En attente</option>
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="billing-year-filter">
          <option value="all">Toutes années</option>
          {(data?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="billing-summary">
          {["b2b_fr", "b2b_intl", "b2c"].map((k) => (
            <div key={k} className="bg-white rounded-2xl border border-border p-4 card-shadow">
              <p className="text-xs text-slate-400">{BILLING_TYPES.find((t) => t.key === k).label}</p>
              <p className="font-display font-bold text-slate-900 text-lg">{fmt(data.totals[k], "EUR")}</p>
              <p className="text-xs text-slate-400">{data.counts[k] || 0} facture(s)</p>
            </div>
          ))}
        </div>
      )}

      {data && (data.unpaid_subscriptions?.length > 0) && (
        <div className="bg-findo-red/5 border border-findo-red/20 rounded-2xl p-4 mb-4" data-testid="billing-unpaid-subscriptions">
          <div className="flex items-center justify-between mb-2">
            <p className="font-display font-bold text-findo-red text-sm">Abonnements impayés</p>
            <div className="flex items-center gap-2">
              <p className="font-display font-bold text-findo-red">{fmt(data.unpaid_subscriptions_total, "EUR")}</p>
              <Button size="sm" className="rounded-full h-7 text-xs bg-findo-red hover:bg-findo-red/90" onClick={remindAll} data-testid="unpaid-remind-all">
                <Send size={12} className="mr-1" /> Tout relancer
              </Button>
            </div>
          </div>
          <div className="space-y-1">
            {data.unpaid_subscriptions.slice(0, 8).map((u, i) => (
              <div key={i} className="flex items-center justify-between text-xs gap-2" data-testid={`unpaid-sub-${i}`}>
                <span className="text-slate-700 truncate flex-1">{u.client_name} <span className="text-slate-400">· {u.client_email}</span></span>
                <span className="font-semibold text-slate-800 whitespace-nowrap">{u.count} facture(s) · {fmt(u.amount_ttc, u.currency)}</span>
                <Button size="sm" variant="outline" className="rounded-full h-7 text-xs border-findo-red/40 text-findo-red hover:bg-findo-red/10" onClick={() => remind(u)} data-testid={`unpaid-remind-${i}`}>
                  <Send size={12} className="mr-1" /> Relancer
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <PageLoader label="Chargement…" className="p-8" size={32} />}
      {!loading && data && (
        <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">N°</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Prestation</th>
                <th className="text-center px-4 py-3">Pays</th>
                <th className="text-right px-4 py-3">Montant TTC</th>
                <th className="text-center px-4 py-3">Statut</th>
                <th className="text-right px-4 py-3">Facture</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.invoices.map((r) => (
                <tr key={`${r.source}-${r.ref}`} data-testid={`billing-row-${r.ref}`}>
                  <td className="px-4 py-3"><Badge className={BILLING_TYPE_BADGE[r.type]}>{BILLING_TYPE_LABEL[r.type]}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs">{r.number || "—"}</td>
                  <td className="px-4 py-3"><span className="font-medium text-slate-800">{r.client_name}</span><span className="block text-xs text-slate-400">{r.client_email}</span></td>
                  <td className="px-4 py-3 text-slate-600">{r.kind}</td>
                  <td className="px-4 py-3 text-center">{r.country ? countryFlag(r.country) : "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmt(r.amount_ttc, r.currency)}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge className={r.status === "paid" ? "bg-teal-100 text-teal-700" : r.status === "pending" ? "bg-slate-100 text-slate-500" : "bg-findo-indigo/10 text-findo-indigo"}>{r.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => download(r)} data-testid={`billing-download-${r.ref}`}>
                      <Download size={14} className="mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
              {data.invoices.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400" data-testid="billing-empty">Aucune facture.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Contact messages ────────────────────────────────────────────── */
const REPLY_TEMPLATES = [
  {
    label: "Accusé de réception",
    text: "Nous avons bien reçu votre message et vous remercions de nous avoir contactés. Notre équipe l'étudie et reviendra vers vous dans les meilleurs délais.",
  },
  {
    label: "Demande d'informations",
    text: "Afin de traiter votre demande, pourriez-vous nous communiquer quelques informations complémentaires (par exemple : l'adresse email du compte concerné, la date et le lieu, ou une description détaillée) ?",
  },
  {
    label: "Dossier clôturé",
    text: "Votre demande a bien été traitée et votre dossier est désormais clôturé. N'hésitez pas à nous recontacter si vous avez la moindre question.",
  },
];

function ContactMessagesPanel() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [mtype, setMtype] = useState("all");

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (status !== "all") p.set("status", status);
      if (mtype !== "all") p.set("mtype", mtype);
      setData(await api(`/admin/contact-messages?${p.toString()}`));
    } catch (e) { toast.error(e.message); }
  }, [status, mtype]);
  useEffect(() => { load(); }, [load]);

  async function mark(m, s) {
    try { await api(`/admin/contact-messages/${m.id}?status=${s}`, { method: "PATCH" }); load(); }
    catch (e) { toast.error(e.message); }
  }

  const [replyOpen, setReplyOpen] = useState(null); // message id with reply box open
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);

  async function sendReply(m) {
    if (!replyText.trim()) return;
    setReplyBusy(true);
    try {
      const r = await api(`/admin/contact-messages/${m.id}/reply`, { method: "POST", body: { reply: replyText } });
      toast.success(r.email_sent ? `Réponse envoyée à ${r.to} · message marqué traité` : "Réponse enregistrée · message marqué traité (email non envoyé)");
      setReplyOpen(null); setReplyText("");
      load();
    } catch (e) { toast.error(e.message); } finally { setReplyBusy(false); }
  }

  return (
    <div data-testid="contact-messages-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="messages-status-filter">
          <option value="all">Tous</option><option value="new">Nouveaux</option><option value="handled">Traités</option><option value="archived">Archivés</option>
        </select>
        <select value={mtype} onChange={(e) => setMtype(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="messages-type-filter">
          <option value="all">Tous types</option>
          <option value="general">Général</option>
          <option value="deceased_deletion">Suppression (décès)</option>
        </select>
        {data && <span className="text-sm text-slate-400">{data.new_count} nouveau(x){data.archived_count ? ` · ${data.archived_count} archivé(s)` : ""}</span>}
      </div>
      {!data && <PageLoader label="Chargement…" className="p-8" size={32} />}
      <div className="space-y-3">
        {data && data.messages.map((m) => (
          <div key={m.id} className="bg-white rounded-2xl border border-border card-shadow p-4" data-testid={`message-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  {m.type === "deceased_deletion"
                    ? <Badge className="bg-rose-100 text-rose-700">Suppression (personne décédée)</Badge>
                    : <Badge className="bg-slate-100 text-slate-600">{m.subject || "Contact"}</Badge>}
                  {m.status === "new" && <Badge className="bg-findo-red/10 text-findo-red">Nouveau</Badge>}
                </div>
                <p className="text-sm font-semibold text-slate-800 mt-1">{m.name} · <span className="text-slate-400 font-normal">{m.email}</span></p>
                {m.type === "deceased_deletion" && (
                  <p className="text-xs text-slate-500 mt-0.5">Compte concerné : <b>{m.deceased_email || "—"}</b> · Lien : {m.relationship || "—"}</p>
                )}
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{m.message}</p>
                <p className="text-xs text-slate-400 mt-2">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-1.5">
                {m.email && (
                  <Button size="sm" className="rounded-full bg-findo-navy hover:bg-findo-navy/90"
                    onClick={() => { setReplyOpen(replyOpen === m.id ? null : m.id); setReplyText(""); }}
                    data-testid={`message-reply-open-${m.id}`}>Répondre</Button>
                )}
                {m.status === "new" && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => mark(m, "handled")} data-testid={`message-handle-${m.id}`}>Marquer traité</Button>
                )}
                {m.status === "handled" && (
                  <>
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => mark(m, "new")} data-testid={`message-reopen-${m.id}`}>Rouvrir</Button>
                    <Button size="sm" variant="outline" className="rounded-full border-slate-300 text-slate-600 hover:bg-slate-50" onClick={() => mark(m, "archived")} data-testid={`message-archive-${m.id}`}>
                      <Archive size={14} className="mr-1" /> Archiver
                    </Button>
                  </>
                )}
                {m.status === "archived" && (
                  <Button size="sm" variant="ghost" className="rounded-full" onClick={() => mark(m, "handled")} data-testid={`message-unarchive-${m.id}`}>Désarchiver</Button>
                )}
              </div>
            </div>
            {(() => {
              const history = m.replies && m.replies.length
                ? m.replies
                : (m.reply ? [{ text: m.reply, at: m.replied_at, by: m.replied_by, sent: m.reply_sent }] : []);
              if (!history.length) return null;
              return (
                <div className="mt-3 space-y-2" data-testid={`message-reply-history-${m.id}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Historique des réponses ({history.length})</p>
                  {history.map((rep, idx) => (
                    <div key={idx} className="rounded-xl bg-emerald-50 border border-emerald-200 p-3" data-testid={`message-reply-item-${m.id}-${idx}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-xs font-semibold text-emerald-700">
                          {rep.by || "superadmin"}
                          {rep.sent === false && <span className="ml-1.5 text-amber-600">(email non envoyé)</span>}
                        </p>
                        <p className="text-xs text-slate-400">{rep.at ? new Date(rep.at).toLocaleString("fr-FR") : ""}</p>
                      </div>
                      <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{rep.text}</p>
                    </div>
                  ))}
                </div>
              );
            })()}
            {replyOpen === m.id && (
              <div className="mt-3 border-t border-border pt-3" data-testid={`message-reply-box-${m.id}`}>
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="text-xs font-semibold text-slate-400">Modèles :</span>
                  {REPLY_TEMPLATES.map((tpl) => (
                    <button key={tpl.label} type="button"
                      onClick={() => setReplyText(tpl.text)}
                      className="text-xs font-semibold rounded-full border border-findo-navy/20 bg-findo-navy/5 text-findo-navy px-3 py-1 hover:bg-findo-navy/10 transition-colors"
                      data-testid={`message-template-${m.id}-${tpl.label}`}>
                      {tpl.label}
                    </button>
                  ))}
                </div>
                <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={4}
                  placeholder={`Répondre à ${m.name || m.email}…`} className="rounded-xl"
                  data-testid={`message-reply-input-${m.id}`} />
                <p className="text-[11px] text-slate-400 mt-1">Une signature (votre nom · Équipe findloop · support@findloop.io) est ajoutée automatiquement.</p>
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" onClick={() => sendReply(m)} disabled={replyBusy || !replyText.trim()}
                    className="rounded-full bg-findo-navy hover:bg-findo-navy/90" data-testid={`message-reply-send-${m.id}`}>
                    {replyBusy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Envoi…</span> : "Envoyer & archiver"}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setReplyOpen(null); setReplyText(""); }}>Annuler</Button>
                  <span className="text-xs text-slate-400">L'email part vers {m.email} et le message est archivé.</span>
                </div>
              </div>
            )}
          </div>
        ))}
        {data && data.messages.length === 0 && <p className="p-8 text-center text-slate-400 text-sm" data-testid="messages-empty">Aucun message.</p>}
      </div>
    </div>
  );
}

/* ─── B2B directory (Country › City › Category) ──────────────────── */
function DirectoryMap({ points }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);
  const geo = (points || []).filter((p) => p.lat != null && p.lng != null);

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: false }).setView([46.6, 2.4], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap", maxZoom: 18,
    }).addTo(map);
    mapObj.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapObj.current = null; };
  }, []);

  useEffect(() => {
    const map = mapObj.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const maxTotal = Math.max(1, ...geo.map((p) => p.count));
    const bounds = [];
    geo.forEach((p) => {
      const radius = 9 + Math.round((p.count / maxTotal) * 20);
      const m = L.circleMarker([p.lat, p.lng], {
        radius, color: "#4F46E5", weight: 2, fillColor: "#6366F1", fillOpacity: 0.5,
      }).addTo(layer);
      m.bindPopup(`<b>${p.city}</b> <span style="color:#94a3b8">${p.country}</span><br/>${p.count} client(s) B2B`);
      bounds.push([p.lat, p.lng]);
    });
    map.invalidateSize();
    if (bounds.length) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 7 });
    setTimeout(() => map.invalidateSize(), 150);
  }, [points]); // eslint-disable-line

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-5 mb-4" data-testid="directory-map-card">
      <p className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5 mb-2">
        <MapPin size={15} /> Couverture géographique — clients B2B par ville
      </p>
      <div ref={mapRef} className="w-full rounded-2xl overflow-hidden border border-border" style={{ height: 300, zIndex: 0 }} data-testid="directory-map" />
    </div>
  );
}

function PartnerDirectoryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (country !== "all") params.set("country", country);
      if (category !== "all") params.set("category", category);
      if (q.trim()) params.set("q", q.trim());
      const d = await api(`/admin/partner-directory?${params.toString()}`);
      setData(d);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [country, category, q]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  async function exportCsv() {
    const qs = new URLSearchParams();
    if (country !== "all") qs.set("country", country);
    if (category !== "all") qs.set("category", category);
    if (q.trim()) qs.set("q", q.trim());
    try {
      const res = await fetch(`${API}/admin/partner-directory/export.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findloop-annuaire-b2b.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible"); }
  }

  return (
    <div data-testid="partner-directory-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un établissement, une ville…"
            className="h-10 rounded-full pl-9" data-testid="directory-search" />
        </div>
        <select value={country} onChange={(e) => setCountry(e.target.value)}
          className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="directory-country-filter">
          <option value="all">Tous les pays</option>
          {(data?.all_countries || []).map((c) => <option key={c} value={c}>{countryFlag(c)} {c}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="directory-category-filter">
          <option value="all">Toutes les catégories</option>
          {PARTNER_CATEGORY_KEYS.map((k) => <option key={k} value={k}>{PARTNER_CATEGORY_LABELS_FR[k]}</option>)}
          <option value="uncategorized">{PARTNER_CATEGORY_LABELS_FR.uncategorized}</option>
        </select>
        <Button onClick={exportCsv} variant="outline" className="rounded-full font-semibold" data-testid="directory-export-btn">
          <Download size={16} className="mr-1.5" /> Exporter CSV
        </Button>
      </div>

      {data && data.map_points && data.map_points.length > 0 && (
        <DirectoryMap points={data.map_points} />
      )}

      {data && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="directory-summary">
          <Badge className="bg-findo-indigo/10 text-findo-indigo">{data.total} client(s) B2B</Badge>
          {(data.category_summary || []).map((c) => (
            <Badge key={c.category} variant="outline" className="border-border text-slate-600">
              {c.label} · {c.count}
            </Badge>
          ))}
        </div>
      )}

      {loading && <PageLoader label="Chargement…" className="p-8" size={32} />}
      {!loading && data && data.countries.length === 0 && (
        <p className="p-8 text-center text-slate-400 text-sm" data-testid="directory-empty">Aucun client B2B ne correspond.</p>
      )}

      <div className="space-y-3">
        {!loading && (data?.countries || []).map((c) => (
          <div key={c.country} className="bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid={`directory-country-${c.country}`}>
            <button onClick={() => toggle(c.country)}
              className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left"
              data-testid={`directory-country-toggle-${c.country}`}>
              <span className="text-xl">{countryFlag(c.country)}</span>
              <span className="font-display font-bold text-slate-900">{c.country}</span>
              <Badge className="bg-slate-100 text-slate-600">{c.count}</Badge>
              <span className="ml-auto text-slate-400 text-sm">{expanded[c.country] === false ? "▸" : "▾"}</span>
            </button>
            {expanded[c.country] !== false && (
              <div className="px-4 pb-4 space-y-3">
                {c.cities.map((city) => (
                  <div key={city.city} className="rounded-xl border border-border/60" data-testid={`directory-city-${c.country}-${city.city}`}>
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-t-xl">
                      <MapPin size={14} className="text-findo-indigo" />
                      <span className="font-semibold text-slate-800 text-sm">{city.city}</span>
                      <Badge variant="outline" className="border-border text-slate-500 text-[11px]">{city.count}</Badge>
                    </div>
                    <div className="p-3 space-y-3">
                      {city.categories.map((cat) => (
                        <div key={cat.category} data-testid={`directory-cat-${cat.category}`}>
                          <p className="text-xs font-bold uppercase tracking-wide text-findo-indigo mb-1.5 inline-flex items-center gap-1.5">
                            <CategoryIcon category={cat.category} size={14} /> {cat.label} · {cat.count}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {cat.partners.map((p) => (
                              <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm"
                                data-testid={`directory-partner-${p.id}`}>
                                <CategoryIcon category={p.category} size={14} className="text-slate-400" />
                                <span className="font-medium text-slate-800">{p.org_name}</span>
                                <span className="text-slate-400 text-xs">{p.email}</span>
                                <Badge className="bg-findo-indigo/10 text-findo-indigo capitalize text-[10px]">{p.tier}</Badge>
                                {p.subscription_status === "suspended" && (
                                  <Badge className="bg-rose-100 text-rose-700 text-[10px]">suspendu</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ─── B2C users ──────────────────────────────────────────────────── */
function B2CPanel({ users, reload }) {
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [impTarget, setImpTarget] = useState(null);

  const filtered = users.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return `${u.name} ${u.email}`.toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un utilisateur…" className="pl-9 h-10 rounded-full" data-testid="b2c-search" />
        </div>
        <Button onClick={() => setFormOpen(true)} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="b2c-add-btn">
          <UserPlus size={16} className="mr-1.5" /> Ajouter un utilisateur
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden" data-testid="b2c-list">
        {filtered.map((u) => {
          const active = u.status !== "suspended";
          return (
            <div key={u.id} className="flex items-center gap-3 p-4" data-testid="b2c-row">
              <span className="text-xl">{countryFlag(u.country)}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{u.name || u.email}</p>
                <p className="text-xs text-slate-400 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
              </div>
              <Badge className={active ? "bg-teal-100 text-teal-700" : "bg-red-100 text-red-700"}>
                {active ? "Actif" : "Suspendu"}
              </Badge>
              <RowActions
                active={active}
                onToggle={() => toggle(u, active, reload)}
                onReset={() => resetPw(`/admin/b2c-users/${u.id}/reset-password`, reload)}
                onDelete={() => del(`/admin/b2c-users/${u.id}`, `l'utilisateur ${u.email}`, reload)}
                onImpersonate={() => setImpTarget({ type: "b2c", id: u.id, name: u.name || u.email })}
                testidPrefix="b2c"
              />
            </div>
          );
        })}
        {filtered.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">Aucun utilisateur B2C.</p>}
      </div>

      <B2CFormDialog open={formOpen} onClose={() => setFormOpen(false)} reload={reload} />
      <ImpersonateDialog target={impTarget} onClose={() => setImpTarget(null)} />
    </div>
  );

  async function toggle(u, active, reload) {
    try {
      await api(`/admin/b2c-users/${u.id}/toggle?active=${!active}`, { method: "PATCH" });
      toast.success(!active ? "Utilisateur activé" : "Utilisateur suspendu");
      reload();
    } catch (e) { toast.error(e.message); }
  }
}

function B2CFormDialog({ open, onClose, reload }) {
  const [form, setForm] = useState({ email: "", name: "", phone: "", country: "FR", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm({ email: "", name: "", phone: "", country: "FR", password: "" }); }, [open]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("/admin/b2c-users", { method: "POST", body: { email: form.email.trim().toLowerCase(), name: form.name, phone: form.phone, country: form.country, password: form.password || undefined } });
      toast.success(`Utilisateur créé${r.initial_password ? ` · mot de passe : ${r.initial_password}` : ""}`, { duration: 10000 });
      onClose();
      reload();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="b2c-form-dialog">
        <DialogHeader><DialogTitle className="font-display">Ajouter un utilisateur B2C</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="findo-numbered space-y-5">
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-form-email" /></div>
          <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-form-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-form-phone" /></div>
            <div><Label>Pays</Label>
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm" data-testid="b2c-form-country">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{`${c.flag} ${c.name}`}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Mot de passe (optionnel — généré si vide)</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-form-password" /></div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="w-full rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="b2c-form-submit">{busy ? <FindoSpinner size={16} /> : "Créer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Shared row actions ─────────────────────────────────────────── */

function RowActions({ active, onToggle, onEdit, onReset, onDelete, onImpersonate, onReactivate, testidPrefix }) {
  const [confirmDel, setConfirmDel] = useState(false);
  const iconBtn = "h-9 w-9 grid place-items-center rounded-lg border border-border text-slate-500 hover:text-findo-indigo hover:border-findo-indigo transition-colors";
  return (
    <div className="flex items-center gap-1.5">
      {onReactivate && <button type="button" onClick={onReactivate} title="Réactiver le partenaire (après régularisation)" className={`${iconBtn} text-emerald-600 border-emerald-300`} data-testid={`${testidPrefix}-reactivate-btn`}><RotateCcw size={16} /></button>}
      {onImpersonate && <button type="button" onClick={onImpersonate} title="Impersonation (avec consentement)" className={iconBtn} data-testid={`${testidPrefix}-impersonate-btn`}><UserCog size={16} /></button>}
      <button type="button" onClick={onToggle} title={active ? "Suspendre" : "Activer"} className={`${iconBtn} ${active ? "" : "text-teal-600 border-teal-300"}`} data-testid={`${testidPrefix}-toggle-btn`}><Power size={16} /></button>
      {onEdit && <button type="button" onClick={onEdit} title="Modifier" className={iconBtn} data-testid={`${testidPrefix}-edit-btn`}><Pencil size={16} /></button>}
      <button type="button" onClick={onReset} title="Réinitialiser le mot de passe" className={iconBtn} data-testid={`${testidPrefix}-reset-btn`}><KeyRound size={16} /></button>
      <button type="button" onClick={() => setConfirmDel(true)} title="Supprimer" className={`${iconBtn} hover:text-red-600 hover:border-red-300`} data-testid={`${testidPrefix}-delete-btn`}><Trash2 size={16} /></button>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid={`${testidPrefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700" data-testid={`${testidPrefix}-delete-confirm`}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

async function resetPw(path, reload) {
  try {
    const r = await api(path, { method: "POST" });
    // Persistent, copyable reveal (not a fleeting toast) so the superadmin can
    // reliably communicate the exact password to the partner.
    toast.success("Mot de passe réinitialisé", {
      description: `Nouveau mot de passe : ${r.temporary_password} — un email vient d'être envoyé au partenaire.`,
      duration: 300000,
      action: {
        label: "Copier",
        onClick: () => {
          try { navigator.clipboard.writeText(r.temporary_password); toast.success("Mot de passe copié"); } catch { /* ignore */ }
        },
      },
    });
    reload && reload();
  } catch (e) { toast.error(e.message); }
}

async function del(path, label, reload) {
  const reason = window.prompt(`Motif de la suppression de ${label} (envoyé à l'utilisateur par e-mail, facultatif) :`, "");
  if (reason === null) return; // cancelled
  try {
    const qs = reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : "";
    await api(`${path}${qs}`, { method: "DELETE" });
    toast.success(`Suppression de ${label} effectuée (e-mail envoyé)`);
    reload();
  } catch (e) { toast.error(e.message); }
}

/* ─── Public ads moderation ──────────────────────────────────────── */

function trashInfo(deletedAt) {
  const del = new Date(deletedAt);
  const purge = new Date(del.getTime() + 30 * 24 * 3600 * 1000);
  const daysLeft = Math.max(0, Math.ceil((purge - Date.now()) / (24 * 3600 * 1000)));
  return `Supprimée le ${del.toLocaleDateString("fr-FR")} · purge dans ${daysLeft} j`;
}

function ModerationHeader() {
  const [settings, setSettings] = useState(null);
  const [threshold, setThreshold] = useState(3);
  const [cats, setCats] = useState([]); // [{category, value}]
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/admin/moderation-settings").then((r) => {
      setSettings(r); setThreshold(r.auto_hide_threshold);
      setCats(Object.entries(r.category_thresholds || {}).map(([category, value]) => ({ category, value })));
    }).catch(() => {});
    api("/admin/moderation-stats?weeks=8").then(setStats).catch(() => {});
  }, []);

  async function save() {
    setSaving(true);
    const category_thresholds = {};
    cats.forEach((c) => { if (c.category.trim()) category_thresholds[c.category.trim()] = Number(c.value) || 1; });
    try {
      const r = await api("/admin/moderation-settings", {
        method: "PATCH",
        body: { auto_hide_threshold: Number(threshold), category_thresholds },
      });
      setSettings(r); setThreshold(r.auto_hide_threshold);
      setCats(Object.entries(r.category_thresholds || {}).map(([category, value]) => ({ category, value })));
      toast.success("Réglages enregistrés");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  }

  const chartData = (stats?.weeks || []).map((w) => ({
    name: new Date(w.week).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
    Masquées: w.hidden, Signalements: w.reported,
  }));

  return (
    <div className="grid md:grid-cols-3 gap-4 mb-5" data-testid="moderation-header">
      <div className="bg-white rounded-2xl border border-border card-shadow p-5">
        <p className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5"><AlertTriangle size={15} /> Seuil de masquage auto</p>
        <p className="text-xs text-slate-400 mt-1">Signalements avant masquage automatique.</p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-slate-500 w-16">Par défaut</span>
          <Input type="number" min={1} max={50} value={threshold} onChange={(e) => setThreshold(e.target.value)} className="h-9 w-20 rounded-xl" data-testid="threshold-input" />
        </div>
        <div className="mt-2 space-y-2" data-testid="category-thresholds">
          {cats.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={c.category} onChange={(e) => setCats(cats.map((x, j) => j === i ? { ...x, category: e.target.value } : x))} placeholder="Catégorie" className="h-9 flex-1 rounded-xl text-sm" data-testid={`cat-threshold-name-${i}`} />
              <Input type="number" min={1} max={50} value={c.value} onChange={(e) => setCats(cats.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} className="h-9 w-16 rounded-xl" data-testid={`cat-threshold-value-${i}`} />
              <button onClick={() => setCats(cats.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`cat-threshold-remove-${i}`}><X size={16} /></button>
            </div>
          ))}
          <button onClick={() => setCats([...cats, { category: "", value: threshold }])} className="text-xs text-findo-indigo font-semibold hover:underline" data-testid="cat-threshold-add">+ Seuil par catégorie</button>
        </div>
        <Button onClick={save} disabled={saving} className="mt-3 h-9 w-full rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="threshold-save">
          {saving ? <FindoSpinner size={16} /> : "Enregistrer"}
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-border card-shadow p-5 md:col-span-2" data-testid="moderation-stats">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-findo-navy">Modération — 8 dernières semaines</p>
          <p className="text-xs text-slate-400">{stats?.total_hidden ?? 0} masquées · {stats?.total_reported ?? 0} signalements</p>
        </div>
        <div style={{ width: "100%", height: 160 }} className="mt-2">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} width={28} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Signalements" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={20} />
              <Bar dataKey="Masquées" fill="#e11d48" radius={[4, 4, 0, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const REPORT_REASON_LABELS = {
  inappropriate: "Contenu inapproprié", duplicate: "Doublon",
  wrong_category: "Erreur de catégorie", other: "Autre",
  spam: "Spam", offensive: "Contenu offensant", fraud: "Fraude",
};

function ReportsPanel() {
  const [reports, setReports] = useState(null);

  function load() {
    api("/admin/public-ads/reports").then((r) => setReports(r.reports || [])).catch(() => setReports([]));
  }
  useEffect(() => { load(); }, []);

  async function hide(adId, reasons) {
    try {
      const label = (reasons || []).map((x) => REPORT_REASON_LABELS[x] || x).join(", ");
      const reason = `Masquée par la modération suite à des signalements${label ? " (" + label + ")" : ""}.`;
      await api(`/admin/public-ads/${adId}/status?status=hidden&reason=${encodeURIComponent(reason)}`, { method: "PATCH" });
      toast.success("Annonce masquée");
      setReports((rs) => (rs || []).map((r) => r.ad_id === adId ? { ...r, ad_status: "hidden" } : r));
    } catch (e) { toast.error(e.message); }
  }
  async function unhide(adId) {
    try {
      await api(`/admin/public-ads/${adId}/status?status=open`, { method: "PATCH" });
      toast.success("Annonce remise en ligne");
      setReports((rs) => (rs || []).map((r) => r.ad_id === adId ? { ...r, ad_status: "open" } : r));
    } catch (e) { toast.error(e.message); }
  }
  async function remove(adId) {
    try {
      await api(`/admin/public-ads/${adId}?permanent=true`, { method: "DELETE" });
      toast.success("Annonce supprimée");
      setReports((rs) => (rs || []).filter((r) => r.ad_id !== adId));
    } catch (e) { toast.error(e.message); }
  }

  if (reports === null) return <div className="h-40 rounded-2xl bg-slate-100 animate-pulse" />;
  if (reports.length === 0) return <p className="text-slate-500 py-12 text-center" data-testid="reports-empty">Aucune annonce signalée.</p>;

  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-white" data-testid="reports-panel">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr>
            <th className="px-4 py-3 font-semibold">Annonce</th>
            <th className="px-4 py-3 font-semibold">Motif(s)</th>
            <th className="px-4 py-3 font-semibold text-center">Signalements</th>
            <th className="px-4 py-3 font-semibold">Dernier</th>
            <th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {reports.map((r) => (
            <tr key={r.ad_id} data-testid={`report-row-${r.ad_id}`} className={r.ad_status === "hidden" ? "opacity-60" : ""}>
              <td className="px-4 py-3">
                <p className="font-semibold text-findo-navy">{r.title}</p>
                <p className="text-xs text-slate-400">{r.category} · {r.ad_type === "found" ? "Objet trouvé" : "Objet perdu"}{r.ad_status === "hidden" ? " · masquée" : ""}</p>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {(r.reasons || []).map((x) => <span key={x} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{REPORT_REASON_LABELS[x] || x}</span>)}
                </div>
                {(r.comments || []).map((c, i) => (
                  <p key={i} className="mt-1.5 text-xs text-slate-600 italic border-l-2 border-findo-red/40 pl-2 max-w-xs break-words" data-testid={`report-comment-${r.ad_id}-${i}`}>« {c} »</p>
                ))}
              </td>
              <td className="px-4 py-3 text-center"><span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-findo-red/10 text-findo-red font-bold text-xs">{r.report_count}</span></td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.last_reported_at ? new Date(r.last_reported_at).toLocaleDateString() : ""}</td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  {r.ad_status === "hidden" ? (
                    <button onClick={() => unhide(r.ad_id)} data-testid={`report-unhide-${r.ad_id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-emerald-200 text-emerald-700 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-50">
                      <Eye size={13} /> Démasquer
                    </button>
                  ) : (
                    <button onClick={() => hide(r.ad_id, r.reasons)} data-testid={`report-hide-${r.ad_id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">
                      <EyeOff size={13} /> Masquer
                    </button>
                  )}
                  <button onClick={() => remove(r.ad_id)} data-testid={`report-delete-${r.ad_id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-findo-red/10 text-findo-red px-3 py-1.5 text-xs font-semibold hover:bg-findo-red/20">
                    <Trash2 size={13} /> Supprimer
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AdsPanel({ ads, counts, reload }) {
  const [searchParams] = useSearchParams();
  const setSearchParams = useSearchParams()[1];
  const focus = searchParams.get("focus");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reportedOnly, setReportedOnly] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [hideTarget, setHideTarget] = useState(null);
  const [hideReason, setHideReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [reportsView, setReportsView] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);

  async function seedDemoAds() {
    setSeedBusy(true);
    try {
      const r = await api(`/admin/public-ads/seed`, { method: "POST" });
      toast.success(`Démo chargée : ${r.inserted} annonces (${r.boosted} boostées) sur ${r.countries} pays`);
      reload();
    } catch (e) { toast.error(e.message); } finally { setSeedBusy(false); }
  }

  const [migBusy, setMigBusy] = useState(false);
  async function migratePhotosReviews() {
    setMigBusy(true);
    try {
      const r = await api(`/admin/demo/migrate-photos-reviews`, { method: "POST" });
      toast.success(`Annonces seed : ${r.ads_created} créées (${r.ads_deleted} supprimées) · Avis : ${r.reviews_created} créés · ${r.reviews_non_seed_deleted} anciens avis supprimés · ${r.reviews_remaining} avis en base`);
      reload();
    } catch (e) { toast.error(e.message); } finally { setMigBusy(false); }
  }

  const filtered = ads.filter((a) => {
    if (reportedOnly && !(a.report_count > 0 || a.auto_hidden)) return false;
    if (statusFilter !== "all" && (a.status || "open") !== statusFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return `${a.description} ${a.category} ${a.lost_place} ${a.owner_email || ""}`.toLowerCase().includes(s);
  });

  async function setStatus(ad, status, reason) {
    try {
      const qs = reason ? `?status=${status}&reason=${encodeURIComponent(reason)}` : `?status=${status}`;
      await api(`/admin/public-ads/${ad.id}/status${qs}`, { method: "PATCH" });
      toast.success(status === "hidden" ? "Annonce masquée" : "Statut mis à jour");
      reload();
    } catch (e) { toast.error(e.message); }
  }
  async function restore(ad) {
    try { await api(`/admin/public-ads/${ad.id}/restore`, { method: "POST" }); toast.success("Annonce restaurée"); reload(); }
    catch (e) { toast.error(e.message); }
  }
  async function del(ad, permanent) {
    try {
      await api(`/admin/public-ads/${ad.id}${permanent ? "?permanent=true" : ""}`, { method: "DELETE" });
      toast.success(permanent ? "Annonce supprimée définitivement" : "Annonce déplacée dans la corbeille");
      setConfirmDel(null);
      reload();
    } catch (e) { toast.error(e.message); }
  }
  function confirmHide() {
    setStatus(hideTarget, "hidden", hideReason.trim() || undefined);
    setHideTarget(null); setHideReason("");
  }
  async function approvePoints(ad) {
    try {
      const r = await api(`/admin/public-ads/${ad.id}/approve-points`, { method: "POST" });
      toast.success(r.awarded ? "Points Héros Trouveur crédités" : "Points déjà crédités");
      setReportsView(null); reload();
    } catch (e) { toast.error(e.message); }
  }
  async function suspendAuthor(ad) {
    try {
      await api(`/admin/public-ads/${ad.id}/suspend-author`, { method: "POST" });
      toast.success("Compte de l'auteur suspendu");
      setSuspendTarget(null); setReportsView(null); reload();
    } catch (e) { toast.error(e.message); }
  }
  useEffect(() => {
    if (focus) {
      const el = document.getElementById(`ad-${focus}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => setSearchParams({ tab: "ads" }), 4000);
      return () => clearTimeout(t);
    }
  }, [focus, ads]);

  const statusBadge = (s) => ({    open: "bg-teal-100 text-teal-700",
    hidden: "bg-amber-100 text-amber-700",
    resolved: "bg-slate-100 text-slate-600",
    closed: "bg-slate-100 text-slate-600",
    trashed: "bg-red-100 text-red-700",
  }[s] || "bg-slate-100 text-slate-600");

  const iconBtn = "h-9 w-9 grid place-items-center rounded-lg border border-border text-slate-500 hover:text-findo-indigo hover:border-findo-indigo transition-colors";

  return (
    <div>
      <ModerationHeader />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une annonce…" className="pl-9 h-10 rounded-full" data-testid="ads-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="ads-status-filter">
          <option value="all">Tous les statuts</option>
          <option value="open">{`Ouvertes (${counts.open || 0})`}</option>
          <option value="hidden">{`Masquées (${counts.hidden || 0})`}</option>
          <option value="resolved">{`Résolues (${counts.resolved || 0})`}</option>
          <option value="trashed">{`Corbeille (${counts.trashed || 0})`}</option>
        </select>
        <button
          onClick={() => setReportedOnly((v) => !v)}
          className={`h-10 rounded-full border px-4 text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${reportedOnly ? "bg-red-600 border-red-600 text-white" : "border-border text-slate-600 hover:border-red-300"}`}
          data-testid="ads-reported-filter">
          <Flag size={14} /> Signalées
        </button>
        <button
          onClick={seedDemoAds}
          disabled={seedBusy}
          className="h-10 rounded-full border border-findo-indigo/30 px-4 text-sm font-semibold inline-flex items-center gap-1.5 text-findo-indigo hover:bg-findo-indigo/5 transition-colors disabled:opacity-50"
          data-testid="ads-seed-btn"
          title="Insère les 200 annonces de démonstration (10 pays) dans cette base">
          <Sparkles size={14} /> {seedBusy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Chargement…</span> : "Charger la démo"}
        </button>
        <button
          onClick={migratePhotosReviews}
          disabled={migBusy}
          className="h-10 rounded-full border border-findo-indigo/30 px-4 text-sm font-semibold inline-flex items-center gap-1.5 text-findo-indigo hover:bg-findo-indigo/5 transition-colors disabled:opacity-50"
          data-testid="ads-migrate-photos-btn"
          title="Met à jour les photos des annonces seed (France) et recrée les 4 avis clients (photos + avatars)">
          <Sparkles size={14} /> {migBusy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Mise à jour…</span> : "MàJ photos & avis"}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden" data-testid="ads-list">
        {filtered.map((a) => {
          const st = a.status || "open";
          const trashed = st === "trashed";
          const hidden = st === "hidden";
          return (
            <div key={a.id} id={`ad-${a.id}`} className={`flex items-center gap-3 p-4 transition-colors ${a.id === focus ? "ring-2 ring-inset ring-findo-red bg-findo-red/5" : ""}`} data-testid="ads-row">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{a.description}</p>
                <p className="text-xs text-slate-400 truncate">{a.category} · {a.lost_place || "—"}{a.owner_email ? ` · ${a.owner_email}` : ""}</p>
              </div>
              {a.report_count > 0 && (
                <button onClick={() => setReportsView(a)} className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2.5 py-1 text-xs font-bold hover:bg-red-200 transition-colors" data-testid="ad-reports-badge">
                  <Flag size={12} /> {a.report_count}
                </button>
              )}
              <Badge className={statusBadge(st)}>{st}</Badge>
              {trashed && a.deleted_at && (
                <span className="text-xs text-slate-400 whitespace-nowrap" data-testid="ad-trash-countdown">
                  {trashInfo(a.deleted_at)}
                </span>
              )}
              <div className="flex items-center gap-1.5">
                <button onClick={() => setPreview(a)} title="Aperçu carte de partage" className={iconBtn} data-testid="ad-preview-btn"><ImageIcon size={16} /></button>
                {trashed ? (
                  <>
                    <button onClick={() => restore(a)} title="Restaurer" className={`${iconBtn} text-teal-600 border-teal-300`} data-testid="ad-restore-btn"><RotateCcw size={16} /></button>
                    <button onClick={() => setConfirmDel({ ad: a, permanent: true })} title="Supprimer définitivement" className={`${iconBtn} hover:text-red-600 hover:border-red-300`} data-testid="ad-perma-delete-btn"><Trash2 size={16} /></button>
                  </>
                ) : (
                  <>
                    {hidden ? (
                      <button onClick={() => setStatus(a, "open")} title="Rétablir" className={`${iconBtn} text-teal-600 border-teal-300`} data-testid="ad-unhide-btn"><Eye size={16} /></button>
                    ) : (
                      <button onClick={() => { setHideTarget(a); setHideReason(""); }} title="Masquer" className={iconBtn} data-testid="ad-hide-btn"><EyeOff size={16} /></button>
                    )}
                    <button onClick={() => setConfirmDel({ ad: a, permanent: false })} title="Mettre à la corbeille" className={`${iconBtn} hover:text-red-600 hover:border-red-300`} data-testid="ad-delete-btn"><Trash2 size={16} /></button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">Aucune annonce.</p>}
      </div>

      {/* Hide-with-reason dialog */}
      <Dialog open={!!hideTarget} onOpenChange={(o) => !o && setHideTarget(null)}>
        <DialogContent data-testid="ad-hide-dialog">
          <DialogHeader><DialogTitle className="font-display">Masquer l'annonce</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Le motif ci-dessous sera envoyé au propriétaire par email.</p>
            <Label>Motif du masquage</Label>
            <Textarea value={hideReason} onChange={(e) => setHideReason(e.target.value)} placeholder="Ex : contenu inapproprié, annonce en double…" className="rounded-xl" data-testid="ad-hide-reason" />
          </div>
          <DialogFooter>
            <Button onClick={confirmHide} className="w-full rounded-xl font-semibold bg-amber-600 hover:bg-amber-700" data-testid="ad-hide-confirm">Masquer et notifier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share-card preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="ad-preview-dialog">
          <DialogHeader><DialogTitle className="font-display">Aperçu de la carte de partage</DialogTitle></DialogHeader>
          {preview && (
            <img src={`${API}/public-ads/${preview.id}/share-image.png?t=${Date.now()}`} alt="preview" className="w-full rounded-xl border border-border" data-testid="ad-preview-img" />
          )}
        </DialogContent>
      </Dialog>

      {/* Reports viewer */}
      <Dialog open={!!reportsView} onOpenChange={(o) => !o && setReportsView(null)}>
        <DialogContent data-testid="ad-reports-dialog">
          <DialogHeader><DialogTitle className="font-display">Signalements ({reportsView?.report_count || 0})</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-80 overflow-auto">
            {(reportsView?.reports || []).map((r) => (
              <div key={r.id} className="rounded-xl border border-border p-3">
                <p className="text-sm font-semibold text-findo-navy capitalize">{r.reason}</p>
                {r.comment && <p className="text-sm text-slate-500 mt-1">{r.comment}</p>}
                <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleString("fr-FR")}</p>
              </div>
            ))}
            {(!reportsView?.reports || reportsView.reports.length === 0) && <p className="text-sm text-slate-400">Aucun détail.</p>}
          </div>
          {reportsView && (
            <DialogFooter className="flex-col sm:flex-row gap-2 border-t border-border pt-4">
              {reportsView.ad_type === "found" && reportsView.points_state === "pending" && (
                <Button onClick={() => approvePoints(reportsView)} className="rounded-xl font-semibold bg-teal-600 hover:bg-teal-700" data-testid="ad-approve-points-btn">
                  <Trophy size={15} className="mr-1.5" /> Valider les points Héros Trouveur
                </Button>
              )}
              <Button variant="outline" onClick={() => setSuspendTarget(reportsView)} disabled={!reportsView.owner_user_id && !reportsView.owner_email} className="rounded-xl font-semibold border-red-300 text-red-600 hover:bg-red-50" data-testid="ad-suspend-author-btn">
                <Ban size={15} className="mr-1.5" /> Suspendre le compte de l'auteur
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <AlertDialogContent data-testid="ad-suspend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Suspendre le compte de l'auteur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le compte trouveur {suspendTarget?.owner_email ? `(${suspendTarget.owner_email})` : ""} sera suspendu et ne pourra plus se connecter ni publier. Cette action est réversible depuis la gestion des comptes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => suspendAuthor(suspendTarget)} className="bg-red-600 hover:bg-red-700" data-testid="ad-suspend-confirm">
              Suspendre le compte
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="ad-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDel?.permanent ? "Supprimer définitivement ?" : "Mettre à la corbeille ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel?.permanent
                ? "Cette action est irréversible."
                : "L'annonce sera conservée 30 jours dans la corbeille puis supprimée automatiquement. Vous pourrez la restaurer entre-temps."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => del(confirmDel.ad, confirmDel.permanent)} className="bg-red-600 hover:bg-red-700" data-testid="ad-delete-confirm">
              {confirmDel?.permanent ? "Supprimer" : "Mettre à la corbeille"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Technical error log ────────────────────────────────────────── */

function ErrorLogsPanel() {
  const [data, setData] = useState({ items: [], groups: [], total: 0, grouped: false });
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  const [grouped, setGrouped] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: "100" });
    if (q) p.set("q", q);
    if (grouped) p.set("grouped", "true");
    api(`/admin/error-logs?${p.toString()}`).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [q, grouped]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  async function clearAll() {
    if (!window.confirm("Vider tout le journal d'erreurs techniques ?")) return;
    try { await api("/admin/error-logs", { method: "DELETE" }); toast.success("Journal d'erreurs vidé"); load(); }
    catch (e) { toast.error(e.message); }
  }

  const rows = grouped ? data.groups || [] : data.items || [];

  return (
    <div data-testid="error-logs-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg font-semibold text-findo-navy">Erreurs techniques ({data.total})</h3>
          <p className="text-sm text-slate-500">Erreurs serveur (500) capturées automatiquement — les plus récentes en premier.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer par route / message…" className="h-9 w-56 rounded-full" data-testid="error-logs-search" />
          <Button variant={grouped ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setGrouped((g) => !g)} data-testid="error-logs-group-toggle">Regrouper</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="error-logs-refresh">Rafraîchir</Button>
          <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={clearAll} disabled={data.total === 0} data-testid="error-logs-clear">Vider</Button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden">
        {rows.map((e) => (
          <div key={e.id + (e.signature || "")} className="p-4 text-sm" data-testid={`error-log-${e.id}`}>
            <button onClick={() => setOpenId(openId === (e.id) ? null : e.id)} className="w-full text-left flex items-center gap-3">
              <Badge className="bg-slate-100 text-slate-600 shrink-0">{e.method}</Badge>
              <code className="text-findo-indigo font-semibold shrink-0">{e.path}</code>
              <span className="flex-1 text-red-600 truncate">{e.error}</span>
              {grouped && <Badge className="bg-red-100 text-red-700 shrink-0" data-testid={`error-count-${e.id}`}>×{e.count}</Badge>}
              <span className="text-xs text-slate-400 shrink-0">{((grouped ? e.last_at : e.at) || "").slice(0, 19).replace("T", " ")}</span>
            </button>
            {openId === e.id && (
              <pre className="mt-3 p-3 bg-slate-900 text-slate-100 rounded-xl text-xs overflow-x-auto whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid={`error-trace-${e.id}`}>{e.trace}</pre>
            )}
          </div>
        ))}
        {!loading && rows.length === 0 && <p className="p-8 text-center text-slate-400 text-sm" data-testid="error-logs-empty">Aucune erreur enregistrée. 🎉</p>}
        {loading && <PageLoader label="Chargement…" className="p-8" size={32} />}
      </div>
    </div>
  );
}

/* ─── Audit log (Journal) ────────────────────────────────────────── */

const ACTION_LABELS = {
  "partner.create": "Partenaire créé",
  "partner.update": "Partenaire modifié",
  "partner.delete": "Partenaire supprimé",
  "partner.toggle": "Partenaire (statut)",
  "partner.reset_password": "Mot de passe partenaire réinitialisé",
  "b2c.create": "Utilisateur B2C créé",
  "b2c.delete": "Utilisateur B2C supprimé",
  "b2c.toggle": "Utilisateur B2C (statut)",
  "b2c.reset_password": "Mot de passe B2C réinitialisé",
  "impersonate": "Impersonation partenaire",
  "admin.invite": "Admin invité",
  "admin.create": "Admin créé",
  "admin.update": "Admin modifié",
  "admin.resend_invite": "Invitation renvoyée",
  "admin.revoke_invite": "Invitation révoquée",
  "admin.delete": "Admin supprimé",
  "admin.auto_suspend": "Admin suspendu (auto)",
  "admin.invite_auto_renew": "Invitation relancée (auto)",
  "admin.reactivate": "Admin réactivé",
};

function actionColor(action) {
  if (action.endsWith(".delete")) return "bg-red-100 text-red-700";
  if (action.endsWith(".create")) return "bg-teal-100 text-teal-700";
  if (action.endsWith(".toggle")) return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

function AuditPanel({ refreshKey }) {
  const PAGE = 25;
  const [action, setAction] = useState("all");
  const [actor, setActor] = useState("all");
  const [data, setData] = useState({ logs: [], total: 0, actions: [], actors: [] });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = (off, append) => {
    setLoading(true);
    const qs = new URLSearchParams({ limit: PAGE, offset: off });
    if (action !== "all") qs.set("action", action);
    if (actor !== "all") qs.set("actor", actor);
    api(`/admin/audit-logs?${qs.toString()}`)
      .then((r) => {
        setData((prev) => ({
          ...r,
          logs: append ? [...prev.logs, ...(r.logs || [])] : (r.logs || []),
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { setOffset(0); load(0, false); /* eslint-disable-next-line */ }, [action, actor, refreshKey]);

  const hasMore = data.logs.length < data.total;

  async function exportCsv() {
    const qs = new URLSearchParams();
    if (action !== "all") qs.set("action", action);
    if (actor !== "all") qs.set("actor", actor);
    try {
      const res = await fetch(`${API}/admin/audit-logs/export.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "findloop-journal.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("audit csv export failed", e);
      toast.error("Export impossible");
    }
  }

  return (
    <div data-testid="audit-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={action} onChange={(e) => setAction(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="audit-filter-action">
          <option value="all">Toutes les actions</option>
          <option value="admin:all">Actions admins (gouvernance)</option>
          {(data.actions || []).map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
        <Button
          variant={action === "admin:all" ? "default" : "outline"}
          onClick={() => setAction(action === "admin:all" ? "all" : "admin:all")}
          className={`h-10 rounded-full font-semibold ${action === "admin:all" ? "bg-findo-indigo hover:bg-findo-indigo/90" : ""}`}
          data-testid="audit-filter-admins">
          <ShieldCheck size={15} className="mr-1.5" /> Actions admins
        </Button>
        <select value={actor} onChange={(e) => setActor(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="audit-filter-actor">
          <option value="all">Tous les administrateurs</option>
          {(data.actors || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Button variant="outline" onClick={exportCsv} className="h-10 rounded-full font-semibold" data-testid="audit-export-btn">
          <Download size={15} className="mr-1.5" /> Exporter CSV
        </Button>
        <PurgeTestDataButton />
        <span className="text-xs text-slate-400 ml-auto" data-testid="audit-count">{data.total} action(s)</span>
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden" data-testid="audit-list">
        {data.logs.map((l) => (
          <div key={l.id} className="flex items-center gap-3 p-4 text-sm" data-testid="audit-row">
            <Badge className={actionColor(l.action)}>{ACTION_LABELS[l.action] || l.action}</Badge>
            <div className="flex-1 min-w-0">
              <p className="text-slate-700 truncate">{l.target_email || l.target_partner_email || l.target_id || "—"}{l.active !== undefined ? ` · ${l.active ? "activé" : "suspendu"}` : ""}</p>
              <p className="text-xs text-slate-400 truncate">par {l.actor_email || "—"}</p>
            </div>
            <span className="text-xs text-slate-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
          </div>
        ))}
        {data.logs.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">Aucune action enregistrée.</p>}
      </div>

      {hasMore && (
        <div className="mt-4 text-center">
          <Button variant="outline" disabled={loading} onClick={() => { const off = offset + PAGE; setOffset(off); load(off, true); }} className="rounded-full font-semibold" data-testid="audit-load-more">
            {loading ? <FindoSpinner size={16} /> : "Charger plus"}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─── RGPD register (exports / erasures) ──────────────────────────── */

const GDPR_ACTION_LABELS = {
  b2c_export: "Export données client",
  b2c_erase: "Suppression compte client",
  export: "Export données partenaire",
  erase: "Suppression compte partenaire",
};

function gdprActionColor(a) {
  if (a.includes("erase")) return "bg-red-100 text-red-700";
  if (a.includes("export")) return "bg-teal-100 text-teal-700";
  return "bg-slate-100 text-slate-600";
}

function GdprPanel() {
  const PAGE = 25;
  const [action, setAction] = useState("all");
  const [subject, setSubject] = useState("");
  const [days, setDays] = useState("all");
  const [data, setData] = useState({ logs: [], total: 0, actions: [] });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = (off, append) => {
    setLoading(true);
    const qs = new URLSearchParams({ limit: PAGE, offset: off });
    if (action !== "all") qs.set("action", action);
    if (subject.trim()) qs.set("subject", subject.trim());
    if (days !== "all") qs.set("days", days);
    api(`/admin/gdpr-logs?${qs.toString()}`)
      .then((r) => {
        setData((prev) => ({ ...r, logs: append ? [...prev.logs, ...(r.logs || [])] : (r.logs || []) }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const tmr = setTimeout(() => { setOffset(0); load(0, false); }, 250);
    return () => clearTimeout(tmr);
    /* eslint-disable-next-line */
  }, [action, subject, days]);

  const hasMore = data.logs.length < data.total;

  async function exportCsv() {
    const qs = new URLSearchParams();
    if (action !== "all") qs.set("action", action);
    if (subject.trim()) qs.set("subject", subject.trim());
    if (days !== "all") qs.set("days", days);
    try {
      const res = await fetch(`${API}/admin/gdpr-logs/export.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findloop-registre-rgpd.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error("gdpr csv export failed", e); }
  }

  return (
    <div data-testid="gdpr-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={action} onChange={(e) => setAction(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="gdpr-filter-action">
          <option value="all">Toutes les opérations</option>
          {(data.actions || []).map((a) => <option key={a} value={a}>{GDPR_ACTION_LABELS[a] || a}</option>)}
        </select>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Rechercher un email…" className="h-10 rounded-full border border-border px-4 text-sm bg-white w-60" data-testid="gdpr-filter-subject" />
        <select value={days} onChange={(e) => setDays(e.target.value)} className="h-10 rounded-full border border-border px-4 text-sm bg-white" data-testid="gdpr-filter-days">
          <option value="all">Toute la période</option>
          <option value="7">7 derniers jours</option>
          <option value="30">30 derniers jours</option>
          <option value="90">90 derniers jours</option>
        </select>
        <Button variant="outline" onClick={exportCsv} className="h-10 rounded-full font-semibold" data-testid="gdpr-export-btn">
          <Download size={15} className="mr-1.5" /> Exporter CSV
        </Button>
        <span className="text-xs text-slate-400 ml-auto" data-testid="gdpr-count">{data.total} opération(s)</span>
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden" data-testid="gdpr-list">
        {data.logs.map((l, i) => (
          <div key={i} className="flex items-center gap-3 p-4 text-sm" data-testid="gdpr-row">
            <Badge className={gdprActionColor(l.action)}>{GDPR_ACTION_LABELS[l.action] || l.action}</Badge>
            <div className="flex-1 min-w-0">
              <p className="text-slate-700 truncate">{l.subject_email || "—"}</p>
            </div>
            <span className="text-xs text-slate-400 whitespace-nowrap">{l.at ? new Date(l.at).toLocaleString("fr-FR") : "—"}</span>
          </div>
        ))}
        {data.logs.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">Aucune opération RGPD enregistrée.</p>}
      </div>

      {hasMore && (
        <div className="mt-4 text-center">
          <Button variant="outline" disabled={loading} onClick={() => { const off = offset + PAGE; setOffset(off); load(off, true); }} className="rounded-full font-semibold" data-testid="gdpr-load-more">
            {loading ? <FindoSpinner size={16} /> : "Charger plus"}
          </Button>
        </div>
      )}
    </div>
  );
}


const QUOTE_STATUS = {
  new: { label: "Nouveau", cls: "bg-findo-red/10 text-findo-red" },
  contacted: { label: "Contacté", cls: "bg-amber-100 text-amber-700" },
  closed: { label: "Clôturé", cls: "bg-emerald-100 text-emerald-700" },
};

function rateColor(rate) {
  // Continuous red (0%) → green (100%) — intensity reflects restitution rate.
  const h = Math.max(0, Math.min(100, rate)) * 1.4; // 0=red, 140=green
  return `hsl(${h}, 72%, 42%)`;
}

function TrafficMap({ cities }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);
  const geo = (cities || []).filter((c) => c.lat != null && c.lng != null);

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: false }).setView([46.6, 2.4], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap", maxZoom: 18,
    }).addTo(map);
    mapObj.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapObj.current = null; };
  }, []);

  useEffect(() => {
    const map = mapObj.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const maxTotal = Math.max(1, ...geo.map((c) => c.total));
    const bounds = [];
    geo.forEach((c) => {
      const radius = 10 + Math.round((c.total / maxTotal) * 22);
      const color = rateColor(c.rate);
      const m = L.circleMarker([c.lat, c.lng], {
        radius, color, weight: 2, fillColor: color, fillOpacity: 0.55,
      }).addTo(layer);
      m.bindPopup(
        `<b>${c.city}</b> <span style="color:#94a3b8">${c.country}</span><br/>` +
        `${c.total} déclaré(s) · ${c.restituted} restitué(s)<br/>` +
        `<b style="color:${color}">${c.rate}%</b> de restitution`
      );
      bounds.push([c.lat, c.lng]);
    });
    map.invalidateSize();
    if (bounds.length) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 7 });
    setTimeout(() => map.invalidateSize(), 150);
  }, [cities]); // eslint-disable-line

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-5 mb-5" data-testid="traffic-map-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5"><MapPin size={15} /> Carte de chaleur — taux de restitution</p>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: rateColor(15) }} /> Faible</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: rateColor(55) }} /> Moyen</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: rateColor(90) }} /> Élevé</span>
        </div>
      </div>
      <div className="relative">
        <div ref={mapRef} className="w-full rounded-2xl overflow-hidden border border-border" style={{ height: 320, zIndex: 0 }} data-testid="traffic-map" />
        {geo.length === 0 && (
          <div className="absolute inset-0 grid place-items-center bg-white/80 rounded-2xl" data-testid="traffic-map-empty">
            <p className="text-sm text-slate-400">Aucune ville géolocalisée sur cette période.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function BulkTargetDialog({ open, onClose, countries, defaultCountry, onApply }) {
  const [cc, setCc] = useState(defaultCountry || "");
  const [rate, setRate] = useState(70);
  useEffect(() => { setCc(defaultCountry || ""); }, [defaultCountry, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-target-dialog">
        <DialogHeader>
          <DialogTitle>Objectif de restitution par pays</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Pays</Label>
            <select value={cc} onChange={(e) => setCc(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-border px-3 text-sm bg-white" data-testid="bulk-target-country">
              {(countries || []).map((c) => <option key={c} value={c}>{countryFlag(c)} {c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Objectif appliqué à toutes les villes (%)</Label>
            <Input type="number" min={0} max={100} value={rate} onChange={(e) => setRate(e.target.value)}
              className="mt-1 h-10 rounded-xl" data-testid="bulk-target-rate" />
            <p className="text-xs text-slate-400 mt-1">Mettez 0 pour retirer les objectifs de ce pays.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button disabled={!cc} onClick={() => onApply(cc, Number(rate) || 0)} className="bg-findo-indigo hover:bg-findo-indigo/90 rounded-full font-semibold" data-testid="bulk-target-apply">
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TargetCell({ city, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(city.target || "");

  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid="target-editor">
        <Input type="number" min={0} max={100} value={val} autoFocus
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { onSave(city.city, city.country, Number(val) || 0); setEditing(false); } }}
          className="h-8 w-16 rounded-lg text-sm" data-testid="target-input" />
        <button onClick={() => { onSave(city.city, city.country, Number(val) || 0); setEditing(false); }} className="text-emerald-600 hover:text-emerald-700" data-testid="target-save"><Check size={16} /></button>
        <button onClick={() => { setEditing(false); setVal(city.target || ""); }} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
      </div>
    );
  }

  if (city.target == null) {
    return (
      <button onClick={() => setEditing(true)} className="text-xs text-findo-indigo font-semibold hover:underline inline-flex items-center gap-1" data-testid="target-set-btn">
        <Target size={13} /> Définir
      </button>
    );
  }

  const reached = (city.progress || 0) >= 100;
  return (
    <button onClick={() => setEditing(true)} className="flex items-center gap-2 group" data-testid="target-progress" title="Modifier l'objectif">
      <div className="w-20 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${reached ? "bg-emerald-500" : "bg-findo-indigo"}`} style={{ width: `${Math.min(100, city.progress || 0)}%` }} />
      </div>
      <span className={`text-xs font-semibold ${reached ? "text-emerald-600" : "text-slate-500"} group-hover:underline`}>
        {city.rate}/{city.target}%
      </span>
    </button>
  );
}

const TRAFFIC_PERIODS = [
  { v: 0, label: "Depuis le début" },
  { v: 30, label: "30 j" },
  { v: 90, label: "90 j" },
  { v: 365, label: "12 mois" },
];

function CityAnalyticsPanel() {
  const [days, setDays] = useState(0);
  const [country, setCountry] = useState("all");
  const [data, setData] = useState({ cities: [], totals: {}, countries: [] });
  const [loading, setLoading] = useState(true);
  const [sendingReport, setSendingReport] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [globalTarget, setGlobalTarget] = useState(0);
  const [yearlyTarget, setYearlyTarget] = useState(0);

  useEffect(() => { api("/admin/global-target").then((r) => setGlobalTarget(r.target)).catch(() => {}); }, []);
  useEffect(() => { api("/admin/yearly-target").then((r) => setYearlyTarget(r.target)).catch(() => {}); }, []);

  async function saveGlobalTarget(v) {
    try {
      const r = await api("/admin/global-target", { method: "PUT", body: { target_rate: v } });
      setGlobalTarget(r.target);
      toast.success(r.target ? `Objectif global : ${r.target}%` : "Objectif global retiré");
    } catch (e) { toast.error("Échec de l'objectif global"); }
  }

  async function saveYearlyTarget(v) {
    try {
      const r = await api("/admin/yearly-target", { method: "PUT", body: { target_rate: v } });
      setYearlyTarget(r.target);
      toast.success(r.target ? `Objectif annuel : ${r.target}%` : "Objectif annuel retiré");
    } catch (e) { toast.error("Échec de l'objectif annuel"); }
  }

  useEffect(() => {
    setLoading(true);
    const cq = country !== "all" ? `&country=${country}` : "";
    api(`/admin/city-analytics?days=${days}&top=20${cq}`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days, country]);

  async function exportCsv() {
    try {
      const cq = country !== "all" ? `&country=${country}` : "";
      const res = await fetch(`${API}/admin/city-analytics/export.csv?days=${days}${cq}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findloop-trafic-villes.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) { toast.error("Échec de l'export"); }
  }

  async function sendMonthlyReport() {
    setSendingReport(true);
    try {
      const r = await api(`/admin/reports/monthly/send-now`, { method: "POST" });
      toast.success(`Rapport « ${r.month} » envoyé (${r.sent} destinataire(s))`);
    } catch (e) { toast.error("Échec de l'envoi du rapport"); }
    finally { setSendingReport(false); }
  }

  async function saveTarget(city, countryCode, value) {
    try {
      await api(`/admin/city-targets`, { method: "PUT", body: { city, country: countryCode, target_rate: value } });
      await reloadData();
      toast.success(value ? `Objectif ${value}% défini` : "Objectif retiré");
    } catch (e) { toast.error("Échec de l'enregistrement de l'objectif"); }
  }

  async function reloadData() {
    const cq = country !== "all" ? `&country=${country}` : "";
    const fresh = await api(`/admin/city-analytics?days=${days}&top=20${cq}`);
    setData(fresh);
  }

  async function applyBulkTarget(countryCode, rate) {
    try {
      const r = await api(`/admin/city-targets/country`, { method: "PUT", body: { country: countryCode, target_rate: rate } });
      await reloadData();
      toast.success(`Objectif appliqué à ${r.applied} ville(s) de ${countryCode}`);
      setBulkOpen(false);
    } catch (e) { toast.error("Échec de l'objectif groupé"); }
  }

  const totals = data.totals || {};
  const chartData = (data.cities || []).slice(0, 10).map((c) => ({
    name: c.city.length > 14 ? c.city.slice(0, 13) + "…" : c.city,
    Déclarés: c.total,
    Restitués: c.restituted,
  }));

  return (
    <div data-testid="traffic-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5">
          <MapPin size={15} /> Trafic par ville
        </span>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <select value={country} onChange={(e) => setCountry(e.target.value)}
            className="h-8 rounded-full border border-border px-3 text-xs bg-white font-semibold text-findo-navy" data-testid="traffic-country-filter">
            <option value="all">Tous les pays</option>
            {(data.countries || []).map((c) => (
              <option key={c} value={c}>{countryFlag(c)} {c}</option>
            ))}
          </select>
          <div className="flex gap-1.5">
            {TRAFFIC_PERIODS.map((p) => (
              <button key={p.v} onClick={() => setDays(p.v)}
                className={`h-8 rounded-full px-3 text-xs font-semibold transition-colors ${days === p.v ? "bg-findo-navy text-white" : "bg-slate-100 text-slate-500 hover:text-findo-navy"}`}
                data-testid={`traffic-period-${p.v}`}>
                {p.label}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={exportCsv} className="h-8 rounded-full font-semibold text-xs" data-testid="traffic-export-btn">
            <Download size={14} className="mr-1.5" /> Exporter CSV
          </Button>
          <Button variant="outline" onClick={sendMonthlyReport} disabled={sendingReport} className="h-8 rounded-full font-semibold text-xs text-findo-navy" data-testid="traffic-monthly-report-btn">
            <Send size={14} className="mr-1.5" /> {sendingReport ? "Envoi…" : "Rapport mensuel"}
          </Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} className="h-8 rounded-full font-semibold text-xs text-findo-indigo" data-testid="traffic-bulk-target-btn">
            <Target size={14} className="mr-1.5" /> Objectif par pays
          </Button>
        </div>
      </div>

      <BulkTargetDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
        countries={data.countries || []} defaultCountry={country !== "all" ? country : ((data.countries || [])[0] || "")}
        onApply={applyBulkTarget} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Kpi Icon={MapPin} label="Villes actives" value={totals.cities ?? 0} />
        <Kpi Icon={Package} label="Objets déclarés" value={totals.total ?? 0} />
        <Kpi Icon={RotateCcw} label="Objets restitués" value={totals.restituted ?? 0} />
        <Kpi Icon={TrendingUp} label="Taux de restitution" value={`${totals.rate ?? 0}%`} />
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow p-4 mb-5 flex items-center gap-3 flex-wrap" data-testid="global-target-control">
        <span className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5"><Target size={15} /> Objectif global de la plateforme</span>
        <div className="flex items-center gap-2 ml-auto">
          <Input type="number" min={0} max={100} defaultValue={globalTarget} key={globalTarget}
            onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== globalTarget) saveGlobalTarget(v); }}
            className="h-9 w-24 rounded-xl" data-testid="global-target-input" />
          <span className="text-xs text-slate-400">% (0 = désactivé) · Taux actuel {totals.rate ?? 0}%</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow p-4 mb-5 flex items-center gap-3 flex-wrap" data-testid="yearly-target-control">
        <span className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5"><Target size={15} /> Objectif annuel {new Date().getFullYear()}</span>
        <div className="flex items-center gap-2 ml-auto">
          <Input type="number" min={0} max={100} defaultValue={yearlyTarget} key={yearlyTarget}
            onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== yearlyTarget) saveYearlyTarget(v); }}
            className="h-9 w-24 rounded-xl" data-testid="yearly-target-input" />
          <span className="text-xs text-slate-400">% (0 = désactivé) · affiché en public avec décompte des jours</span>
        </div>
      </div>

      <TrafficMap cities={data.cities || []} />

      <div className="bg-white rounded-2xl border border-border card-shadow p-5 mb-5" data-testid="traffic-chart">
        <p className="text-sm font-semibold text-findo-navy mb-2">Top 10 villes — déclarés vs restitués</p>
        {chartData.length === 0 ? (
          <p className="p-8 text-center text-slate-400 text-sm">{loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Chargement…</span> : "Aucune donnée sur cette période."}</p>
        ) : (
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} width={28} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Déclarés" fill="#191A54" radius={[4, 4, 0, 0]} maxBarSize={26} />
                <Bar dataKey="Restitués" fill="#0E9F6E" radius={[4, 4, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid="traffic-table">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-5 py-3 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          <span>Ville</span><span className="text-right w-16">Déclarés</span>
          <span className="text-right w-16">Restitués</span><span className="text-right w-14">Taux</span>
          <span className="text-right w-40">Objectif</span>
        </div>
        <div className="divide-y divide-border">
          {(data.cities || []).map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-5 py-3 text-sm items-center" data-testid="traffic-city-row">
              <span className="font-medium text-findo-navy truncate">
                {c.city} <span className="text-slate-400 text-xs">{c.country}</span>
              </span>
              <span className="text-right w-16 font-semibold text-slate-700">{c.total}</span>
              <span className="text-right w-16 font-semibold text-emerald-600">{c.restituted}</span>
              <span className="text-right w-14">
                <Badge className={c.rate >= 50 ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-600"}>{c.rate}%</Badge>
              </span>
              <div className="w-40 flex justify-end">
                <TargetCell city={c} onSave={saveTarget} />
              </div>
            </div>
          ))}
          {(data.cities || []).length === 0 && (
            <p className="p-8 text-center text-slate-400 text-sm">{loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Chargement…</span> : "Aucune donnée."}</p>
          )}
        </div>
      </div>
    </div>
  );
}


const ALERT_THRESHOLDS = [10, 20, 30];

function AlertsPanel() {
  const [settings, setSettings] = useState({ threshold_pp: 20, min_declared: 5, window_days: 30 });
  const [alerts, setAlerts] = useState({ alerts: [], total: 0 });
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  const loadAlerts = () => api("/admin/city-alerts?limit=100").then(setAlerts).catch(() => {});
  useEffect(() => {
    api("/admin/city-alert-settings").then(setSettings).catch(() => {});
    loadAlerts();
  }, []);

  async function saveSettings(patch) {
    setSaving(true);
    try {
      const r = await api("/admin/city-alert-settings", { method: "PATCH", body: patch });
      setSettings(r);
      toast.success("Réglages enregistrés");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  }

  async function runCheck() {
    setChecking(true);
    try {
      const r = await api("/admin/reports/city-drop-check", { method: "POST" });
      toast.success(`${r.alerted} nouvelle(s) alerte(s) · ${r.drops} baisse(s) détectée(s)`);
      loadAlerts();
    } catch (e) { toast.error(e.message); } finally { setChecking(false); }
  }

  return (
    <div data-testid="alerts-panel">
      <div className="bg-white rounded-2xl border border-border card-shadow p-5 mb-5" data-testid="alert-settings">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5"><BellRing size={15} /> Réglages des alertes de baisse</p>
          <Button variant="outline" onClick={runCheck} disabled={checking} className="h-9 rounded-full font-semibold text-xs" data-testid="alert-run-check">
            <TrendingDown size={14} className="mr-1.5" /> {checking ? "Analyse…" : "Vérifier maintenant"}
          </Button>
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs text-slate-500">Seuil de baisse (points de %)</Label>
            <div className="mt-2 flex gap-2" data-testid="alert-threshold-choices">
              {ALERT_THRESHOLDS.map((v) => (
                <button key={v} onClick={() => saveSettings({ threshold_pp: v })} disabled={saving}
                  className={`h-9 rounded-full px-4 text-sm font-semibold transition-colors ${settings.threshold_pp === v ? "bg-findo-navy text-white" : "bg-slate-100 text-slate-500 hover:text-findo-navy"}`}
                  data-testid={`alert-threshold-${v}`}>
                  {v} pts
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Volume minimum (objets déclarés par fenêtre)</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input type="number" min={1} max={10000} defaultValue={settings.min_declared}
                onBlur={(e) => { const v = Number(e.target.value) || 1; if (v !== settings.min_declared) saveSettings({ min_declared: v }); }}
                className="h-9 w-24 rounded-xl" data-testid="alert-min-declared" />
              <span className="text-xs text-slate-400">Fenêtre : {settings.window_days} j glissants</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid="alerts-history">
        <div className="px-5 py-3 bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Historique des alertes</span>
          <span className="text-xs text-slate-400">{alerts.total} alerte(s)</span>
        </div>
        <div className="divide-y divide-border">
          {(alerts.alerts || []).map((a, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-3 text-sm" data-testid="alert-row">
              <span className="h-8 w-8 grid place-items-center rounded-lg bg-red-50 text-red-600 shrink-0"><TrendingDown size={16} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-findo-navy">{a.city} <span className="text-slate-400 text-xs">{a.country}</span></p>
                <p className="text-xs text-slate-500">{a.rate_prev}% → <b className="text-red-600">{a.rate_now}%</b></p>
              </div>
              <Badge className="bg-red-100 text-red-700">▼ {a.drop_pp} pts</Badge>
              <span className="text-xs text-slate-400 whitespace-nowrap">{a.alerted_at ? new Date(a.alerted_at).toLocaleDateString("fr-FR") : ""}</span>
            </div>
          ))}
          {(alerts.alerts || []).length === 0 && (
            <p className="p-8 text-center text-slate-400 text-sm">Aucune alerte de baisse enregistrée.</p>
          )}
        </div>
      </div>
    </div>
  );
}


function ImpersonateDialog({ target, onClose }) {
  const [phase, setPhase] = useState("idle");
  const [requestId, setRequestId] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!target) { setPhase("idle"); setRequestId(null); if (pollRef.current) clearInterval(pollRef.current); return; }
    (async () => {
      try {
        setPhase("pending");
        const r = await api("/admin/impersonation/request", { method: "POST", body: { target_type: target.type, target_id: target.id } });
        setRequestId(r.request_id);
      } catch (e) { toast.error(e.message); onClose(); }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [target]);

  useEffect(() => {
    if (!requestId || phase !== "pending") return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api(`/admin/impersonation/${requestId}/poll`);
        if (r.status === "approved") { setPhase("approved"); clearInterval(pollRef.current); }
        else if (r.status === "expired") { setPhase("expired"); clearInterval(pollRef.current); }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [requestId, phase]);

  async function start() {
    setPhase("starting");
    try {
      const r = await api(`/admin/impersonation/${requestId}/start`, { method: "POST" });
      const adminToken = getToken();
      if (r.target_type === "partner") setToken(r.token);
      else setB2CToken(r.token);
      writeImpersonation({
        session_id: r.session_id, target_type: r.target_type,
        target_name: r.target?.name || target.name, expires_at: r.expires_at, admin_token: adminToken,
      });
      window.location.href = r.target_type === "partner" ? "/b2b/registry" : "/mon-compte";
    } catch (e) { toast.error(e.message); setPhase("approved"); }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="impersonate-dialog">
        <DialogHeader>
          <DialogTitle>Impersonation avec consentement</DialogTitle>
          <DialogDescription>
            {target?.name} recevra un email pour autoriser l'accès. L'impersonation ne démarrera qu'après son approbation (session limitée à 2 h, entièrement journalisée).
          </DialogDescription>
        </DialogHeader>
        <div className="py-3 text-sm">
          {phase === "pending" && <p className="text-amber-600 font-semibold" data-testid="impersonate-pending">⏳ Demande envoyée — en attente de l'approbation de l'utilisateur…</p>}
          {phase === "approved" && <p className="text-emerald-600 font-semibold" data-testid="impersonate-approved">✔ Consentement accordé. Vous pouvez démarrer.</p>}
          {phase === "starting" && <p className="text-slate-500">Démarrage…</p>}
          {phase === "expired" && <p className="text-findo-danger font-semibold">La demande a expiré. Fermez et réessayez.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="impersonate-cancel">Fermer</Button>
          <Button disabled={phase !== "approved"} onClick={start} className="rounded-full bg-findo-navy font-semibold" data-testid="impersonate-start-btn">
            Démarrer l'impersonation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoicesPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focus = searchParams.get("focus");
  const [data, setData] = useState({ invoices: [], total: 0, total_paid_eur: 0, total_unpaid_eur: 0 });
  const [aging, setAging] = useState({ buckets: [], total_due_eur: 0 });
  const [recovery, setRecovery] = useState(null);
  const [trend, setTrend] = useState(null);
  const [targetInput, setTargetInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [year, setYear] = useState(() => String(new Date().getFullYear()));
  const [reminders, setReminders] = useState(null); // {inv, list} for dialog
  const [bulkBucket, setBulkBucket] = useState(null); // aging key | "all" | null
  const [bulkChannel, setBulkChannel] = useState("both"); // "email" | "both"
  const [bulkLoading, setBulkLoading] = useState(false);
  async function load() {
    setLoading(true);
    try {
      const [inv, ag, rec] = await Promise.all([
        api("/admin/invoices"), api("/admin/invoices/aging"), api("/admin/invoices/recovery-stats"),
      ]);
      setData(inv); setAging(ag); setRecovery(rec); setTargetInput(String(rec.target || ""));
      api("/admin/invoices/recovery-trend?months=6").then(setTrend).catch(() => {});
    }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (focus && !loading) {
      const el = document.getElementById(`inv-${focus}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => setSearchParams({ tab: "invoices" }), 4000);
      return () => clearTimeout(t);
    }
  }, [focus, loading, data]);
  async function markPaid(id) {
    try { await api(`/admin/invoices/${id}/mark-paid`, { method: "POST" }); toast.success("Facture marquée payée"); load(); }
    catch (e) { toast.error(e.message); }
  }
  async function saveTarget() {
    try {
      const r = await api("/admin/recovery-target", { method: "PUT", body: { target: Number(targetInput) || 0 } });
      toast.success(`Objectif de recouvrement : ${r.target}%`);
      const rec = await api("/admin/invoices/recovery-stats"); setRecovery(rec);
      api("/admin/invoices/recovery-trend?months=6").then(setTrend).catch(() => {});
    } catch (e) { toast.error(e.message); }
  }
  async function openReminders(inv) {
    try {
      const r = await api(`/admin/invoices/${inv.quote_id}/reminders`);
      setReminders({ inv, list: r.reminders || [] });
    } catch (e) { toast.error(e.message); }
  }
  async function downloadBlob(path, filename) {
    try {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) { const t = await res.json().catch(() => ({})); throw new Error(t.detail || `Erreur ${res.status}`); }
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }
  const exportCsv = () => downloadBlob("/admin/invoices/export.csv", "findloop-factures.csv");
  const exportAccounting = () => downloadBlob(`/admin/invoices/export-accounting.csv?month=${month}`, `findloop-comptabilite-${month}.csv`);
  const exportAccountingYear = () => downloadBlob(`/admin/invoices/export-accounting.csv?month=${year}`, `findloop-comptabilite-${year}.csv`);
  const downloadReceipt = (inv) => downloadBlob(`/admin/invoices/${inv.quote_id}/receipt.pdf`, `recu-${inv.number}.pdf`);
  async function remindNow(inv) {
    try {
      const r = await api(`/admin/invoices/${inv.quote_id}/remind`, { method: "POST" });
      toast.success(`Relance envoyée${r.sms_sent ? " (email + SMS)" : " (email)"}`);
      load();
    } catch (e) { toast.error(e.message); }
  }
  async function doBulk() {
    setBulkLoading(true);
    try {
      const params = new URLSearchParams();
      if (bulkBucket && bulkBucket !== "all") params.set("bucket", bulkBucket);
      params.set("channel", bulkChannel);
      const r = await api(`/admin/invoices/remind-bulk?${params.toString()}`, { method: "POST" });
      toast.success(`${r.count} relance(s) envoyée(s) · ${r.emails_sent} email · ${r.sms_sent} SMS`);
      setBulkBucket(null); load();
    } catch (e) { toast.error(e.message); } finally { setBulkLoading(false); }
  }
  const reminderLabel = (r) => r.kind === "due" ? "Échéance" : r.kind === "sms_j30" ? "Relance SMS J+30" : r.kind?.startsWith("stage_J") ? `Rappel ${r.kind.replace("stage_J", "J+")}` : r.kind;
  return (
    <div data-testid="invoices-panel">
      <div className="grid grid-cols-3 gap-4 mb-5">
        <Kpi Icon={FileText} label="Factures" value={data.total} />
        <Kpi Icon={Check} label="Payé (€)" value={data.total_paid_eur} />
        <Kpi Icon={AlertTriangle} label="Impayé (€)" value={data.total_unpaid_eur} />
      </div>
      {recovery && (
        <div className="mb-5 bg-white rounded-2xl border border-border card-shadow p-4" data-testid="recovery-target-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <p className="text-sm font-semibold text-findo-navy flex items-center gap-1.5"><Target size={15} /> Objectif de recouvrement — {recovery.month}</p>
            <div className="flex items-center gap-1.5">
              <Input type="number" min={0} max={100} value={targetInput} onChange={(e) => setTargetInput(e.target.value)} className="h-8 w-20 text-xs" data-testid="recovery-target-input" placeholder="%" />
              <Button size="sm" onClick={saveTarget} className="rounded-full bg-findo-navy h-8 text-xs font-semibold" data-testid="recovery-target-save">Définir</Button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-slate-500">Taux encaissé : <b className="text-findo-navy">{recovery.rate}%</b> ({recovery.paid_eur} € / {recovery.invoiced_eur} €)</span>
            <span className="font-semibold" data-testid="recovery-target-value">{recovery.target ? `Objectif ${recovery.target}%` : "Aucun objectif"}</span>
          </div>
          <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${recovery.reached ? "bg-emerald-500" : "bg-findo-navy"}`} style={{ width: `${recovery.target ? recovery.progress : recovery.rate}%` }} data-testid="recovery-target-progress" />
          </div>
          {recovery.reached && <p className="text-xs text-emerald-600 font-semibold mt-1.5" data-testid="recovery-target-reached">🎉 Objectif de recouvrement atteint ce mois-ci !</p>}
          {trend && trend.points && trend.points.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border" data-testid="recovery-trend">
              <p className="text-xs font-semibold text-slate-500 mb-2">Évolution du taux de recouvrement (6 mois)</p>
              {trend.target ? (
                <div className="flex items-center gap-4 mb-1 text-[11px] text-slate-400" data-testid="recovery-trend-legend">
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" /> Objectif atteint</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-findo-red" /> En dessous</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-300" /> Aucune facture</span>
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={trend.points} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip formatter={(v, n, p) => [`${v}%  (${p.payload.paid_eur} € / ${p.payload.invoiced_eur} €)`, "Taux"]} />
                  {trend.target ? <ReferenceLine y={trend.target} stroke="#E4232E" strokeDasharray="4 4" label={{ value: `Objectif ${trend.target}%`, fontSize: 10, fill: "#E4232E", position: "insideTopRight" }} /> : null}
                  <Line type="monotone" dataKey="rate" stroke="#191A54" strokeWidth={2.5}
                    dot={(props) => {
                      const { cx, cy, payload, index } = props;
                      const noData = !payload.invoiced_eur;
                      const color = noData ? "#cbd5e1" : trend.target ? (payload.rate >= trend.target ? "#10b981" : "#E4232E") : "#191A54";
                      return <circle key={`dot-${index}`} cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={1.5} data-testid={`recovery-trend-dot-${payload.month}`} />;
                    }}
                    activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-lg font-semibold text-findo-navy">Archive de facturation</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCsv} className="rounded-full font-semibold" data-testid="invoices-export-btn"><Download size={14} className="mr-1.5" /> Export CSV</Button>
          <div className="flex items-center gap-1.5 bg-white rounded-full border border-border px-2 py-0.5">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="text-xs bg-transparent outline-none py-1" data-testid="accounting-month" />
            <Button size="sm" onClick={exportAccounting} className="rounded-full bg-findo-navy h-7 text-xs font-semibold" data-testid="accounting-export-btn"><Download size={13} className="mr-1" /> Export compta</Button>
          </div>
          <div className="flex items-center gap-1.5 bg-white rounded-full border border-border px-2 py-0.5">
            <input type="number" min={2020} max={2100} value={year} onChange={(e) => setYear(e.target.value)} className="text-xs bg-transparent outline-none py-1 w-16" data-testid="accounting-year" />
            <Button size="sm" onClick={exportAccountingYear} className="rounded-full bg-findo-navy h-7 text-xs font-semibold" data-testid="accounting-year-export-btn"><Download size={13} className="mr-1" /> Export année</Button>
          </div>
        </div>
      </div>
      <div className="mb-5 bg-white rounded-2xl border border-border card-shadow p-4" data-testid="invoices-aging">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="text-sm font-semibold text-findo-navy">Recouvrement — impayés par ancienneté</p>
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-findo-red">Total dû : {aging.total_due_eur} €</p>
            <Button size="sm" onClick={() => setBulkBucket("all")} disabled={!aging.total_due_eur} className="rounded-full bg-findo-red hover:bg-findo-red/90 h-8 text-xs font-semibold" data-testid="bulk-remind-all-btn"><Send size={13} className="mr-1" /> Relancer tous les impayés</Button>
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {aging.buckets.map((b) => (
            <div key={b.key} className={`rounded-xl p-2.5 text-center border ${b.count ? "border-findo-red/30 bg-findo-red/5" : "border-border bg-slate-50"}`} data-testid={`aging-bucket-${b.key}`}>
              <p className="text-[11px] text-slate-500 leading-tight">{b.label}</p>
              <p className="text-lg font-bold text-findo-navy mt-1">{b.count}</p>
              <p className="text-[11px] text-slate-400">{b.total_eur} €</p>
              {b.count > 0 && (
                <button onClick={() => setBulkBucket(b.key)} className="mt-1.5 text-[11px] font-semibold text-findo-red hover:underline" data-testid={`bulk-remind-bucket-${b.key}`}>Relancer</button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden">
        {loading ? <PageLoader label="Chargement…" className="p-8" size={32} />
          : data.invoices.length === 0 ? <p className="p-8 text-center text-slate-400 text-sm">Aucune facture émise.</p>
          : <div className="divide-y divide-border">
            {data.invoices.map((inv) => (
              <div key={inv.quote_id} id={`inv-${inv.number}`} className={`flex items-center gap-3 p-3.5 text-sm transition-colors ${inv.number === focus ? "ring-2 ring-inset ring-findo-red bg-findo-red/5" : ""}`} data-testid="invoice-row">
                <span className="font-mono font-semibold text-findo-navy w-32 flex-shrink-0">{inv.number}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{inv.org || inv.client_name}</p>
                  <p className="text-xs text-slate-400 truncate">{inv.email} · émise {String(inv.issued_at).slice(0, 10)}</p>
                </div>
                <span className="font-bold text-slate-900">{inv.amount_ttc} {inv.currency}</span>
                <Badge className={inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {inv.status === "paid" ? "Payée" : "Impayée"}
                </Badge>
                <Button size="sm" variant="outline" onClick={() => openReminders(inv)} className="rounded-full h-8 text-xs font-semibold" data-testid={`invoice-reminders-${inv.quote_id}`}>
                  <BellRing size={13} className="mr-1" /> Relances{inv.reminder_count ? ` (${inv.reminder_count})` : ""}
                </Button>
                {inv.status === "paid"
                  ? <Button size="sm" variant="outline" onClick={() => downloadReceipt(inv)} className="rounded-full h-8 text-xs font-semibold" data-testid={`invoice-receipt-${inv.quote_id}`}><Download size={13} className="mr-1" /> Reçu</Button>
                  : <>
                      <Button size="sm" variant="outline" onClick={() => remindNow(inv)} className="rounded-full h-8 text-xs font-semibold border-findo-red/40 text-findo-red hover:bg-findo-red/5" data-testid={`invoice-remind-${inv.quote_id}`}><Send size={13} className="mr-1" /> Relancer</Button>
                      <Button size="sm" onClick={() => markPaid(inv.quote_id)} className="rounded-full bg-findo-navy h-8 text-xs" data-testid={`invoice-mark-paid-${inv.quote_id}`}>Marquer payée</Button>
                    </>
                }
              </div>
            ))}
          </div>}
      </div>
      <Dialog open={!!reminders} onOpenChange={(o) => !o && setReminders(null)}>
        <DialogContent data-testid="reminders-dialog">
          <DialogHeader>
            <DialogTitle>Historique des relances — {reminders?.inv?.number}</DialogTitle>
            <DialogDescription>Rappels email et SMS envoyés pour cette facture.</DialogDescription>
          </DialogHeader>
          {reminders?.list?.length ? (
            <div className="divide-y divide-border max-h-80 overflow-auto" data-testid="reminders-list">
              {reminders.list.map((r) => (
                <div key={r.id} className="flex items-center gap-2 py-2.5 text-sm" data-testid="reminder-row">
                  <Badge className={r.channel === "sms" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}>{r.channel === "sms" ? "SMS" : "Email"}</Badge>
                  <span className="flex-1 text-slate-700">{reminderLabel(r)}</span>
                  <span className="text-xs text-slate-400">{new Date(r.at).toLocaleString("fr-FR")}</span>
                </div>
              ))}
            </div>
          ) : <p className="py-6 text-center text-slate-400 text-sm" data-testid="reminders-empty">Aucune relance envoyée pour cette facture.</p>}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!bulkBucket} onOpenChange={(o) => !o && setBulkBucket(null)}>
        <AlertDialogContent data-testid="bulk-remind-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Relancer les impayés</AlertDialogTitle>
            <AlertDialogDescription>
              Un email (avec facture PDF){bulkChannel === "both" ? " et un SMS (si numéro connu)" : ""} sera envoyé {bulkBucket === "all" ? "à toutes les factures impayées" : `aux factures impayées de la tranche « ${bulkBucket} »`}. Confirmer l'envoi ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 py-1" data-testid="bulk-channel-choice">
            <button onClick={() => setBulkChannel("email")} className={`rounded-full px-3 h-8 text-xs font-semibold border ${bulkChannel === "email" ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border"}`} data-testid="bulk-channel-email">Email seul</button>
            <button onClick={() => setBulkChannel("both")} className={`rounded-full px-3 h-8 text-xs font-semibold border ${bulkChannel === "both" ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border"}`} data-testid="bulk-channel-both">Email + SMS</button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-remind-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doBulk} disabled={bulkLoading} className="bg-findo-red hover:bg-findo-red/90" data-testid="bulk-remind-confirm">{bulkLoading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Envoi…</span> : "Relancer"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Secondary admins (RBAC) ────────────────────────────────────── */

function relTime(iso) {
  if (!iso) return "jamais";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (isNaN(diff)) return "—";
  const m = Math.floor(diff / 60000), h = Math.floor(m / 60), day = Math.floor(h / 24);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  if (h < 24) return `il y a ${h} h`;
  if (day < 30) return `il y a ${day} j`;
  return d.toLocaleDateString("fr-FR");
}

function inviteExpired(iso) {
  if (!iso) return true;
  return new Date(iso).getTime() <= Date.now();
}

function inviteCountdown(iso) {
  if (!iso) return "Invitation en attente";
  const diff = new Date(iso).getTime() - Date.now();
  if (isNaN(diff)) return "Invitation en attente";
  if (diff <= 0) return "⚠ Invitation expirée";
  const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000);
  return h >= 1 ? `Expire dans ${h} h ${m} min` : `Expire dans ${m} min`;
}

const GOV_DOT = {
  "admin.auto_suspend": "bg-findo-red",
  "admin.invite_auto_renew": "bg-findo-indigo",
  "admin.reactivate": "bg-teal-500",
  "admin.invite": "bg-amber-400",
  "admin.revoke_invite": "bg-slate-400",
  "admin.delete": "bg-slate-500",
};

function isInactiveAdmin(a) {
  const cands = [a.last_login_at, a.reactivated_at, a.created_at].filter(Boolean);
  if (!cands.length) return false;
  const ref = cands.sort().slice(-1)[0];
  return (Date.now() - new Date(ref).getTime()) > 30 * 24 * 3600 * 1000;
}

const GOV_FIELDS = [
  { key: "inactive_days", label: "Alerte d'inactivité (jours)", min: 1, max: 365 },
  { key: "suspend_days", label: "Suspension auto (jours)", min: 1, max: 730 },
  { key: "renew_within_hours", label: "Fenêtre de relance invitation (heures)", min: 1, max: 168 },
  { key: "max_renews", label: "Relances auto maximum", min: 0, max: 10 },
  { key: "dedup_days", label: "Anti-doublon alertes (jours)", min: 1, max: 90 },
];

function GovSettingsCard({ settings, onSave }) {
  const [form, setForm] = useState(settings);
  useEffect(() => { setForm(settings); }, [settings]);
  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-4 mb-4" data-testid="gov-settings-card">
      <p className="font-display font-semibold text-findo-navy mb-3 flex items-center gap-2">
        <Settings size={16} className="text-findo-indigo" /> Réglages de gouvernance
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {GOV_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-xs">{f.label}</Label>
            <Input type="number" min={f.min} max={f.max} value={form[f.key] ?? ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value === "" ? "" : Number(e.target.value) })}
              className="mt-1 h-10 rounded-xl" data-testid={`gov-setting-${f.key}`} />
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <Button onClick={() => onSave(form)} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="gov-settings-save">
          Enregistrer les réglages
        </Button>
      </div>
    </div>
  );
}

function AdminsPanel({ ctx }) {
  const [data, setData] = useState({ admins: [], roles: [], assignable_perms: [] });
  const [formOpen, setFormOpen] = useState(false);
  const [permsTarget, setPermsTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [gov, setGov] = useState(null);
  const [govDays, setGovDays] = useState(90);
  const [govAction, setGovAction] = useState("");
  const [settings, setSettings] = useState(null);
  const [recap, setRecap] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [account, setAccount] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const isMain = ctx?.is_main;

  const loadGov = (days, action) =>
    api(`/admin/admins/governance-history?days=${days}${action ? `&action=${action}` : ""}`).then(setGov).catch(() => {});
  const load = () => {
    api("/admin/admins").then(setData).catch((e) => toast.error(e.message));
    loadGov(govDays, govAction);
    api("/admin/admins/governance-settings").then(setSettings).catch(() => {});
    api("/admin/admins/team-recap").then(setRecap).catch(() => {});
    if (ctx?.is_main) api("/admin/account").then(setAccount).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (gov) loadGov(govDays, govAction); /* eslint-disable-next-line */ }, [govDays, govAction]);

  const roleLabel = (k) => (data.roles.find((r) => r.key === k) || {}).label || k;
  const permLabel = (k) => (data.assignable_perms.find((p) => p.key === k) || {}).label || k;

  async function setRole(a, role) {
    setBusyId(a.id);
    try { await api(`/admin/admins/${a.id}`, { method: "PATCH", body: { role } }); toast.success("Rôle mis à jour"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }
  async function toggle(a) {
    const next = a.admin_status === "active" ? "suspended" : "active";
    setBusyId(a.id);
    try { await api(`/admin/admins/${a.id}`, { method: "PATCH", body: { status: next } }); toast.success(next === "active" ? "Admin activé" : "Admin suspendu"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }
  async function resendInvite(a) {
    setBusyId(a.id);
    try { await api(`/admin/admins/${a.id}/resend-invite`, { method: "POST" }); toast.success("Invitation renvoyée"); }
    catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }
  async function revokeInvite(a) {
    setBusyId(a.id);
    try { await api(`/admin/admins/${a.id}/revoke-invite`, { method: "POST" }); toast.success("Invitation révoquée"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }
  async function remove(a) {
    setBusyId(a.id);
    try { await api(`/admin/admins/${a.id}`, { method: "DELETE" }); toast.success("Admin supprimé"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }
  async function changeEmail(e) {
    e.preventDefault();
    const em = (newEmail || "").trim().toLowerCase();
    if (!em) return;
    const authorized = (account?.superadmin_emails || []).map((x) => x.toLowerCase());
    if (!authorized.includes(em)) {
      toast.error("Ajoutez d'abord cet email au secret SUPERADMIN_EMAILS (Panneau de déploiement → Secrets) puis redéployez, avant de changer l'email ici — sinon vous perdriez l'accès à la console.", { duration: 11000 });
      return;
    }
    if (!window.confirm(`Changer l'email du compte superadmin de « ${account?.email} » vers « ${em} » ?\n\nVous serez déconnecté et devrez vous reconnecter avec le nouvel email.`)) return;
    setEmailBusy(true);
    try {
      const r = await api("/admin/account/email", { method: "PATCH", body: { new_email: em } });
      toast.success(`Email mis à jour : ${r.new_email}. Reconnectez-vous avec le nouvel email.`, { duration: 9000 });
      setNewEmail("");
      api("/admin/account").then(setAccount).catch(() => {});
    } catch (err) { toast.error(err.message, { duration: 11000 }); }
    finally { setEmailBusy(false); }
  }
  async function checkInactivity() {
    try {
      const r = await api("/admin/admins/check-inactivity", { method: "POST" });
      toast.success(r.inactive > 0 ? `${r.inactive} admin(s) inactif(s) — ${r.alerted} alerte(s) envoyée(s)` : "Aucun admin inactif");
    } catch (e) { toast.error(e.message); }
  }
  async function runMaintenance() {
    try {
      const [susp, renew] = await Promise.all([
        api("/admin/admins/auto-suspend", { method: "POST" }),
        api("/admin/admins/auto-renew-invites", { method: "POST" }),
      ]);
      toast.success(`Maintenance : ${susp.suspended} suspendu(s) · ${renew.renewed} invitation(s) relancée(s)`);
      load();
    } catch (e) { toast.error(e.message); }
  }
  async function sendRecap() {
    try {
      const r = await api("/admin/admins/team-recap/send-now", { method: "POST" });
      toast.success(`Récap envoyé (${r.sent}) — ${r.active} actif(s), ${r.pending_invites} invitation(s) en attente`);
    } catch (e) { toast.error(e.message); }
  }
  async function reactivate(a) {
    setBusyId(a.id);
    try { await api(`/admin/admins/${a.id}/reactivate`, { method: "POST" }); toast.success("Admin réactivé — lien d'accès envoyé"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }
  async function saveSettings(next) {
    try {
      const r = await api("/admin/admins/governance-settings", { method: "PUT", body: next });
      setSettings(r); toast.success("Réglages enregistrés");
    } catch (e) { toast.error(e.message); }
  }
  async function exportGovCsv() {
    try {
      const res = await fetch(`${API}/admin/admins/governance-history/export.csv?days=${govDays}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findloop-gouvernance.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible"); }
  }

  const STATUS_META = {
    active: { label: "Actif", cls: "bg-teal-100 text-teal-700" },
    suspended: { label: "Suspendu", cls: "bg-red-100 text-red-700" },
    invited: { label: "Invité", cls: "bg-amber-100 text-amber-700" },
  };

  return (
    <div>
      {isMain && account && (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="superadmin-account-card">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={18} className="text-findo-indigo" />
            <h3 className="font-bold text-slate-800">Mon compte superadmin</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Email de connexion actuel : <b className="text-slate-800" data-testid="superadmin-current-email">{account.email}</b>
          </p>
          <form onSubmit={changeEmail} className="flex flex-col sm:flex-row sm:items-center gap-2 max-w-xl">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="nouvel-email@getfindo.io"
              className="h-10 rounded-xl flex-1"
              data-testid="superadmin-new-email-input"
            />
            <Button type="submit" disabled={emailBusy || !newEmail.trim()} className="rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90 flex-shrink-0" data-testid="superadmin-change-email-btn">
              {emailBusy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Mise à jour…</span> : "Changer l'email"}
            </Button>
          </form>
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="superadmin-email-warning">
            ⚠️ Pour éviter de perdre l'accès : le nouvel email doit <b>déjà</b> figurer dans le secret <code className="bg-amber-100 px-1 rounded">SUPERADMIN_EMAILS</code> (Panneau de déploiement → Secrets, puis redéployer). Emails superadmin autorisés actuellement :{" "}
            <span className="font-semibold" data-testid="superadmin-allowlist">{(account.superadmin_emails || []).join(", ") || "—"}</span>.
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">
          Gérez les administrateurs secondaires : invitez-les par email, ajustez leur rôle et leurs permissions précises. Toute action est tracée dans le Journal.
        </p>
        {isMain && (
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            <Button variant="outline" onClick={sendRecap} className="rounded-full font-semibold" data-testid="admin-team-recap">
              <Mail size={15} className="mr-1.5" /> Récap hebdo
            </Button>
            <Button variant="outline" onClick={runMaintenance} className="rounded-full font-semibold" data-testid="admin-run-maintenance">
              <RefreshCw size={15} className="mr-1.5" /> Maintenance
            </Button>
            <Button variant="outline" onClick={checkInactivity} className="rounded-full font-semibold" data-testid="admin-check-inactivity">
              <AlertTriangle size={15} className="mr-1.5" /> Vérifier l'inactivité
            </Button>
            <Button variant="outline" onClick={() => setShowSettings((s) => !s)} className="rounded-full font-semibold" data-testid="admin-gov-settings-toggle">
              <Settings size={15} className="mr-1.5" /> Réglages
            </Button>
            <Button onClick={() => setFormOpen(true)} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="admin-add-btn">
              <UserPlus size={16} className="mr-1.5" /> Ajouter un admin
            </Button>
          </div>
        )}
      </div>

      {isMain && gov && (
        <div className="mb-4" data-testid="gov-history">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            {recap?.health && (
              <div className="flex items-center gap-2" data-testid="gov-health-badge">
                <span className="text-lg">{recap.health.emoji}</span>
                <div>
                  <p className={`text-sm font-display font-bold ${recap.health.level === "red" ? "text-findo-red" : recap.health.level === "yellow" ? "text-amber-600" : "text-teal-600"}`}>
                    Santé de l'équipe : {recap.health.label}
                  </p>
                  {recap.health.trend && (
                    <p className={`text-[11px] font-semibold ${recap.health.trend.direction === "up" ? "text-teal-600" : recap.health.trend.direction === "down" ? "text-findo-red" : "text-slate-400"}`} data-testid="gov-health-trend">
                      {recap.health.trend.direction === "up" ? "▲" : recap.health.trend.direction === "down" ? "▼" : "▬"} {recap.health.trend.label}
                    </p>
                  )}
                  <p className="text-[11px] text-slate-400">{recap.health.reasons.join(" · ")}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <select value={govDays} onChange={(e) => setGovDays(Number(e.target.value))}
                className="h-9 rounded-full border border-border px-3 text-sm font-semibold text-findo-navy bg-white" data-testid="gov-period-select">
                <option value={30}>30 jours</option>
                <option value={90}>90 jours</option>
                <option value={365}>365 jours</option>
              </select>
              <Button variant="outline" size="sm" onClick={exportGovCsv} className="rounded-full font-semibold" data-testid="gov-export-csv">
                <Download size={14} className="mr-1.5" /> Exporter (CSV)
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Suspensions auto", gov.counts["admin.auto_suspend"] || 0, "text-findo-red"],
              ["Relances auto", gov.counts["admin.invite_auto_renew"] || 0, "text-findo-indigo"],
              ["Réactivations", gov.counts["admin.reactivate"] || 0, "text-teal-600"],
              ["Invitations révoquées", gov.counts["admin.revoke_invite"] || 0, "text-slate-600"],
            ].map(([label, val, cls]) => (
              <div key={label} className="bg-white rounded-xl border border-border p-3">
                <p className={`text-2xl font-display font-bold ${cls}`}>{val}</p>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{label} · {govDays} j</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-border card-shadow p-4 mt-3" data-testid="gov-timeline">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <p className="font-display font-semibold text-findo-navy flex items-center gap-2">
                <History size={16} className="text-findo-indigo" /> Frise de gouvernance · {govDays} derniers jours
              </p>
              <select value={govAction} onChange={(e) => setGovAction(e.target.value)}
                className="h-9 rounded-full border border-border px-3 text-sm font-semibold text-findo-navy bg-white" data-testid="gov-timeline-filter">
                <option value="">Tous les événements</option>
                {(gov.actions || []).map((a) => (
                  <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                ))}
              </select>
            </div>
            {(gov.recent || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-2" data-testid="gov-timeline-empty">Aucun événement de gouvernance sur la période.</p>
            ) : (
              <ol className="relative border-l-2 border-slate-100 ml-2 space-y-4">
                {gov.recent.map((e, i) => (
                  <li key={i} className="ml-4" data-testid="gov-timeline-item">
                    <span className={`absolute -left-[9px] h-4 w-4 rounded-full border-2 border-white ${GOV_DOT[e.action] || "bg-slate-300"}`} />
                    <p className="text-sm font-semibold text-slate-800">{ACTION_LABELS[e.action] || e.action}</p>
                    <p className="text-xs text-slate-500">
                      {e.target_email ? <span className="font-medium">{e.target_email}</span> : null}
                      {e.actor_email && e.actor_email !== "system" ? ` · par ${e.actor_email}` : (e.actor_email === "system" ? " · automatique" : "")}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{relTime(e.created_at)}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}

      {isMain && showSettings && settings && (
        <GovSettingsCard settings={settings} onSave={saveSettings} />
      )}

      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border overflow-hidden" data-testid="admins-list">
        {data.admins.map((a) => {
          const status = a.admin_status || "active";
          const sm = STATUS_META[status] || STATUS_META.active;
          const custom = (a.admin_perms || []).length > 0;
          return (
            <div key={a.id} className="flex items-center gap-3 p-4 flex-wrap" data-testid="admin-row">
              <span className="h-9 w-9 flex-shrink-0 grid place-items-center rounded-lg bg-findo-indigo/10 text-findo-indigo"><ShieldCheck size={17} /></span>
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-slate-800 truncate">{a.org_name || a.email}</p>
                <p className="text-xs text-slate-400 truncate">{a.email}</p>
                {custom && (
                  <p className="text-[11px] text-findo-indigo mt-0.5 font-semibold" data-testid={`admin-perms-summary-${a.id}`}>
                    Perms : {(a.admin_perms || []).map(permLabel).join(", ")}
                  </p>
                )}
                {status === "invited" ? (
                  <p className={`text-[11px] mt-0.5 font-semibold ${inviteExpired(a.invite_expires_at) ? "text-findo-danger" : "text-amber-600"}`} data-testid={`admin-invite-countdown-${a.id}`}>
                    {inviteCountdown(a.invite_expires_at)}
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-0.5" data-testid={`admin-activity-${a.id}`}>
                    Connexion : {relTime(a.last_login_at)} · Activité : {a.last_action ? `${ACTION_LABELS[a.last_action] || a.last_action} (${relTime(a.last_action_at)})` : "aucune"} · {a.actions_30d || 0} action(s) / 30 j
                  </p>
                )}
              </div>
              {isMain ? (
                <select
                  value={a.admin_role}
                  disabled={busyId === a.id}
                  onChange={(e) => setRole(a, e.target.value)}
                  className="h-9 rounded-lg border border-border bg-white px-2 text-sm font-semibold text-findo-navy cursor-pointer focus:outline-none focus:ring-2 focus:ring-findo-indigo/30"
                  data-testid={`admin-role-${a.id}`}>
                  {(data.roles || []).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              ) : (
                <Badge className="bg-slate-100 text-findo-navy">{roleLabel(a.admin_role)}</Badge>
              )}
              <Badge className={sm.cls} data-testid={`admin-status-${a.id}`}>{sm.label}</Badge>
              {status === "suspended" && a.auto_suspended && (
                <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1" data-testid={`admin-autosuspended-${a.id}`}>
                  <AlertTriangle size={12} /> Suspendu auto
                </Badge>
              )}
              {status === "active" && isInactiveAdmin(a) && (
                <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1" data-testid={`admin-inactive-${a.id}`}>
                  <AlertTriangle size={12} /> Inactif
                </Badge>
              )}
              {isMain && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button variant="outline" size="sm" disabled={busyId === a.id} onClick={() => setPermsTarget(a)} className="rounded-full h-8 px-3 font-semibold" data-testid={`admin-perms-${a.id}`}>
                    <KeyRound size={14} className="mr-1" /> Permissions
                  </Button>
                  {status === "invited" && (
                    <Button variant="outline" size="sm" disabled={busyId === a.id} onClick={() => resendInvite(a)} className="rounded-full h-8 px-3 font-semibold text-findo-indigo" data-testid={`admin-resend-${a.id}`}>
                      <Mail size={14} className="mr-1" /> Renvoyer
                    </Button>
                  )}
                  {status === "invited" && (
                    <Button variant="outline" size="sm" disabled={busyId === a.id} onClick={() => revokeInvite(a)} className="rounded-full h-8 px-3 font-semibold text-findo-red border-findo-red/30 hover:bg-findo-red/10" data-testid={`admin-revoke-${a.id}`}>
                      <X size={14} className="mr-1" /> Révoquer
                    </Button>
                  )}
                  {status === "active" && (
                    <Button variant="outline" size="sm" disabled={busyId === a.id} onClick={() => toggle(a)} className="rounded-full h-8 px-3 font-semibold" data-testid={`admin-toggle-${a.id}`}>
                      <Power size={14} className="mr-1" /> Suspendre
                    </Button>
                  )}
                  {status === "suspended" && (
                    <Button variant="outline" size="sm" disabled={busyId === a.id} onClick={() => reactivate(a)} className="rounded-full h-8 px-3 font-semibold text-teal-700 border-teal-300 hover:bg-teal-50" data-testid={`admin-reactivate-${a.id}`}>
                      <RotateCcw size={14} className="mr-1" /> Réactiver + accès
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" disabled={busyId === a.id} className="rounded-full h-8 w-8 p-0 text-findo-danger border-findo-danger/30 hover:bg-findo-danger/10" data-testid={`admin-delete-${a.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cet admin ?</AlertDialogTitle>
                        <AlertDialogDescription>{a.email} perdra tout accès à la console. Cette action est irréversible.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(a)} className="bg-findo-danger hover:bg-findo-danger/90" data-testid={`admin-delete-confirm-${a.id}`}>Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          );
        })}
        {data.admins.length === 0 && <p className="p-8 text-center text-slate-400 text-sm" data-testid="admins-empty">Aucun administrateur secondaire.</p>}
      </div>

      {isMain && <AdminFormDialog open={formOpen} onClose={() => setFormOpen(false)} roles={data.roles} assignablePerms={data.assignable_perms} reload={load} />}
      {isMain && <AdminPermsDialog target={permsTarget} assignablePerms={data.assignable_perms} onClose={() => setPermsTarget(null)} reload={load} />}
    </div>
  );
}

function AdminPermsDialog({ target, assignablePerms, onClose, reload }) {
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setSel(target.admin_perms || []); }, [target]);
  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  async function save() {
    setBusy(true);
    try {
      await api(`/admin/admins/${target.id}`, { method: "PATCH", body: { perms: sel } });
      toast.success("Permissions mises à jour");
      onClose(); reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-perms-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Permissions personnalisées</DialogTitle>
          <DialogDescription>
            Cochez les permissions précises à accorder. Si aucune n'est cochée, le rôle prédéfini s'applique. « Vue d'ensemble » est toujours incluse.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {assignablePerms.map((p) => (
            <label key={p.key} className="flex items-center gap-3 p-2.5 rounded-xl border border-border hover:bg-slate-50 cursor-pointer" data-testid={`admin-perm-opt-${p.key}`}>
              <input type="checkbox" checked={sel.includes(p.key)} onChange={() => toggle(p.key)} className="h-4 w-4 accent-findo-indigo" data-testid={`admin-perm-check-${p.key}`} />
              <span className="text-sm font-medium text-slate-800">{p.label}</span>
              <code className="ml-auto text-[11px] text-slate-400">{p.key}</code>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSel([])} className="rounded-xl font-semibold" data-testid="admin-perms-clear">Réinitialiser (rôle par défaut)</Button>
          <Button onClick={save} disabled={busy} className="rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="admin-perms-save">{busy ? <FindoSpinner size={16} /> : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminFormDialog({ open, onClose, roles, assignablePerms, reload }) {
  const empty = { email: "", org_name: "", role: "support", password: "", mode: "invite", perms: [] };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm({ ...empty, role: roles[0]?.key || "support" }); /* eslint-disable-next-line */ }, [open, roles]);

  const togglePerm = (k) => setForm((f) => ({ ...f, perms: f.perms.includes(k) ? f.perms.filter((x) => x !== k) : [...f.perms, k] }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        email: form.email.trim().toLowerCase(), org_name: form.org_name || undefined,
        role: form.role, perms: form.perms.length ? form.perms : undefined,
      };
      if (form.mode === "password") body.password = form.password;
      const r = await api("/admin/admins", { method: "POST", body });
      toast.success(r.invited ? "Invitation envoyée par email" : "Administrateur créé");
      onClose();
      reload();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-form-dialog">
        <DialogHeader><DialogTitle className="font-display">Ajouter un administrateur</DialogTitle>
          <DialogDescription>Invitez un administrateur par email (il choisit son mot de passe) ou définissez-en un manuellement. Ajoutez des permissions précises si besoin.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="findo-numbered space-y-5">
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="admin-form-email" /></div>
          <div><Label>Nom (optionnel)</Label><Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="admin-form-name" /></div>
          <div><Label>Rôle</Label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border bg-white px-3 text-sm font-semibold text-findo-navy cursor-pointer focus:outline-none focus:ring-2 focus:ring-findo-indigo/30" data-testid="admin-form-role">
              {(roles || []).length === 0 && <option value="support">Support</option>}
              {(roles || []).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            {(() => {
              const sel = (roles || []).find((r) => r.key === form.role);
              if (!sel?.perms?.length) return null;
              return (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="admin-form-role-perms">
                  {sel.perms.map((p) => (
                    <span key={p} className="inline-flex items-center rounded-full bg-findo-indigo/10 text-findo-indigo text-[11px] font-semibold px-2 py-0.5">
                      {p}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>

          <div>
            <Label>Méthode d'accès</Label>
            <div className="mt-1.5 flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, mode: "invite" })} data-testid="admin-form-mode-invite"
                className={`flex-1 h-10 rounded-xl text-sm font-semibold border transition-colors ${form.mode === "invite" ? "bg-findo-indigo text-white border-findo-indigo" : "bg-white text-findo-navy border-border"}`}>
                Inviter par email
              </button>
              <button type="button" onClick={() => setForm({ ...form, mode: "password" })} data-testid="admin-form-mode-password"
                className={`flex-1 h-10 rounded-xl text-sm font-semibold border transition-colors ${form.mode === "password" ? "bg-findo-indigo text-white border-findo-indigo" : "bg-white text-findo-navy border-border"}`}>
                Mot de passe manuel
              </button>
            </div>
          </div>
          {form.mode === "password" && (
            <div><Label>Mot de passe (≥ 6 caractères)</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="admin-form-password" /></div>
          )}

          <div>
            <Label>Permissions personnalisées (optionnel)</Label>
            <p className="text-[11px] text-slate-400 mb-1.5">Laissez vide pour utiliser les permissions du rôle.</p>
            <div className="grid grid-cols-2 gap-1.5">
              {assignablePerms.map((p) => (
                <label key={p.key} className="flex items-center gap-2 p-2 rounded-lg border border-border hover:bg-slate-50 cursor-pointer text-sm" data-testid={`admin-form-perm-${p.key}`}>
                  <input type="checkbox" checked={form.perms.includes(p.key)} onChange={() => togglePerm(p.key)} className="h-4 w-4 accent-findo-indigo" />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} className="w-full rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="admin-form-submit">
              {busy ? <FindoSpinner size={16} /> : (form.mode === "invite" ? "Envoyer l'invitation" : "Créer l'administrateur")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ImpersonationLogsPanel() {
  const [data, setData] = useState({ sessions: [], total: 0 });
  const [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try { setData(await api("/admin/impersonation-logs?limit=200")); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  function fmtDur(s) {
    if (s == null) return "en cours";
    const m = Math.floor(s / 60), sec = s % 60;
    return m ? `${m} min ${sec}s` : `${sec}s`;
  }
  function cause(s) {
    if (s.revoked_by === "user") return { label: "Révoquée par l'utilisateur", cls: "bg-findo-red/10 text-findo-red" };
    if (s.ended_by === "expiration" || s.ended_reason === "expired") return { label: "Expirée", cls: "bg-slate-100 text-slate-600" };
    if (s.ended_at) return { label: "Terminée par l'admin", cls: "bg-slate-100 text-slate-600" };
    return { label: "En cours", cls: "bg-amber-100 text-amber-700" };
  }
  return (
    <div data-testid="impersonation-logs-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-semibold text-findo-navy">Journal des impersonations ({data.total})</h3>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full font-semibold" data-testid="impersonation-logs-refresh">Rafraîchir</Button>
      </div>
      <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden">
        {loading ? <PageLoader label="Chargement…" className="p-8" size={32} />
          : data.sessions.length === 0 ? <p className="p-8 text-center text-slate-400 text-sm">Aucune impersonation enregistrée.</p>
          : <div className="divide-y divide-border">
            {data.sessions.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3.5 text-sm" data-testid="impersonation-log-row">
                <Badge className="bg-findo-navy/10 text-findo-navy capitalize">{s.target_type === "partner" ? "B2B" : "B2C"}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{s.target_name} <span className="text-slate-400 font-normal">({s.target_email})</span></p>
                  <p className="text-xs text-slate-400 truncate">par {s.admin_email} · {s.started_at ? new Date(s.started_at).toLocaleString("fr-FR") : ""}</p>
                </div>
                {(() => { const c = cause(s); return <Badge className={c.cls} data-testid="impersonation-log-cause">{c.label}</Badge>; })()}
                <Badge className={s.ended_at ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}>Durée : {fmtDur(s.duration_seconds)}</Badge>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}

function CookieConsentPanel() {
  const [data, setData] = useState({ consents: [], total: 0, granted: 0, refused: 0 });
  const [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try { setData(await api("/admin/cookie-consents?limit=200")); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="cookie-consent-panel">
      <div className="grid grid-cols-3 gap-4 mb-5">
        <Kpi Icon={Cookie} label="Consentements" value={data.total} />
        <Kpi Icon={Check} label="Mesure acceptée" value={data.granted} />
        <Kpi Icon={X} label="Mesure refusée" value={data.refused} />
      </div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-semibold text-findo-navy">Journal des consentements cookies</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              const res = await fetch(`${API}/admin/cookie-consents/export.csv`, { headers: { Authorization: `Bearer ${getToken()}` } });
              const blob = await res.blob(); const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = "findloop-consentements-cookies.csv"; a.click(); URL.revokeObjectURL(url);
            } catch (e) { toast.error(e.message); }
          }} className="rounded-full font-semibold" data-testid="cookie-consent-export"><Download size={14} className="mr-1.5" /> Export CSV</Button>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="cookie-consent-refresh">Rafraîchir</Button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden">
        {loading ? (
          <PageLoader label="Chargement…" className="p-8" size={32} />
        ) : data.consents.length === 0 ? (
          <p className="p-8 text-center text-slate-400 text-sm">Aucun consentement enregistré.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.consents.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-3.5 text-sm" data-testid="cookie-consent-row">
                <span className="text-slate-500 tabular-nums w-40 flex-shrink-0">{c.at ? new Date(c.at).toLocaleString("fr-FR") : "—"}</span>
                <Badge className={c.measurement ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                  Mesure : {c.measurement ? "acceptée" : "refusée"}
                </Badge>
                <Badge className="bg-findo-navy/10 text-findo-navy">Nécessaires</Badge>
                {c.lang && <span className="uppercase text-xs font-semibold text-slate-400">{c.lang}</span>}
                <span className="flex-1 text-slate-400 truncate text-xs">{c.ua}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SignedQuotesPanel() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("signed");
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = (st = status, query = q) => api(`/admin/signed-quotes?status=${st}&q=${encodeURIComponent(query)}`).then(setData).catch(() => setData({ quotes: [], counts: {} }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Deep-link from the "lié au devis" badge in the Aperçus journal.
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus) {
      setStatus("all"); setQ(focus); load("all", focus);
      const next = new URLSearchParams(searchParams); next.delete("focus"); setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  async function exportCsv() {
    try {
      const res = await fetch(`${API}/admin/signed-quotes/export.csv?status=${status}&q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findloop-devis-signes.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch { toast.error("Export impossible"); }
  }

  const counts = data?.counts || {};
  const fmt = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const STATUSES = [["signed", "Signé"], ["pending", "En attente"], ["archived", "Archivé"], ["all", "Tous"]];
  const daysTo = (iso) => (iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000) : null);

  return (
    <div data-testid="signed-quotes-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-display text-xl font-semibold text-findo-navy">Devis signés & contre-signés</h3>
          <p className="text-sm text-slate-500">Stockage des devis contre-signés — classés par date décroissante.</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer par partenaire…" className="h-10 rounded-full border border-border px-4 text-sm w-56" data-testid="signed-quotes-search" />
          <Button type="submit" size="sm" className="rounded-full font-semibold" data-testid="signed-quotes-search-btn">Rechercher</Button>
          <Button type="button" size="sm" variant="outline" onClick={exportCsv} disabled={!data || data.quotes.length === 0} className="rounded-full font-semibold gap-1.5" data-testid="signed-quotes-export-btn">
            <Download size={14} /> Exporter CSV
          </Button>
        </form>
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="signed-quotes-filters">
        {STATUSES.map(([k, label]) => (
          <button key={k} onClick={() => setStatus(k)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${status === k ? "bg-findo-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`signed-quotes-tab-${k}`}>
            {label}{k !== "all" && counts[k] != null ? ` (${counts[k]})` : ""}
          </button>
        ))}
      </div>
      {!data ? (
        <PageLoader label="Chargement…" className="py-8" size={32} />
      ) : data.quotes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-10 text-center text-slate-400" data-testid="signed-quotes-empty">Aucun devis pour ce filtre.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="signed-quotes-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Partenaire</th>
                <th className="text-left px-4 py-3">N° devis</th>
                <th className="text-left px-4 py-3">Date de signature</th>
                <th className="text-left px-4 py-3">Statut</th>
                <th className="text-left px-4 py-3">Échéance contrat</th>
                <th className="text-right px-4 py-3">Document</th>
              </tr>
            </thead>
            <tbody>
              {data.quotes.map((r) => (
                <React.Fragment key={r.id}>
                <tr className="border-t border-border" data-testid="signed-quote-row">
                  <td className="px-4 py-3 font-semibold text-findo-navy">{r.partner}<div className="text-xs font-normal text-slate-400">{r.email}</div></td>
                  <td className="px-4 py-3 text-slate-600">{r.number || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.signed_at)}{r.countersigned ? <span className="ml-1.5 text-xs text-emerald-600 font-semibold">· contre-signé</span> : ""}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{r.quote_status}</span></td>
                  <td className="px-4 py-3" data-testid={`signed-quote-expiry-${r.id}`}>
                    {r.contract_expiry ? (() => {
                      const dl = daysTo(r.contract_expiry);
                      const expired = dl != null && dl < 0;
                      const soon = dl != null && dl >= 0 && dl <= 30;
                      return (
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${expired ? "bg-red-100 text-findo-danger" : soon ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                          {fmt(r.contract_expiry)}{expired ? " · expiré" : soon ? ` · J-${dl}` : ""}
                        </span>
                      );
                    })() : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.access_token ? (
                      <a href={`${API}/quotes/${r.access_token}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-findo-navy font-semibold hover:underline" data-testid={`signed-quote-download-${r.id}`}>
                        <Download size={15} /> PDF
                      </a>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
                {Array.isArray(r.quote_history) && r.quote_history.length > 0 && (
                  <tr className="border-t-0" data-testid={`signed-quote-history-row-${r.id}`}>
                    <td colSpan={6} className="px-4 pb-3 pt-0 bg-slate-50/40">
                      <QuoteHistory history={r.quote_history} currency={r.quote?.currency || "EUR"} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function QuotesPanel() {
  const [status, setStatus] = useState("all");
  const [data, setData] = useState({ quotes: [], total: 0, counts: {} });

  const load = () => {
    const qs = status === "all" ? "" : `?status=${status}`;
    api(`/admin/quote-requests${qs}`).then(setData).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  async function setQuoteStatus(id, s) {
    try { await api(`/admin/quote-requests/${id}`, { method: "PATCH", body: { status: s } }); load(); toast.success("Statut mis à jour"); }
    catch (e) { toast.error(e.message); }
  }

  async function runReminders() {
    try {
      const r = await api(`/admin/quote-requests/run-reminders`, { method: "POST" });
      toast.success(`${r.reminders_sent || 0} relance(s) envoyée(s)`);
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function counterDecision(id, accept) {
    try {
      await api(`/admin/quote-requests/${id}/counter-decision`, { method: "POST", body: { accept } });
      toast.success(accept ? "Offre acceptée — contrat envoyé pour signature" : "Offre refusée");
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function countersign(id, name, months) {
    try {
      await api(`/admin/quote-requests/${id}/countersign`, { method: "POST", body: { name: name.trim(), contract_months: Number(months) } });
      toast.success("Document contre-signé par findloop");
      load();
    } catch (e) { toast.error(e.message); }
  }

  const [countersignTarget, setCountersignTarget] = useState(null);

  async function activatePlan(q) {
    const val = window.prompt(`Activer un plan sur mesure (Grands comptes) pour ${q.organization}.\nMontant mensuel HT en € (auto-facturé chaque mois) :`, q.custom_monthly_eur || "");
    if (val === null) return;
    const monthly = Number(val);
    if (!(monthly >= 0)) { toast.error("Montant invalide"); return; }
    try {
      const r = await api(`/admin/quote-requests/${q.id}/activate-plan`, { method: "POST", body: { monthly_eur: monthly } });
      toast.success(`Plan sur mesure activé pour ${r.email} (${r.monthly_eur} €/mois)`);
      load();
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div data-testid="quotes-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", "new", "contacted", "closed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`h-9 rounded-full px-4 text-sm font-semibold transition-colors ${status === s ? "bg-findo-navy text-white" : "bg-slate-100 text-slate-500 hover:text-findo-navy"}`}
            data-testid={`quotes-filter-${s}`}>
            {s === "all" ? "Tous" : QUOTE_STATUS[s].label}
            {s !== "all" && data.counts?.[s] != null && <span className="ml-1.5 opacity-70">{data.counts[s]}</span>}
          </button>
        ))}
        <Button size="sm" variant="outline" onClick={runReminders} className="rounded-full ml-auto text-findo-navy" data-testid="quotes-run-reminders">
          <Send size={14} className="mr-1.5" /> Relancer les devis en attente
        </Button>
        <span className="text-xs text-slate-400" data-testid="quotes-count">{data.total} demande(s)</span>
      </div>

      <div className="space-y-3" data-testid="quotes-list">
        {data.quotes.map((q) => (
          <div key={q.id} className="bg-white rounded-2xl border border-border card-shadow p-5" data-testid="quote-row">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-findo-navy">{q.organization}</span>
                  <Badge className={QUOTE_STATUS[q.status]?.cls || "bg-slate-100"}>{QUOTE_STATUS[q.status]?.label || q.status}</Badge>
                  <Badge className="bg-slate-100 text-slate-600">{q.region === "afrique" ? "Afrique" : "Europe"}</Badge>
                  {q.sites != null && <Badge className="bg-slate-100 text-slate-600">{q.sites} site(s)</Badge>}
                  {q.reminder_sent_at && <Badge className="bg-amber-100 text-amber-700">Relancé</Badge>}
                </div>
                <p className="text-sm text-slate-500 mt-1">{q.name} · <a href={`mailto:${q.email}`} className="text-findo-red hover:underline">{q.email}</a></p>
                {q.segment && <p className="text-xs text-slate-400 mt-0.5">{q.segment}</p>}
                {q.message && <p className="text-sm text-slate-600 mt-2 bg-findo-cream/50 rounded-xl p-3">{q.message}</p>}
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap">{q.created_at ? new Date(q.created_at).toLocaleDateString("fr-FR") : ""}</span>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap items-center">
              {q.status !== "contacted" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setQuoteStatus(q.id, "contacted")} data-testid="quote-mark-contacted">Marquer contacté</Button>}
              {q.status !== "closed" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setQuoteStatus(q.id, "closed")} data-testid="quote-mark-closed">Clôturer</Button>}
              {q.status !== "new" && <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => setQuoteStatus(q.id, "new")} data-testid="quote-mark-new">Rouvrir</Button>}
              <QuoteEditor request={q} onDone={load} />
              <Button size="sm" className="rounded-full bg-findo-turquoise hover:bg-findo-turquoise/90 text-white" onClick={() => activatePlan(q)} data-testid="quote-activate-plan">
                Activer plan sur mesure
              </Button>
              {q.access_token && (
                <a href={`/devis/${q.access_token}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-findo-indigo hover:underline" data-testid="quote-open-link">Voir le devis</a>
              )}
            </div>
            {q.quote_status && (
              <div className="mt-3 rounded-xl bg-slate-50 border border-border p-3" data-testid="quote-wf">
                <p className="text-xs font-semibold text-findo-navy">
                  Devis {q.quote?.number} · <span data-testid="quote-wf-status">{QUOTE_WF[q.quote_status] || q.quote_status}</span>
                </p>
                {q.quote_status === "countered" && q.counter_offer && (
                  <div className="mt-2" data-testid="quote-counter-block">
                    <p className="text-sm text-amber-700 font-semibold">Offre du partenaire : {Number(q.counter_offer.amount).toFixed(2)} € (initial : {Number(q.quote?.totals?.total_ttc || 0).toFixed(2)} €)</p>
                    {q.counter_offer.message && <p className="text-xs text-slate-500 italic mt-0.5">« {q.counter_offer.message} »</p>}
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="rounded-full bg-findo-navy" onClick={() => counterDecision(q.id, true)} data-testid="quote-accept-offer">Accepter l'offre</Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => counterDecision(q.id, false)} data-testid="quote-refuse-offer">Refuser l'offre</Button>
                    </div>
                  </div>
                )}
                {q.signature && <p className="text-xs text-emerald-600 mt-1">✔ Signé par {q.signature.name} le {String(q.signature.signed_at).slice(0, 10)}</p>}
                {(q.quote_status === "signed" || q.quote_status === "contract_signed") && (
                  q.findo_signature ? (
                    <p className="text-xs text-findo-navy font-semibold mt-1" data-testid="quote-findo-signed">🖋 Contre-signé par findloop · {q.findo_signature.name} le {String(q.findo_signature.signed_at).slice(0, 10)}</p>
                  ) : (
                    <Button size="sm" className="rounded-full bg-findo-navy mt-2" onClick={() => setCountersignTarget(q.id)} data-testid="quote-countersign-btn">
                      🖋 Contre-signer (findloop)
                    </Button>
                  )
                )}
                {Array.isArray(q.quote_history) && q.quote_history.length > 0 && (
                  <QuoteHistory history={q.quote_history} currency={q.quote?.currency} />
                )}
              </div>
            )}
          </div>
        ))}
        {data.quotes.length === 0 && <p className="p-8 text-center text-slate-400 text-sm bg-white rounded-2xl border border-border">Aucune demande de devis.</p>}
      </div>

      <CountersignDialog
        open={!!countersignTarget}
        onClose={() => setCountersignTarget(null)}
        onConfirm={async (name, months) => { await countersign(countersignTarget, name, months); setCountersignTarget(null); }}
      />
    </div>
  );
}

function CountersignDialog({ open, onClose, onConfirm }) {
  const [name, setName] = useState("");
  const [months, setMonths] = useState("12");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(""); setMonths("12"); setConsent(false); } }, [open]);
  const monthsNum = Number(months);
  const monthsValid = Number.isInteger(monthsNum) && monthsNum >= 1 && monthsNum <= 120;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="countersign-dialog">
        <DialogHeader>
          <DialogTitle>Contre-signature findloop</DialogTitle>
          <DialogDescription>
            Vous vous apprêtez à contre-signer ce document au nom de findloop. Cet acte engage findloop et rend le contrat bilatéral. La date est enregistrée automatiquement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nom du signataire (findloop)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Direction findloop" className="mt-1.5 rounded-xl" data-testid="countersign-name" />
          </div>
          <div>
            <Label>Durée du contrat (en mois) *</Label>
            <Input type="number" min={1} max={120} value={months} onChange={(e) => setMonths(e.target.value)} placeholder="ex : 12" className="mt-1.5 rounded-xl" data-testid="countersign-months" />
            <p className="text-xs text-slate-400 mt-1">L'échéance sera calculée à partir de la date de signature du client.</p>
            {!monthsValid && months !== "" && <p className="text-xs text-findo-danger mt-1" data-testid="countersign-months-error">Saisissez une durée entre 1 et 120 mois.</p>}
          </div>
          <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-findo-navy" data-testid="countersign-consent" />
            <span>Je certifie être habilité à contre-signer ce document au nom de findloop (valeur d'engagement).</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="countersign-cancel">Annuler</Button>
          <Button
            disabled={busy || name.trim().length < 2 || !consent || !monthsValid}
            onClick={async () => { setBusy(true); try { await onConfirm(name, monthsNum); } finally { setBusy(false); } }}
            className="rounded-full bg-findo-navy font-semibold"
            data-testid="countersign-submit">
            {busy ? <FindoSpinner size={16} /> : "Contre-signer maintenant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PurgeTestDataButton() {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);

  async function openDialog() {
    try {
      const r = await api("/admin/purge-test-data?dry_run=true", { method: "POST" });
      setCounts(r.counts);
      setOpen(true);
    } catch (e) { toast.error(e.message); }
  }
  async function confirm() {
    setBusy(true);
    try {
      await api("/admin/purge-test-data?dry_run=false", { method: "POST" });
      toast.success("Données de test supprimées");
      setOpen(false);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <>
      <Button variant="outline" onClick={openDialog} className="h-10 rounded-full font-semibold text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="purge-test-btn">
        <AlertTriangle size={15} className="mr-1.5" /> Purger les données de test
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Purger les données de test ?</AlertDialogTitle>
            <AlertDialogDescription>
              Supprime définitivement les comptes et annonces de test (préfixe « TEST_ » ou email @example.com) et leurs entrées de journal. Les annonces de démonstration (seed) sont conservées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {counts && (
            <div className="rounded-xl bg-slate-50 border border-border p-4 text-sm space-y-1" data-testid="purge-counts">
              <p className="flex justify-between"><span className="text-slate-500">Utilisateurs B2C</span><b>{counts.b2c_users}</b></p>
              <p className="flex justify-between"><span className="text-slate-500">Partenaires</span><b>{counts.partners}</b></p>
              <p className="flex justify-between"><span className="text-slate-500">Annonces</span><b>{counts.public_ads}</b></p>
              <p className="flex justify-between"><span className="text-slate-500">Entrées de journal</span><b>{counts.audit_logs}</b></p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} disabled={busy || total === 0} className="bg-red-600 hover:bg-red-700" data-testid="purge-confirm">
              {busy ? <FindoSpinner size={16} /> : `Supprimer (${total})`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const QUOTE_WF = {
  sent: "Devis envoyé — en attente",
  signed: "✔ Accepté et signé",
  declined: "Décliné par le partenaire",
  countered: "Offre reçue — à traiter",
  counter_accepted: "Offre acceptée — en attente de signature",
  counter_refused: "Offre refusée",
  contract_signed: "✔ Contrat signé",
};

const QUOTE_EVENT = {
  sent: "Devis envoyé au partenaire",
  signed: "Devis accepté et signé",
  declined: "Devis décliné",
  countered: "Contre-offre du partenaire",
  counter_accepted: "Contre-offre acceptée",
  counter_refused: "Contre-offre refusée",
  contract_signed: "Contrat signé",
  contract_renewed: "Contrat renouvelé",
  findo_countersigned: "Contre-signé par findloop",
  apercu_accepted: "Aperçu de facturation accepté",
};

function QuoteHistory({ history, currency = "EUR" }) {
  const [open, setOpen] = useState(false);
  const sorted = [...history].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  return (
    <div className="mt-3 border-t border-border pt-2" data-testid="quote-history">
      <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-findo-indigo hover:underline" data-testid="quote-history-toggle">
        {open ? "Masquer" : "Afficher"} le journal du devis ({sorted.length})
      </button>
      {open && (
        <ol className="mt-2 space-y-2" data-testid="quote-history-list">
          {sorted.map((h, i) => (
            <li key={i} className="flex items-start gap-2.5 text-xs" data-testid="quote-history-item">
              <span className="mt-1 h-2 w-2 rounded-full bg-findo-navy flex-shrink-0" />
              <div>
                <p className="font-semibold text-findo-navy">{QUOTE_EVENT[h.event] || h.event}</p>
                <p className="text-slate-400">
                  {h.at ? new Date(h.at).toLocaleString("fr-FR") : ""}
                  {h.by ? ` · ${h.by}` : ""}
                  {h.amount != null ? ` · ${Number(h.amount).toFixed(2)} ${currency}` : ""}
                  {h.event === "contract_renewed" && h.months ? ` · +${h.months} mois` : ""}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const emptyItem = () => ({ description: "", qty: 1, unit_ht: 0, tva_pct: 20 });

function QuoteEditor({ request, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const existing = request.quote;
  const [company, setCompany] = useState(existing?.company || { name: "findloop", address: "", email: "contact@findloop.io", phone: "" });
  const [client, setClient] = useState(existing?.client || { org: request.organization || "", name: request.name || "", email: request.email || "", phone: "", address: "" });
  const [items, setItems] = useState(existing?.items?.map((i) => ({ description: i.description, qty: i.qty, unit_ht: i.unit_ht, tva_pct: i.tva_pct })) || [emptyItem()]);
  const [terms, setTerms] = useState(existing?.payment_terms || "Paiement à 30 jours à réception de facture.");
  const [notes, setNotes] = useState(existing?.notes || "");

  const totalTTC = items.reduce((s, it) => {
    const ht = (Number(it.qty) || 0) * (Number(it.unit_ht) || 0);
    return s + ht + ht * (Number(it.tva_pct) || 0) / 100;
  }, 0);

  function setItem(i, k, v) { setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it)); }

  async function send() {
    if (!items.length || items.some((it) => !it.description.trim())) return toast.error("Complétez les prestations.");
    setBusy(true);
    try {
      await api(`/admin/quote-requests/${request.id}/issue`, {
        method: "POST",
        body: { company, client, items, payment_terms: terms, notes, validity_days: 30, currency: "EUR" },
      });
      toast.success("Devis envoyé au demandeur ✉️");
      setOpen(false); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <>
      <Button size="sm" className="rounded-full bg-findo-red hover:bg-findo-red/90" onClick={() => setOpen(true)} data-testid="quote-editor-open">
        {existing ? "Modifier / renvoyer le devis" : "Créer le devis"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="quote-editor-dialog">
          <DialogHeader>
            <DialogTitle>Devis pour {request.organization}</DialogTitle>
            <DialogDescription>Complétez le devis puis envoyez-le au demandeur par e-mail.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-border p-3 space-y-2">
                <p className="text-xs font-semibold text-findo-navy">Émetteur (findloop)</p>
                <Input placeholder="Nom" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} className="h-9 rounded-lg" data-testid="qe-company-name" />
                <Input placeholder="Adresse" value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} className="h-9 rounded-lg" data-testid="qe-company-address" />
                <Input placeholder="Email" value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} className="h-9 rounded-lg" />
                <Input placeholder="Téléphone" value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} className="h-9 rounded-lg" />
              </div>
              <div className="rounded-xl border border-border p-3 space-y-2">
                <p className="text-xs font-semibold text-findo-navy">Client</p>
                <Input placeholder="Organisation" value={client.org} onChange={(e) => setClient({ ...client, org: e.target.value })} className="h-9 rounded-lg" data-testid="qe-client-org" />
                <Input placeholder="Contact" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} className="h-9 rounded-lg" />
                <Input placeholder="Email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} className="h-9 rounded-lg" data-testid="qe-client-email" />
                <Input placeholder="Téléphone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} className="h-9 rounded-lg" />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-findo-navy mb-1.5">Prestations</p>
              <div className="space-y-2" data-testid="qe-items">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <Input placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="col-span-5 h-9 rounded-lg" data-testid={`qe-item-desc-${i}`} />
                    <Input type="number" placeholder="Qté" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="col-span-2 h-9 rounded-lg" />
                    <Input type="number" placeholder="PU HT" value={it.unit_ht} onChange={(e) => setItem(i, "unit_ht", e.target.value)} className="col-span-2 h-9 rounded-lg" data-testid={`qe-item-price-${i}`} />
                    <Input type="number" placeholder="TVA%" value={it.tva_pct} onChange={(e) => setItem(i, "tva_pct", e.target.value)} className="col-span-2 h-9 rounded-lg" />
                    <button onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="col-span-1 text-slate-400 hover:text-findo-danger" data-testid={`qe-item-remove-${i}`}>✕</button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" className="rounded-full mt-2" onClick={() => setItems([...items, emptyItem()])} data-testid="qe-add-item">+ Ajouter une ligne</Button>
              <p className="text-right text-sm font-bold text-findo-navy mt-2" data-testid="qe-total">Total TTC : {totalTTC.toFixed(2)} €</p>
            </div>

            <div><Label className="text-xs">Conditions de paiement</Label><Input value={terms} onChange={(e) => setTerms(e.target.value)} className="mt-1 h-9 rounded-lg" /></div>
            <div><Label className="text-xs">Notes (optionnel)</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 h-9 rounded-lg" /></div>

            <Button onClick={send} disabled={busy} className="w-full rounded-full font-semibold bg-findo-navy" data-testid="qe-send">
              {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Envoi…</span> : "Envoyer le devis au demandeur"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const KPI_TONES = {
  indigo: { bg: "bg-findo-indigo/10", text: "text-findo-indigo", bar: "from-findo-indigo/60 to-findo-indigo" },
  teal: { bg: "bg-teal-100", text: "text-teal-600", bar: "from-teal-400 to-teal-600" },
  amber: { bg: "bg-amber-100", text: "text-amber-600", bar: "from-amber-400 to-amber-600" },
  rose: { bg: "bg-rose-100", text: "text-rose-600", bar: "from-rose-400 to-rose-600" },
};

function ExpiringContractsBanner({ canView, onGo }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (canView) api("/admin/contracts/expiring-count").then(setD).catch(() => {}); }, [canView]);
  if (!canView || !d || (d.expiring_soon + d.expired) === 0) return null;
  const s = d.expiring_soon;
  return (
    <button onClick={onGo} data-testid="expiring-contracts-banner"
      className="w-full text-left mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-center gap-3 hover:bg-amber-100 transition-colors">
      <CalendarClock size={20} className="text-amber-600 flex-shrink-0" />
      <div className="flex-1">
        <p className="font-display font-semibold text-amber-800" data-testid="expiring-contracts-count">
          {s} contrat{s > 1 ? "s" : ""} arrive{s > 1 ? "nt" : ""} à échéance sous {d.days} jours
          {d.expired > 0 ? ` · ${d.expired} déjà expiré${d.expired > 1 ? "s" : ""}` : ""}
        </p>
        <p className="text-xs text-amber-700 mt-0.5">Cliquez pour voir les devis signés et relancer le renouvellement.</p>
      </div>
      <ChevronRight size={18} className="text-amber-600" />
    </button>
  );
}

const fmtEur = (n) => (n != null ? Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 2 }) : "—");

function MonthTrend({ pct, prev }) {
  if (prev == null) return null;
  if (pct == null) return <p className="mt-1 text-xs text-slate-400" data-testid="kpi-revenue-trend">Mois dernier : 0 €</p>;
  const up = pct >= 0;
  return (
    <div className="mt-1 text-xs" data-testid="kpi-revenue-trend">
      <span className={`inline-flex items-center gap-1 font-semibold whitespace-nowrap ${up ? "text-emerald-600" : "text-rose-600"}`}>
        {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {up ? "+" : ""}{pct} %
      </span>
      <span className="block text-slate-400">vs mois dernier ({fmtEur(prev)} €)</span>
    </div>
  );
}

function Kpi({ Icon, label, value, tone = "indigo", sub, testid }) {
  const t = KPI_TONES[tone] || KPI_TONES.indigo;
  return (
    <div className="relative bg-white rounded-2xl border border-border p-5 card-shadow overflow-hidden transition-transform hover:-translate-y-0.5">
      <span className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${t.bar}`} />
      <span className={`h-10 w-10 grid place-items-center rounded-xl ${t.bg} ${t.text}`}><Icon size={20} /></span>
      <p className="mt-3 text-2xl font-display font-extrabold text-slate-900" data-testid={testid}>{value}</p>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      {sub}
    </div>
  );
}


function NotificationCenter({ onNavigate, onKpi, onStatus }) {
  const [data, setData] = useState({ notifications: [], unread: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const seenIds = React.useRef(null);

  const load = () => api("/admin/notifications?limit=50").then((d) => {
    // Fire a browser notification for ANY genuinely new alert (background tab too).
    if (seenIds.current !== null && typeof Notification !== "undefined" && Notification.permission === "granted") {
      d.notifications
        .filter((n) => !seenIds.current.has(n.id))
        .forEach((n) => { try { new Notification(n.title, { body: n.body }); } catch { /* ignore */ } });
    }
    seenIds.current = new Set(d.notifications.map((n) => n.id));
    setData(d);
  }).catch(() => {});

  useEffect(() => {
    load();  // initial snapshot
    let ws = null, pollId = null, reconnectTimer = null, closed = false, backoff = 1000;
    const startPoll = () => { if (!pollId) pollId = setInterval(load, 30000); };
    const stopPoll = () => { if (pollId) { clearInterval(pollId); pollId = null; } };
    const connect = () => {
      try {
        const wsUrl = `${API.replace(/^http/, "ws")}/admin/ws/notifications?token=${getToken()}`;
        ws = new WebSocket(wsUrl);
        ws.onopen = () => { stopPoll(); backoff = 1000; if (onStatus) onStatus(true); };
        ws.onmessage = (ev) => {
          let data = null;
          try { data = JSON.parse(ev.data); } catch { /* ignore */ }
          if (data && data.event === "kpi") { if (onKpi) onKpi(); return; }
          load();  // load() handles list refresh + browser notification
        };
        ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
        ws.onclose = () => {
          if (!closed) {
            if (onStatus) onStatus(false);
            startPoll();  // fallback polling while disconnected
            reconnectTimer = setTimeout(connect, backoff);
            backoff = Math.min(backoff * 2, 30000);  // exponential backoff
          }
        };
      } catch { startPoll(); if (onStatus) onStatus(false); }
    };
    connect();
    return () => {
      closed = true; stopPoll();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch { /* ignore */ } }
    };
  }, []);

  async function enableBrowser() {
    if (typeof Notification === "undefined") return;
    try { setPerm(await Notification.requestPermission()); } catch { /* ignore */ }
  }

  async function markRead(n) {
    if (!n.read) {
      try { await api(`/admin/notifications/${n.id}/read`, { method: "POST" }); } catch { /* ignore */ }
    }
    if (onNavigate && ["devis", "invoice", "report", "sendcloud_mismatch", "server_memory", "stripe_webhook_silent", "stripe_reconciled"].includes(n.type)) { setOpen(false); onNavigate(n); }
    load();
  }
  async function markAll() {
    try { await api("/admin/notifications/read-all", { method: "POST" }); } catch { /* ignore */ }
    load();
  }
  async function exportCsv() {
    try {
      const res = await fetch(`${API}/admin/notifications/export.csv`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "findloop-notifications.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }

  const META = {
    quota: { icon: <Mail size={15} />, cls: "bg-findo-red/10 text-findo-red" },
    devis: { icon: <FileText size={15} />, cls: "bg-findo-indigo/10 text-findo-indigo" },
    invoice: { icon: <Euro size={15} />, cls: "bg-amber-100 text-amber-700" },
    report: { icon: <Flag size={15} />, cls: "bg-findo-red/10 text-findo-red" },
    digest: { icon: <TrendingUp size={15} />, cls: "bg-emerald-100 text-emerald-700" },
    sendcloud_mismatch: { icon: <Truck size={15} />, cls: "bg-findo-red/10 text-findo-red" },
    server_memory: { icon: <AlertTriangle size={15} />, cls: "bg-findo-red/10 text-findo-red" },
    stripe_webhook_silent: { icon: <WifiOff size={15} />, cls: "bg-findo-red/10 text-findo-red" },
    stripe_reconciled: { icon: <RefreshCw size={15} />, cls: "bg-emerald-100 text-emerald-700" },
  };
  const metaFor = (t) => META[t] || { icon: <BellRing size={15} />, cls: "bg-slate-100 text-slate-600" };

  const FILTERS = [["all", "Tous"], ["quota", "Quota"], ["devis", "Devis"], ["invoice", "Factures"], ["report", "Annonces"]];
  const counts = data.notifications.reduce((a, n) => { a[n.type] = (a[n.type] || 0) + 1; return a; }, {});
  const visible = filter === "all" ? data.notifications : data.notifications.filter((n) => n.type === filter);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full font-semibold relative" data-testid="notif-bell">
          <Bell size={15} />
          {data.unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-findo-red text-white text-[10px] font-bold" data-testid="notif-unread-badge">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-2xl overflow-hidden" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-display font-semibold text-findo-navy">Notifications</p>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} className="text-xs font-semibold text-findo-indigo flex items-center gap-1" data-testid="notif-export-csv"><Download size={13} /> CSV</button>
            {data.unread > 0 && (
              <button onClick={markAll} className="text-xs font-semibold text-findo-red" data-testid="notif-mark-all">Tout marquer lu</button>
            )}
          </div>
        </div>
        <div className="flex gap-1 px-3 py-2 border-b border-border overflow-x-auto" data-testid="notif-filters">
          {FILTERS.map(([k, label]) => {
            const c = k === "all" ? data.notifications.length : (counts[k] || 0);
            return (
              <button key={k} onClick={() => setFilter(k)} data-testid={`notif-filter-${k}`}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${filter === k ? "bg-findo-navy text-white" : "bg-slate-100 text-findo-navy hover:bg-slate-200"}`}>
                {label}{c > 0 ? ` (${c})` : ""}
              </button>
            );
          })}
        </div>
        {typeof Notification !== "undefined" && perm !== "granted" && (
          <button onClick={enableBrowser} className="w-full px-4 py-2 text-xs font-semibold text-findo-indigo bg-findo-indigo/5 hover:bg-findo-indigo/10 border-b border-border flex items-center gap-1.5" data-testid="notif-enable-browser">
            <Bell size={13} /> Activer les alertes navigateur (temps réel)
          </button>
        )}
        <div className="max-h-96 overflow-y-auto divide-y divide-border">
          {visible.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notif-empty">Aucune notification.</p>
          ) : visible.map((n) => {
            const mt = metaFor(n.type);
            return (
              <button key={n.id} onClick={() => markRead(n)} data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 transition-colors ${n.read ? "opacity-60" : ""}`}>
                <span className={`h-8 w-8 flex-shrink-0 grid place-items-center rounded-lg ${mt.cls}`}>{mt.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900 truncate">{n.title}</span>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-findo-red flex-shrink-0" />}
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5">{n.body}</span>
                  <span className="block text-[11px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString("fr-FR")}</span>
                </span>
              </button>
            );
          })}
        </div>
        <DigestSettings />
      </PopoverContent>
    </Popover>
  );
}

function DigestSettings() {
  const CATS = [["devis", "Devis"], ["invoice", "Factures"], ["report", "Annonces"]];
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && cfg === null) {
      try { setCfg(await api("/admin/digest-settings")); } catch { setCfg({ hour: 0, categories: ["devis", "invoice", "report"] }); }
    }
  }
  function toggleCat(c) {
    setCfg((p) => {
      const has = p.categories.includes(c);
      const categories = has ? p.categories.filter((x) => x !== c) : [...p.categories, c];
      return { ...p, categories: categories.length ? categories : p.categories };
    });
  }
  async function save() {
    setBusy(true);
    try {
      const r = await api("/admin/digest-settings", { method: "PUT", body: { hour: Number(cfg.hour), categories: cfg.categories } });
      setCfg(r);
      toast.success("Résumé quotidien enregistré");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="border-t border-border">
      <button onClick={toggle} className="w-full px-4 py-2.5 flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="digest-settings-toggle">
        <Settings size={13} /> Résumé quotidien {open ? "▲" : "▼"}
      </button>
      {open && cfg && (
        <div className="px-4 pb-3 space-y-2.5" data-testid="digest-settings">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Envoi à partir de :</span>
            <select value={cfg.hour} onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })}
              className="h-8 rounded-lg border border-border px-2 text-xs font-semibold text-findo-navy" data-testid="digest-hour">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}h UTC</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CATS.map(([k, label]) => (
              <button key={k} onClick={() => toggleCat(k)} data-testid={`digest-cat-${k}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${cfg.categories.includes(k) ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-500 border-border"}`}>
                {label}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={save} disabled={busy} className="rounded-full h-8 text-xs w-full" data-testid="digest-save">Enregistrer le résumé</Button>
        </div>
      )}
    </div>
  );
}

function EmailQuotaCard({ quota, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  const [phone, setPhone] = useState("");
  const [editingPhone, setEditingPhone] = useState(false);

  useEffect(() => { if (quota) { setVal(String(quota.limit)); setPhone(quota.alert_phone || ""); } }, [quota]);
  useEffect(() => { api("/admin/email-quota/history?months=6").then(setHist).catch(() => {}); }, []);
  if (!quota) return null;

  const pct = Math.min(100, quota.pct || 0);
  const warn = pct >= (quota.alert_pct || 90);
  const barColor = warn ? "#E4232E" : pct >= 60 ? "#E9A23B" : "#00A389";

  async function save() {
    const n = parseInt(val, 10);
    if (!Number.isFinite(n) || n < 1) { toast.error("Limite invalide"); return; }
    setBusy(true);
    try {
      const r = await api("/admin/email-quota", { method: "PUT", body: { limit: n } });
      const fresh = await api("/admin/email-quota");
      onSaved(fresh);
      api("/admin/email-quota/history?months=6").then(setHist).catch(() => {});
      setEditing(false);
      toast.success(`Limite mise à jour : ${r.limit}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function saveAlertPct(pct) {
    try {
      await api("/admin/email-quota/alert-pct", { method: "PUT", body: { pct: Number(pct) } });
      const fresh = await api("/admin/email-quota");
      onSaved(fresh);
      toast.success(`Seuil d'alerte : ${pct}%`);
    } catch (e) { toast.error(e.message); }
  }

  async function saveAlertPhone() {
    try {
      await api("/admin/email-quota/alert-phone", { method: "PUT", body: { phone: phone.trim() } });
      const fresh = await api("/admin/email-quota");
      onSaved(fresh);
      setEditingPhone(false);
      toast.success(phone.trim() ? "Numéro SMS enregistré" : "Numéro SMS retiré");
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="bg-white rounded-2xl border border-border p-5 card-shadow mb-6" data-testid="email-quota-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`h-10 w-10 grid place-items-center rounded-xl ${warn ? "bg-findo-danger/10 text-findo-danger" : "bg-findo-indigo/10 text-findo-indigo"}`}><Mail size={20} /></span>
          <div>
            <p className="text-sm font-semibold text-slate-900">Quota d'emails (B2B + B2C) — {quota.period}</p>
            <p className="text-xs text-slate-500">
              <span data-testid="email-quota-sent">{quota.sent}</span> envoyés ·{" "}
              <span data-testid="email-quota-remaining" className="font-semibold">{quota.remaining}</span> restants sur{" "}
              <span data-testid="email-quota-limit">{quota.limit}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {warn && <Badge className="bg-findo-danger text-white" data-testid="email-quota-warn">Seuil {quota.alert_pct}% atteint</Badge>}
          {editing ? (
            <>
              <Input type="number" min={1} value={val} onChange={(e) => setVal(e.target.value)} className="h-9 w-28 rounded-lg" data-testid="email-quota-input" />
              <Button size="sm" onClick={save} disabled={busy} className="rounded-full" data-testid="email-quota-save">Enregistrer</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="rounded-full">Annuler</Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-full font-semibold" data-testid="email-quota-edit">Modifier la limite</Button>
          )}
        </div>
      </div>
      <div className="mt-3 h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} data-testid="email-quota-bar" />
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
        <span>Seuil d'alerte :</span>
        <select value={quota.alert_pct || 90} onChange={(e) => saveAlertPct(e.target.value)}
          className="h-8 rounded-lg border border-border px-2 text-xs font-semibold text-findo-navy" data-testid="email-quota-alert-pct">
          <option value={80}>80 %</option>
          <option value={90}>90 %</option>
          <option value={95}>95 %</option>
        </select>
        <span className="text-slate-400">de la limite déclenche l'alerte superadmin</span>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
        <span>SMS d'alerte critique (Twilio) :</span>
        {editingPhone ? (
          <>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33612345678"
              className="h-8 w-40 rounded-lg text-xs" data-testid="email-quota-alert-phone-input" />
            <Button size="sm" onClick={saveAlertPhone} className="rounded-full h-8 text-xs" data-testid="email-quota-alert-phone-save">Enregistrer</Button>
            <button onClick={() => { setPhone(quota.alert_phone || ""); setEditingPhone(false); }} className="text-slate-400">Annuler</button>
          </>
        ) : (
          <>
            <span className="font-semibold text-findo-navy" data-testid="email-quota-alert-phone">{quota.alert_phone || "aucun"}</span>
            <button onClick={() => setEditingPhone(true)} className="font-semibold text-findo-indigo" data-testid="email-quota-alert-phone-edit">Modifier</button>
          </>
        )}
      </div>
      {hist && hist.points && (() => {
        const maxSent = Math.max(0, ...hist.points.map((p) => p.sent || 0));
        const showLimitLine = hist.limit && hist.limit <= maxSent * 2;
        return (
          <div className="mt-4" data-testid="email-quota-chart">
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-xs font-semibold text-slate-500">Envois d'emails · 6 derniers mois</p>
              <p className="text-[11px] text-slate-400">Limite mensuelle : <span className="font-semibold text-findo-red">{hist.limit}</span></p>
            </div>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={hist.points} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDEAF6" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false}
                  domain={[0, showLimitLine ? (dataMax) => Math.ceil(hist.limit * 1.1) : "auto"]} />
                <Tooltip formatter={(v) => [`${v} emails`, "Envoyés"]} />
                {showLimitLine && (
                  <ReferenceLine y={hist.limit} stroke="#E4232E" strokeDasharray="4 4"
                    label={{ value: `Limite ${hist.limit}`, position: "insideTopRight", fill: "#E4232E", fontSize: 10 }} />
                )}
                <Line type="monotone" dataKey="sent" name="Envoyés" stroke="#191A54" strokeWidth={2.5}
                  dot={{ r: 3, fill: "#191A54" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      })()}
      <EmailQuotaByPartner />
    </div>
  );
}

function EmailQuotaByPartner() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && rows === null) {
      try {
        const r = await api("/admin/email-quota/top-partners?limit=10");
        setRows(r.partners || []);
      } catch { setRows([]); }
    }
  }

  return (
    <div className="mt-3">
      <button onClick={toggle} className="text-xs font-semibold text-findo-indigo" data-testid="email-quota-by-partner-toggle">
        {open ? "Masquer" : "Voir"} le détail par établissement (B2B)
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-border overflow-hidden" data-testid="email-quota-by-partner">
          {rows === null ? (
            <PageLoader label="Chargement…" className="py-4" size={32} />
          ) : rows.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-400" data-testid="email-quota-by-partner-empty">Aucun email envoyé à un partenaire ce mois-ci.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr><th className="text-left px-3 py-2 font-semibold">Établissement</th><th className="text-right px-3 py-2 font-semibold">Emails</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((p) => (
                  <tr key={p.email} data-testid={`email-partner-row-${p.email}`}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-slate-900">{countryFlag(p.country)} {p.org_name}</span>
                      <span className="block text-xs text-slate-400">{p.email}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-display font-bold text-findo-navy">{p.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function EstablishmentEditDialog({ est, onClose, reload }) {
  const [form, setForm] = useState({ name: "", address: "", city: "", country: "FR" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (est) setForm({ name: est.name || "", address: est.address || "", city: est.city || "", country: est.country || "FR" });
  }, [est]);
  async function submit(ev) {
    ev.preventDefault();
    setBusy(true);
    try {
      await api(`/admin/establishments/${est.id}`, { method: "PATCH", body: { name: form.name, address: form.address, city: form.city, country: form.country } });
      toast.success("Établissement modifié");
      onClose(); reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={!!est} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="est-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Modifier l'établissement</DialogTitle>
          <DialogDescription>Mettez à jour le nom, l'adresse et la localisation de l'établissement.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="findo-numbered space-y-5">
          <div><Label>Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="est-edit-name" /></div>
          <div><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="est-edit-address" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="est-edit-city" /></div>
            <div><Label>Pays</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase().slice(0, 2) })} className="mt-1.5 h-11 rounded-xl" data-testid="est-edit-country" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-full">Annuler</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-findo-indigo hover:bg-findo-indigo/90" data-testid="est-edit-save">{busy ? <FindoSpinner size={16} /> : "Enregistrer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransferItemsDialog({ est, siblings, onClose, reload }) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTarget(""); }, [est]);
  async function submit() {
    if (!target) { toast.error("Choisissez l'établissement cible"); return; }
    setBusy(true);
    try {
      const r = await api(`/admin/establishments/${est.id}/transfer-items`, { method: "POST", body: { target_id: target } });
      toast.success(`${r.moved} objet(s) transféré(s) vers « ${r.target_name} »`);
      onClose(); reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={!!est} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="est-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Transférer les objets</DialogTitle>
          <DialogDescription>
            Déplacez tous les objets de « {est?.name} » ({est?.items_total ?? 0}) vers un autre établissement du même client avant suppression.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {siblings.length === 0 ? (
            <p className="text-sm text-slate-500">Ce client n'a aucun autre établissement pouvant recevoir les objets. Créez-en un d'abord (via l'impersonation du propriétaire).</p>
          ) : (
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-11 w-full rounded-full border border-border px-3 text-sm" data-testid="est-transfer-target">
              <option value="">— Établissement cible —</option>
              {siblings.map((s) => <option key={s.id} value={s.id}>{s.name}{s.city ? ` · ${s.city}` : ""}</option>)}
            </select>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full">Annuler</Button>
          <Button onClick={submit} disabled={busy || !target || siblings.length === 0} className="rounded-full bg-findo-indigo hover:bg-findo-indigo/90" data-testid="est-transfer-confirm">
            {busy ? <FindoSpinner size={16} /> : "Transférer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EST_AUDIT_LABELS = {
  "admin.establishment.update": "Modification",
  "admin.establishment.delete": "Suppression",
  "admin.establishment.toggle": "Activation / suspension",
  "admin.establishment.transfer_items": "Transfert d'objets",
  "partner.reset_password": "Réinit. mot de passe propriétaire",
  "partner.update": "Modification propriétaire",
  "partner.toggle": "Activation / suspension propriétaire",
  "impersonation.start": "Impersonation démarrée",
  "impersonation.request": "Impersonation demandée",
};

function EstablishmentHistoryDialog({ est, onClose }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    if (!est) { setLogs(null); return; }
    (async () => {
      try { const d = await api(`/admin/establishments/${est.id}/audit`); setLogs(d.logs || []); }
      catch (e) { toast.error(e.message); setLogs([]); }
    })();
  }, [est]);
  return (
    <Dialog open={!!est} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="est-history-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Journal de modération</DialogTitle>
          <DialogDescription>Historique des actions super-admin pour « {est?.name} ».</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto py-1">
          {logs === null && <PageLoader label="Chargement…" className="p-4" size={32} />}
          {logs && logs.length === 0 && <p className="text-sm text-slate-400 p-4 text-center" data-testid="est-history-empty">Aucune action enregistrée.</p>}
          {logs && logs.length > 0 && (
            <ul className="space-y-2">
              {logs.map((l) => (
                <li key={l.id} className="rounded-xl border border-border p-3 text-sm" data-testid={`est-history-row-${l.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-800">{EST_AUDIT_LABELS[l.action] || l.action}</span>
                    <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">par {l.actor_email}</p>
                  {l.action === "admin.establishment.transfer_items" && l.items != null && (
                    <p className="text-xs text-slate-500">{l.items} objet(s) → {l.target_name}</p>
                  )}
                  {l.action === "admin.establishment.update" && l.changes && (
                    <p className="text-xs text-slate-500">{Object.keys(l.changes).join(", ")}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EstablishmentsAdminPanel() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [transferEst, setTransferEst] = useState(null);
  const [historyEst, setHistoryEst] = useState(null);

  async function load() {
    try { setRows(await api("/admin/establishments")); }
    catch (e) { toast.error(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function toggle(e) {
    try {
      await api(`/admin/establishments/${e.id}`, { method: "PATCH", body: { active: !e.active } });
      toast.success(e.active ? "Établissement suspendu" : "Établissement réactivé");
      load();
    } catch (err) { toast.error(err.message); }
  }

  async function removeEst(e) {
    if ((e.items_total ?? 0) > 0) {
      toast.error("Transférez d'abord les objets de cet établissement.");
      setTransferEst(e);
      return;
    }
    if (!window.confirm(`Supprimer définitivement « ${e.name} » ?`)) return;
    await del(`/admin/establishments/${e.id}`, e.name, load);
  }

  async function resetOwnerPw(e) {
    if (!e.partner_id) { toast.error("Propriétaire introuvable"); return; }
    if (!window.confirm(`Réinitialiser le mot de passe du propriétaire (${e.partner_email}) ?`)) return;
    await resetPw(`/admin/partners/${e.partner_id}/reset-password`, load);
  }

  if (!rows) return <PageLoader label="Chargement…" className="p-8" size={32} />;

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.name || "").toLowerCase().includes(s) || (r.city || "").toLowerCase().includes(s) ||
      (r.partner_org_name || "").toLowerCase().includes(s) || (r.partner_email || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-establishments-panel">
      <div className="flex items-center gap-3 mb-4">
        <Input data-testid="admin-est-search" placeholder="Rechercher par nom, ville, propriétaire…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <span className="text-sm text-slate-400">{filtered.length} établissement(s)</span>
      </div>
      <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Établissement</th>
              <th className="text-left px-4 py-3">Propriétaire</th>
              <th className="text-left px-4 py-3">Ville</th>
              <th className="text-center px-4 py-3">Objets</th>
              <th className="text-center px-4 py-3">Type</th>
              <th className="text-center px-4 py-3">Statut</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((e) => (
              <tr key={e.id} data-testid={`admin-est-row-${e.id}`}>
                <td className="px-4 py-3">
                  <span className="font-semibold text-slate-900">{e.name}</span>
                  <span className="block text-xs text-slate-400">{e.country}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-slate-700">{e.partner_org_name || "—"}</span>
                  <span className="block text-xs text-slate-400">{e.partner_email || ""}</span>
                </td>
                <td className="px-4 py-3 text-slate-600">{e.city || "—"}</td>
                <td className="px-4 py-3 text-center font-semibold">{e.items_total ?? 0}</td>
                <td className="px-4 py-3 text-center">
                  {e.is_primary
                    ? <Badge className="bg-findo-indigo/10 text-findo-indigo">Principal</Badge>
                    : <Badge className="bg-slate-100 text-slate-600">Secondaire</Badge>}
                </td>
                <td className="px-4 py-3 text-center">
                  {e.active
                    ? <Badge className="bg-teal-100 text-teal-700">Actif</Badge>
                    : <Badge className="bg-red-100 text-red-700">Suspendu</Badge>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" title="Modifier" data-testid={`admin-est-edit-${e.id}`} onClick={() => setEditing(e)} className="h-8 w-8 rounded-full">
                      <Pencil size={15} />
                    </Button>
                    <Button size="icon" variant="ghost" title={e.active ? "Suspendre" : "Réactiver"} data-testid={`admin-est-toggle-${e.id}`} onClick={() => toggle(e)} className="h-8 w-8 rounded-full">
                      <Power size={15} className={e.active ? "text-amber-600" : "text-teal-600"} />
                    </Button>
                    <Button size="icon" variant="ghost" title="Réinitialiser le mot de passe du propriétaire" data-testid={`admin-est-resetpw-${e.id}`} onClick={() => resetOwnerPw(e)} disabled={!e.partner_id} className="h-8 w-8 rounded-full">
                      <KeyRound size={15} className="text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Impersonation du propriétaire" data-testid={`admin-est-impersonate-${e.id}`} onClick={() => e.partner_id && setImpersonateTarget({ type: "partner", id: e.partner_id, name: e.partner_org_name || e.partner_email })} disabled={!e.partner_id} className="h-8 w-8 rounded-full">
                      <UserCog size={15} className="text-findo-indigo" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Transférer les objets vers un autre établissement" data-testid={`admin-est-transfer-${e.id}`} onClick={() => setTransferEst(e)} className="h-8 w-8 rounded-full">
                      <RefreshCw size={15} className="text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Journal de modération" data-testid={`admin-est-history-${e.id}`} onClick={() => setHistoryEst(e)} className="h-8 w-8 rounded-full">
                      <History size={15} className="text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Supprimer" data-testid={`admin-est-delete-${e.id}`} onClick={() => removeEst(e)} className="h-8 w-8 rounded-full">
                      <Trash2 size={15} className="text-findo-danger" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Aucun établissement.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <EstablishmentEditDialog est={editing} onClose={() => setEditing(null)} reload={load} />
      <ImpersonateDialog target={impersonateTarget} onClose={() => setImpersonateTarget(null)} />
      <TransferItemsDialog est={transferEst} siblings={transferEst ? (rows || []).filter((r) => r.partner_id === transferEst.partner_id && r.id !== transferEst.id) : []} onClose={() => setTransferEst(null)} reload={load} />
      <EstablishmentHistoryDialog est={historyEst} onClose={() => setHistoryEst(null)} />
    </div>
  );
}

const RET_GROUPS = [
  { key: "valuables", label: "Objets de valeur" },
  { key: "clothing", label: "Vêtements & accessoires" },
  { key: "id_documents", label: "Documents d'identité" },
  { key: "other", label: "Autres objets" },
];
const RET_COUNTRY_LABELS = { FR: "France", default: "Afrique / par défaut" };

function RetentionDemoCard() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  async function run() {
    setRunning(true); setReport(null);
    try { setReport(await api("/admin/retention/demo-alert", { method: "POST" })); toast.success("Alerte de rétention simulée"); }
    catch (e) { toast.error(e.message); } finally { setRunning(false); }
  }
  async function cleanup() {
    try { await api("/admin/retention/demo-alert", { method: "DELETE" }); setReport(null); toast.success("Objet de démo supprimé"); }
    catch (e) { toast.error(e.message); }
  }

  return (
    <div className="bg-white rounded-2xl border border-border p-5 card-shadow mb-4" data-testid="retention-demo-card">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo"><CalendarClock size={18} /></span>
        <div>
          <h3 className="font-display font-bold text-lg text-slate-900">Tester une alerte de rétention</h3>
          <p className="text-xs text-slate-500">Crée un objet de démo et simule les alertes légales J-15, J-7 et l'échéance J0 (isolé, sans e-mail réel).</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <Button onClick={run} disabled={running} className="rounded-full font-semibold" data-testid="retention-demo-run">
          <RefreshCw size={16} className={`mr-1.5 ${running ? "animate-spin" : ""}`} />
          {running ? "En cours…" : "Tester une alerte de rétention"}
        </Button>
        {report && (
          <Button onClick={cleanup} variant="outline" className="rounded-full" data-testid="retention-demo-cleanup">
            <Trash2 size={15} className="mr-1.5" /> Nettoyer
          </Button>
        )}
      </div>

      {report && (
        <div className="mt-4" data-testid="retention-demo-report">
          <p className="text-xs text-slate-500 mb-2">Objet de démo : <b>{report.item_id}</b> · Préalertes envoyées : <b>{(report.prealerts_sent || []).map((d) => `J-${d}`).join(", ") || "—"}</b></p>
          <ol className="space-y-2">
            {report.steps.map((s, i) => {
              const bad = s.legal_reached || s.reminder_fired?.startsWith("J-") || s.alert_fired;
              return (
                <li key={i} className="flex items-start gap-3 rounded-xl border border-border bg-slate-50 p-3" data-testid={`retention-demo-step-${i}`}>
                  <span className={`mt-0.5 h-6 min-w-6 px-1.5 grid place-items-center rounded-full text-white text-[11px] font-bold ${s.step === "J0" ? "bg-findo-red" : s.step.startsWith("J-") ? "bg-amber-500" : "bg-emerald-500"}`}>{s.step}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm">{s.label}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                      {s.legal_months != null && <span>Durée légale : {s.legal_months} mois</span>}
                      {s.legal_days_remaining != null && <span>Jours restants : {s.legal_days_remaining}</span>}
                      {s.prealert_active != null && <span>Préalerte active : <b>{s.prealert_active ? "oui" : "non"}</b></span>}
                      {s.reminder_fired && <span>Rappel : <b className="text-amber-600">{s.reminder_fired}</b></span>}
                      {s.legal_reached != null && <span>Échéance atteinte : <b className={s.legal_reached ? "text-findo-red" : "text-emerald-600"}>{s.legal_reached ? "oui" : "non"}</b></span>}
                      {s.alert_fired != null && <span>Alerte émise : <b className="text-findo-red">{s.alert_fired ? "oui" : "non"}</b></span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          {report.note && <p className="text-xs text-slate-400 mt-3 italic">{report.note}</p>}
        </div>
      )}
    </div>
  );
}

function LegalRetentionPanel() {
  const [countries, setCountries] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [newCountry, setNewCountry] = useState("");

  async function load() {
    try { const d = await api("/admin/legal-retention"); setCountries(d.countries || {}); }
    catch (e) { toast.error(e.message); }
  }
  useEffect(() => { load(); }, []);

  function setVal(ck, g, v) {
    setCountries((c) => ({ ...c, [ck]: { ...c[ck], [g]: v === "" ? "" : Math.max(0, Number(v)) } }));
  }
  async function save(ck) {
    setSavingKey(ck);
    try {
      const groups = Object.fromEntries(RET_GROUPS.map((g) => [g.key, Number(countries[ck]?.[g.key] || 0)]));
      const d = await api("/admin/legal-retention", { method: "PUT", body: { country: ck, groups } });
      setCountries(d.countries);
      toast.success(`Durées enregistrées pour ${RET_COUNTRY_LABELS[ck] || ck}`);
    } catch (e) { toast.error(e.message); } finally { setSavingKey(null); }
  }
  function addCountry() {
    const ck = newCountry.trim().toUpperCase();
    if (!ck || countries[ck]) { setNewCountry(""); return; }
    setCountries((c) => ({ ...c, [ck]: { valuables: 2, clothing: 1, id_documents: 1, other: 1 } }));
    setNewCountry("");
  }

  if (!countries) return <PageLoader label="Chargement…" className="p-8" size={32} />;

  return (
    <div data-testid="legal-retention-panel">
      <RetentionDemoCard />
      <div className="mb-4">
        <h3 className="font-display font-bold text-slate-900">Durées légales de conservation (en mois)</h3>
        <p className="text-sm text-slate-500">Ajustez les durées par pays et par type d'objet selon la législation. Le pays « Afrique / par défaut » s'applique à tout pays non configuré.</p>
      </div>
      <div className="space-y-4">
        {Object.keys(countries).sort((a, b) => (a === "default" ? 1 : a.localeCompare(b))).map((ck) => (
          <div key={ck} className="bg-white rounded-2xl border border-border card-shadow p-5" data-testid={`retention-country-${ck}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold text-findo-navy">{RET_COUNTRY_LABELS[ck] || ck} <span className="text-xs text-slate-400 font-normal">({ck})</span></span>
              <Button size="sm" onClick={() => save(ck)} disabled={savingKey === ck} data-testid={`retention-save-${ck}`} className="rounded-full bg-findo-indigo hover:bg-findo-indigo/90">
                {savingKey === ck ? "…" : "Enregistrer"}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {RET_GROUPS.map((g) => (
                <div key={g.key}>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">{g.label}</label>
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min="0" max="120" value={countries[ck]?.[g.key] ?? ""} onChange={(e) => setVal(ck, g.key, e.target.value)}
                      data-testid={`retention-${ck}-${g.key}`} className="h-10" />
                    <span className="text-xs text-slate-400">mois</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Input placeholder="Code pays (ex. BE, SN, MA)" value={newCountry} onChange={(e) => setNewCountry(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2))} className="max-w-xs h-10" data-testid="retention-new-country" maxLength={2} />
        <Button variant="outline" onClick={addCountry} className="rounded-full" data-testid="retention-add-country">Ajouter un pays</Button>
      </div>
      <p className="mt-1.5 text-xs text-slate-400">Code pays ISO-2 en majuscules (ex : BE, CH, SN). Deux lettres maximum.</p>
    </div>
  );
}


const DISPO_LABELS_FR = {
  auctioned: "Vendu aux enchères",
  donated: "Donné à une association",
  handed_to_authorities: "Remis aux autorités",
  destroyed: "Détruit",
};

function DispositionJournalPanel() {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  useEffect(() => {
    api("/admin/dispositions")
      .then((d) => setData(d))
      .catch(() => setData({ count: 0, items: [] }))
      .finally(() => setLoading(false));
  }, []);

  async function downloadExport(fmt) {
    setExporting(fmt);
    try {
      const res = await fetch(`${API}/admin/dispositions/export.${fmt}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Export impossible");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `findloop-dispositions.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Export impossible");
    } finally {
      setExporting("");
    }
  }

  return (
    <div className="mt-8" data-testid="disposition-journal-panel">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-slate-900">Journal des dispositions ({data.count})</h3>
          <p className="text-sm text-slate-500">Objets expirés traités par les partenaires : vente aux enchères, don, remise aux autorités ou destruction — action horodatée.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" disabled={!!exporting || data.count === 0}
            onClick={() => downloadExport("csv")} className="rounded-full" data-testid="dispositions-export-csv">
            {exporting === "csv" ? "…" : "Export CSV"}
          </Button>
          <Button variant="outline" size="sm" disabled={!!exporting || data.count === 0}
            onClick={() => downloadExport("pdf")} className="rounded-full" data-testid="dispositions-export-pdf">
            {exporting === "pdf" ? "…" : "Export PDF"}
          </Button>
        </div>
      </div>
      {loading ? (
        <PageLoader label="Chargement…" className="py-8" size={32} />
      ) : data.items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="disposition-journal-empty">Aucune disposition enregistrée pour le moment.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-border text-[11px] font-bold uppercase tracking-wide text-slate-500">
            <div className="col-span-4">Objet</div>
            <div className="col-span-3">Partenaire / Établissement</div>
            <div className="col-span-3">Action</div>
            <div className="col-span-2">Date</div>
          </div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-border">
            {data.items.map((it) => {
              const log = (it.disposition_log && it.disposition_log.length > 0)
                ? it.disposition_log
                : [{ type: it.disposition, at: it.disposed_at }];
              return (
                <div key={it.id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm" data-testid={`admin-disposition-${it.id}`}>
                  <div className="col-span-4 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{it.description || it.category}</p>
                    <p className="text-[11px] text-slate-400">{it.category}</p>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <p className="text-slate-700 truncate">{it.partner_name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{it.establishment_name}</p>
                  </div>
                  <div className="col-span-3">
                    {log.map((e, i) => (
                      <div key={i} className="mb-1">
                        <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          {DISPO_LABELS_FR[e.type] || e.type}
                        </span>
                        {e.note ? <p className="text-[11px] text-slate-500 italic mt-0.5" data-testid={`admin-disposition-note-${it.id}-${i}`}>📝 {e.note}</p> : null}
                      </div>
                    ))}
                  </div>
                  <div className="col-span-2 text-[11px] text-slate-500">
                    {log.map((e, i) => (
                      <p key={i}>{e.at ? new Date(e.at).toLocaleString("fr-FR") : "—"}</p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
